import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../layout/Layout';
import { userApi, systemApi, deviceApi, paymentApi, roomApi, organisationApi, bookingApi, consoleApi, shutterApi, haEntityApi, permissionApi, myceliumApi, pricingApi, getMyEffectiveOrganisationPermissions } from '../../services/api';
import { AdminNetworkSummaryWidget } from './AdminNetwork';
import { buildAuthHeaders } from '../../services/authStorage';
import AuthContext from '../../context/AuthContext';
import BotSessionKeysPanel from '../BotSessionKeysPanel';
import StepUpDialog from '../auth/StepUpDialog';
import MemberPricePlansSection from './memberPricePlans/MemberPricePlansSection';
import RoomControlsPanel from '../room-controls/RoomControlsPanel';
import RoomSensorsPanel from '../room-controls/RoomSensorsPanel';
import RoomLinkedDevicesPanel from '../room-controls/RoomLinkedDevicesPanel';
import RoomLinkedNodePanel from '../room-controls/RoomLinkedNodePanel';
import { isRoomControlsRelayEnabled } from '../../utils/txFeatureFlags';
import { isMemberPricePlansBackendAvailable } from '../../utils/priceFeatureFlags';
import wsService from '../../services/websocket';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import { useDialogDraft } from '../../hooks/useDialogDraft';
import { buildDraftScopeKey, getDraftForScope, clearDraftForScope } from '../../utils/dialogDraftStore';
import TermManagementPanel from './TermManagementPanel';
import AccountOriginAudit from './AccountOriginAudit';
import AccessControlPanel from './AccessControlPanel';
import { requesterNameOf } from '../bookings-admin/studioBookingsView';
import AccessMemberList from './AccessMemberList';
import useMySystemPermissions from '../../hooks/useMySystemPermissions';
import {
  buildTopTabs,
  resolveRoomDeepLink,
  roomsWithTabs,
  visibleSubTabs,
} from './adminRoomsTabs';
import { CAPABILITY as ROOM_DEVICES_CAPABILITY, hasCapability } from './roomCapability';
import MemberPermissionOverridePanel from './MemberPermissionOverridePanel';
import { ORGANISATION_PERMISSION_KEYS, formatPermissionLabel } from './organisationPermissionKeys';
import { buildRolePolicyRowKey } from './rolePolicyRowKey.mjs';

// Re-exported for backward compatibility — both keys previously lived
// inline in this file; other modules (and this file's own tests) import
// them from here.
export { ORGANISATION_PERMISSION_KEYS, formatPermissionLabel };
import './Admin.css';
import { appAccessSelectValue, resolveAppAccess } from './appAccessPolicy';
import AppPermissionMatrix from './AppPermissionMatrix';
import AppPermissionDeclarationDrawer from './AppPermissionDeclarationDrawer';
import {
  normaliseApplicationPermissions,
  pendingKey as appPermissionPendingKey,
  appPermissionsByRole,
} from './appPermissionCatalogue';
import { groupRecurringBookings } from './adminBookingsGrouping';
import {
  canLoadAdminRoomsScope,
  isGlobalSuperAdmin,
  resolveAdminRoomsSelection,
  selectAdminRoomsOrganisations,
} from './adminRoomsOrganisationScope';
import useDismissOnOutsideClick from '../../hooks/useDismissOnOutsideClick';
import {
  canDeleteUsers as userCanDeleteUsers,
  loadMySystemPermissions,
  canRemoveFromOrganisation,
  buildDeleteUserConfirm,
  buildBulkDeleteUserConfirm,
  buildRemoveFromOrganisationConfirm,
} from './userRemovalPolicy';
import { organisationActionsFor } from './organisationActionsPolicy';

const APP_USER_ROLES = ['App_Test', 'App_A', 'App_B', 'App_Bot_A', 'App_Bot_B'];

const isAppUserRole = (role) => APP_USER_ROLES.includes(role);

const ROLE_PERMISSION_PREVIEW = [
  'users_view_all',
  'users_create',
  'users_delete',
  'users_role_change_global',
  'permissions_system_manage',
  'permissions_organisation_manage',
  'app_users_manage',
];

const SYSTEM_PERMISSION_KEYS = [
  'organisation_create',
  'organisation_delete',
  'organisation_view_all',
  'users_view_all',
  'users_create',
  'users_delete',
  'users_role_change_global',
  'permissions_system_manage',
  'permissions_organisation_manage',
  'security_policy_manage',
  'sessions_view_all',
  'audit_view',
  'app_users_manage',
  'enforce_SAL4_Network_Access',
];

const HUMAN_PERMISSION_ROLES = ['student', 'teacher', 'admin', 'super_admin'];
const PERMISSION_PLACEHOLDER_ROLES = [...HUMAN_PERMISSION_ROLES, ...APP_USER_ROLES];

const getAdminApiErrorMessage = (err, fallback) => (
  err.response?.data?.message
  || err.response?.data?.error?.message
  || err.response?.data?.error
  || err.response?.data?.code
  || err.message
  || fallback
);

const getNetworkSal4PermissionValue = (payload) => {
  const body = payload?.data || payload || {};
  const permission = body.data?.permission || body.permission || body.data || body;
  const permissions = permission.permissions || permission;

  return Boolean(permissions.enforce_SAL4_Network_Access);
};

const normaliseArrayPayload = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const formatOptionalDateTime = (value) => {
  if (!value) return 'Not seen yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const maskServiceKeyId = (keyId) => (
  keyId ? `...${String(keyId).slice(-8)}` : 'No key reference'
);

const DEFAULT_ORG_SECURITY_POLICY = {
  require2svAllUsers: false,
  require2svAdminsOnly: true,
  allowFederatedMfa: true,
};

const parseBooleanPolicyValue = (value, fallback) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
  }
  return fallback;
};

const getPolicyValue = (policy, camelKey, snakeKey, fallback) => (
  parseBooleanPolicyValue(policy?.[camelKey] ?? policy?.[snakeKey], fallback)
);

const normaliseOrganisationSecurityPolicy = (org) => {
  const policy = org?.securityPolicy || org?.security_policy || org;
  const hasPolicyFields = Boolean(policy && (
    Object.prototype.hasOwnProperty.call(policy, 'require2svAllUsers') ||
    Object.prototype.hasOwnProperty.call(policy, 'require_2sv_all_users') ||
    Object.prototype.hasOwnProperty.call(policy, 'require2svAdminsOnly') ||
    Object.prototype.hasOwnProperty.call(policy, 'require_2sv_admins_only') ||
    Object.prototype.hasOwnProperty.call(policy, 'allowFederatedMfa') ||
    Object.prototype.hasOwnProperty.call(policy, 'allow_federated_mfa')
  ));

  if (!hasPolicyFields) return null;

  return {
    require2svAllUsers: getPolicyValue(policy, 'require2svAllUsers', 'require_2sv_all_users', DEFAULT_ORG_SECURITY_POLICY.require2svAllUsers),
    require2svAdminsOnly: getPolicyValue(policy, 'require2svAdminsOnly', 'require_2sv_admins_only', DEFAULT_ORG_SECURITY_POLICY.require2svAdminsOnly),
    allowFederatedMfa: getPolicyValue(policy, 'allowFederatedMfa', 'allow_federated_mfa', DEFAULT_ORG_SECURITY_POLICY.allowFederatedMfa),
  };
};

const isTwoSvEnforced = (user = {}) => Boolean(user.twoSvEnforced ?? user.two_sv_enforced);

// M004-F0615 / T-M004-0022(c): a missing rate must never render as £0.00 — a
// plausible wrong price is worse than a visible gap. Only a real minor-unit
// number formats to money; every other value (null/undefined/non-numeric)
// returns null so the caller renders an honest "no plan"/"rate not set" state.
const formatPricingRate = (minorUnits, currency = 'GBP') => {
  if (minorUnits === null || minorUnits === undefined || minorUnits === '') return null;
  const amount = Number(minorUnits) / 100;
  if (!Number.isFinite(amount)) return null;
  const symbol = currency === 'GBP' || !currency ? '£' : `${String(currency).toUpperCase()} `;
  return `${symbol}${amount.toFixed(2)}`;
};

const isTwoSvEnrolled = (user = {}) => Boolean(
  user.twoSvEnrolled
  ?? user.two_sv_enrolled
  ?? user.twoSvEnrolledAt
  ?? user.two_sv_enrolled_at
);

const parseVerificationBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'verified'].includes(normalized)) return true;
    if (['false', '0', 'no', 'unverified', 'null', 'none', ''].includes(normalized)) return false;
  }
  return undefined;
};

const hasVerifiedEmail = (user = {}) => {
  const explicit = parseVerificationBoolean(user.emailVerified ?? user.email_verified ?? user.isEmailVerified ?? user.is_email_verified);
  if (explicit !== undefined) {
    return explicit;
  }

  return Boolean(user.emailVerifiedAt ?? user.email_verified_at);
};

const hasUnverifiedEmailLoginMethod = (user = {}) => {
  const loginMethods = Array.isArray(user.loginMethods ?? user.login_methods)
    ? (user.loginMethods ?? user.login_methods)
    : [];

  return loginMethods.some((method) => {
    const provider = String(method.provider ?? method.type ?? method.authMethod ?? method.loginType ?? '').toLowerCase();
    const methodEmail = method.email ?? method.loginEmail ?? method.emailAddress ?? method.email_address;
    const verified = parseVerificationBoolean(method.isVerified ?? method.is_verified ?? method.emailVerified ?? method.email_verified);
    const sameEmail = !methodEmail || !user.email || String(methodEmail).toLowerCase() === String(user.email).toLowerCase();

    return ['email', 'password'].includes(provider) && sameEmail && verified === false;
  });
};

const needsEmailVerificationAction = (user = {}) => (
  !hasVerifiedEmail(user) || hasUnverifiedEmailLoginMethod(user)
);

// ==================== Admin Dashboard ====================
const AdminDashboard = () => {
  const [systemHealth, setSystemHealth] = useState({ status: 'loading' });
  const [occupancy, setOccupancy] = useState(0);
  const [climate, setClimate] = useState({ temperature: '--', humidity: '--' });
  const [revenue, setRevenue] = useState({ today: 0, month: 0 });
  const [alerts, setAlerts] = useState([]);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    // Fetch initial data
    const fetchData = async () => {
      try {
        // Mock data for demonstration
        setSystemHealth({
          status: 'healthy',
          servers: [
            { name: 'Enigma', status: 'online', uptime: '99.9%' },
            { name: 'NVR', status: 'online', uptime: '99.5%' },
            { name: 'Database', status: 'online', uptime: '100%' },
          ],
        });
        setOccupancy(12);
        setClimate({ temperature: 21, humidity: 45 });
        setRevenue({ today: 245.00, month: 3420.00 });
        setAlerts([
          { id: 1, type: 'warning', message: 'Camera 3 offline', time: '2 min ago' },
          { id: 2, type: 'info', message: 'High occupancy in Studio A', time: '15 min ago' },
          { id: 3, type: 'error', message: 'Payment overdue: User #1234', time: '1 hour ago' },
        ]);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      }
    };

    fetchData();

    // Set up WebSocket connection
    wsService.connect();

    const unsubConnection = wsService.subscribe('connectionChange', ({ state }) => {
      setConnectionState(state);
    });

    const unsubOccupancy = wsService.subscribe('occupancy', (data) => {
      setOccupancy(data.count);
      setLastUpdate(new Date());
    });

    const unsubAlerts = wsService.subscribe('alert', (data) => {
      setAlerts((prev) => [data, ...prev].slice(0, 10));
      setLastUpdate(new Date());
    });

    const pushNetworkAlert = (data, fallbackType) => {
      setAlerts((prev) => [
        {
          id: `${fallbackType}-${data?.anomalyId || data?.detectedAt || Date.now()}`,
          type: String(data?.severity || '').toLowerCase() === 'critical' ? 'error' : 'warning',
          message: data?.title || fallbackType,
          time: 'Just now',
        },
        ...prev,
      ].slice(0, 10));
      setLastUpdate(new Date());
    };

    const unsubNetworkAnomaly = wsService.subscribe('network:anomaly', (data) => {
      pushNetworkAlert(data, 'Network anomaly');
    });

    const unsubNetworkHealth = wsService.subscribe('network:health_alert', (data) => {
      pushNetworkAlert(data, 'Network health alert');
    });

    // Polling for system health
    const healthInterval = setInterval(fetchData, 60000);

    return () => {
      unsubConnection();
      unsubOccupancy();
      unsubAlerts();
      unsubNetworkAnomaly();
      unsubNetworkHealth();
      clearInterval(healthInterval);
    };
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'offline':
      case 'error':
        return 'error';
      default:
        return 'info';
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Layout>
      <div className="admin-dashboard">
        <header className="page-header">
          <div>
            <h1>Dashboard</h1>
            <p className="subtitle">Operations overview</p>
          </div>
          <div className="connection-status">
            <span className={`status-dot ${connectionState}`}></span>
            <span>{connectionState === 'connected' ? 'Live' : connectionState}</span>
            <span className="last-update">Last: {formatTime(lastUpdate)}</span>
          </div>
        </header>

        <div className="dashboard-widgets">
          {/* System Health */}
          <div className="widget widget-system">
            <div className="widget-header">
              <h2>🖥️ System Health</h2>
              <span className={`status-badge ${getStatusColor(systemHealth.status)}`}>
                {systemHealth.status === 'healthy' ? '✓ All Systems OK' : 'Issues Detected'}
              </span>
            </div>
            <div className="server-list">
              {systemHealth.servers?.map((server) => (
                <div key={server.name} className="server-item">
                  <span className={`status-dot ${getStatusColor(server.status)}`}></span>
                  <span className="server-name">{server.name}</span>
                  <span className="server-uptime">{server.uptime}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Live Occupancy */}
          <div className="widget widget-occupancy">
            <div className="widget-header">
              <h2>👥 Live Occupancy</h2>
            </div>
            <div className="occupancy-display">
              <span className="occupancy-count">{occupancy}</span>
              <span className="occupancy-label">people in studio</span>
            </div>
          </div>

          {/* Climate Status */}
          <div className="widget widget-climate">
            <div className="widget-header">
              <h2>🌡️ Climate Status</h2>
            </div>
            <div className="climate-display">
              <div className="climate-item">
                <span className="climate-value">{climate.temperature}°C</span>
                <span className="climate-label">Temperature</span>
              </div>
              <div className="climate-item">
                <span className="climate-value">{climate.humidity}%</span>
                <span className="climate-label">Humidity</span>
              </div>
            </div>
          </div>

          {/* Revenue */}
          <div className="widget widget-revenue">
            <div className="widget-header">
              <h2>💰 Revenue</h2>
              <Link to="/app/admin/payments" className="link-view">View all</Link>
            </div>
            <div className="revenue-display">
              <div className="revenue-item">
                <span className="revenue-value">£{revenue.today.toFixed(2)}</span>
                <span className="revenue-label">Today</span>
              </div>
              <div className="revenue-item">
                <span className="revenue-value">£{revenue.month.toFixed(2)}</span>
                <span className="revenue-label">This Month</span>
              </div>
            </div>
          </div>

          <AdminNetworkSummaryWidget />

          {/* Recent Alerts */}
          <div className="widget widget-alerts widget-wide">
            <div className="widget-header">
              <h2>🔔 Recent Alerts</h2>
              <button className="btn-text">Mark all read</button>
            </div>
            <div className="alerts-list">
              {alerts.length > 0 ? (
                alerts.map((alert) => (
                  <div key={alert.id} className={`alert-item ${alert.type}`}>
                    <span className="alert-icon">
                      {alert.type === 'error' ? '❌' : alert.type === 'warning' ? '⚠️' : 'ℹ️'}
                    </span>
                    <span className="alert-message">{alert.message}</span>
                    <span className="alert-time">{alert.time}</span>
                  </div>
                ))
              ) : (
                <p className="no-alerts">No recent alerts</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

// ==================== User Management Page ====================
// M004-F0261: Dedicated admin page for user and role management.
const UserManagementPage = () => {
  const { user: currentUser } = useContext(AuthContext);
  // Owner ruling 2026-10-04: Delete is a `users_delete` capability, not a role check.
  // Read from the caller's own system-permission map (Core GET /users/me/system-permissions);
  // `{}` until it answers and on any failure, so Delete stays hidden (fail closed).
  const [mySystemPermissions, setMySystemPermissions] = useState({});
  const canDeleteUsersNow = userCanDeleteUsers(mySystemPermissions);
  useEffect(() => {
    let cancelled = false;
    loadMySystemPermissions(userApi).then((map) => {
      if (!cancelled) setMySystemPermissions(map);
    });
    return () => { cancelled = true; };
  }, [currentUser?.id, currentUser?.uuid]);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTopTab = requestedTab === 'roles' ? 'roles' : 'users';
  const [roleManagementMode, setRoleManagementMode] = useState('system');
  const [users, setUsers] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [organisationFilter, setOrganisationFilter] = useState('all');
  const [rolePolicyRows, setRolePolicyRows] = useState([]);
  // M004-F0696 (M001-D033 §8): app-declared keys come from the server's
  // `applicationPermissions` block; pending edits are keyed per (key, role).
  const [applicationPermissions, setApplicationPermissions] = useState([]);
  const [appPermissionPending, setAppPermissionPending] = useState({});
  const [myOrganisationPermissions, setMyOrganisationPermissions] = useState({});
  const [matrixStepUp, setMatrixStepUp] = useState(null);
  const [declarationAppCode, setDeclarationAppCode] = useState('');
  const [rolePolicyLoading, setRolePolicyLoading] = useState(false);
  const [rolePolicyError, setRolePolicyError] = useState('');
  const [rolePolicyRequiresSal, setRolePolicyRequiresSal] = useState(false);
  const [permissionChangeReason, setPermissionChangeReason] = useState('');
  const [permissionPendingChanges, setPermissionPendingChanges] = useState({});
  const [permissionSaveLoading, setPermissionSaveLoading] = useState(false);
  const [permissionSaveStatus, setPermissionSaveStatus] = useState('');
  const [selectedPermissionOrganisationId, setSelectedPermissionOrganisationId] = useState('');
  const [appAccessRows, setAppAccessRows] = useState([]);
  const [appAccessLoading, setAppAccessLoading] = useState(false);
  const [appAccessError, setAppAccessError] = useState('');
  const [appAccessPendingChanges, setAppAccessPendingChanges] = useState({});
  const [appAccessChangeReason, setAppAccessChangeReason] = useState('');
  const [appAccessSaveLoading, setAppAccessSaveLoading] = useState(false);
  const [appAccessSaveStatus, setAppAccessSaveStatus] = useState('');
  const [selectedAppAccessUserId, setSelectedAppAccessUserId] = useState('');
  const [userAppAccessRows, setUserAppAccessRows] = useState([]);
  const [userAppAccessPendingChanges, setUserAppAccessPendingChanges] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 1,
  });
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailDrawerUser, setDetailDrawerUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    displayName: '',
    phone: '',
    role: 'student',
    organisation_id: '',
  });
  const [formError, setFormError] = useState('');
  const [formErrorCode, setFormErrorCode] = useState('');
  // M001-F0372: outcome of an Add User; persists after the dialog closes (the
  // 'queued' warning must not vanish with the modal).
  const [createNotice, setCreateNotice] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  // M004-F0620 / T-M004-0028: which write the Edit User "Role *" field is
  // allowed to perform for the user currently open in the dialog.
  //   mode 'global'        — actor holds `users_role_change_global`; unchanged
  //                          behaviour (PUT /users/:uuid).
  //   mode 'organisation'  — actor lacks that, but manages an organisation the
  //                          target belongs to and holds
  //                          `organisation_users_role_change` there; role
  //                          changes route to PUT /organisations/:id/users/:userId.
  //   mode 'none'          — neither; field is read-only, `reason` explains why.
  //   mode 'loading'       — resolution in flight; field is disabled.
  //   mode 'add'           — Add User dialog; not applicable (global create route).
  const [editRoleAuthority, setEditRoleAuthority] = useState({ mode: 'add', organisationId: null, reason: '' });
  // M004-F0613 / T-M004-0020: Add/Edit User is the reported dialog. "new" and "edit"
  // are separate scope keys (per M004-D016 §5.2) so a draft for one user's edit can
  // never leak into the add form or into a different user's edit.
  const newUserDraft = useDialogDraft('admin-user-new', 'new', { active: showModal && modalMode === 'add' });
  const editUserDraft = useDialogDraft('admin-user-edit', selectedUser?.uuid, { active: showModal && modalMode === 'edit' });
  const userDialogDraftRef = modalMode === 'add' ? newUserDraft.containerRef : editUserDraft.containerRef;
  const [userActionLoading, setUserActionLoading] = useState(null);
  const [adminSecurityLoading, setAdminSecurityLoading] = useState('');
  const [adminSecurityMessage, setAdminSecurityMessage] = useState('');
  const [adminSecurityError, setAdminSecurityError] = useState('');
  const [recoveryEvidenceRef, setRecoveryEvidenceRef] = useState('');
  const [showPasswordResetConfirm, setShowPasswordResetConfirm] = useState(false);
  const [adminSecurityStepUpChallenge, setAdminSecurityStepUpChallenge] = useState(null);
  const [adminSecurityStepUpRetry, setAdminSecurityStepUpRetry] = useState(null);
  const [drawerMemberships, setDrawerMemberships] = useState([]);
  // organisationId -> caller's `organisation_users_remove` (true only when
  // my-permissions said so; absent/false hides "Remove from organisation").
  const [drawerRemovePermitted, setDrawerRemovePermitted] = useState({});
  const removePermissionRequestedRef = useRef(new Set());
  const removePermissionGenerationRef = useRef(0);
  const [drawerMembershipsLoading, setDrawerMembershipsLoading] = useState(false);
  const [drawerMembershipError, setDrawerMembershipError] = useState('');
  const [membershipActionLoading, setMembershipActionLoading] = useState(false);
  const [membershipForm, setMembershipForm] = useState({
    organisationId: '',
    role: 'student',
    isPrimary: false,
  });

  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // Latest-request guard: only the most recent fetchUsers call may apply its
  // result, error or loading=false. An earlier (unfiltered) response that lands
  // after a later (filtered) one must not overwrite it.
  const usersRequestSeqRef = useRef(0);
  const fetchUsers = async () => {
    const requestSeq = ++usersRequestSeqRef.current;
    const isLatest = () => requestSeq === usersRequestSeqRef.current;
    try {
      setLoading(true);
      setError(null);
      const response = await userApi.getUsers({
        search: search || undefined,
        role: roleFilter !== 'all' ? roleFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        organisation_id: organisationFilter !== 'all' ? organisationFilter : undefined,
        page: currentPage,
        limit: pageSize,
      });
      if (!isLatest()) return;
      if (response.data.success) {
        const data = response.data.data || {};
        const fetchedUsers = data.users || [];
        const meta = data.pagination || data.meta || data;
        const total = Number(meta.total ?? fetchedUsers.length);
        const limit = Number(meta.limit ?? pageSize);
        const page = Number(meta.page ?? currentPage);
        const totalPages = Number(meta.totalPages ?? meta.total_pages ?? Math.max(1, Math.ceil(total / Math.max(limit, 1))));

        setUsers(fetchedUsers);
        setPagination({
          page,
          limit,
          total,
          totalPages: Math.max(1, totalPages),
        });
      }
    } catch (err) {
      if (!isLatest()) return;
      console.error('Failed to fetch users:', err);
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      if (isLatest()) setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch organisations for the dropdown
    const fetchOrgs = async () => {
      try {
        const response = await organisationApi.getMine();
        const fetchedOrganisations = response.data?.data || [];
        setOrganisations(fetchedOrganisations);
        if (!selectedPermissionOrganisationId && fetchedOrganisations.length > 0) {
          setSelectedPermissionOrganisationId(String(fetchedOrganisations[0].id));
        }
      } catch (err) {
        console.error('Failed to fetch organisations:', err);
      }
    };
    fetchOrgs();
  }, [selectedPermissionOrganisationId]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedUsers(new Set());
  }, [search, roleFilter, statusFilter, organisationFilter, pageSize]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, roleFilter, statusFilter, organisationFilter, currentPage, pageSize]);

  // Auto-set organisation_id when orgs load and the add modal is open with no org selected
  useEffect(() => {
    if (showModal && modalMode === 'add' && !formData.organisation_id && organisations.length > 0) {
      setFormData(prev => ({ ...prev, organisation_id: organisations[0].id }));
    }
  }, [organisations, showModal, modalMode]);

  const filteredUsers = users.filter((user) => {
    const userDebugId = String(user.id || user.uuid || '');
    const matchesSearch = !search ||
                         (user.displayName?.toLowerCase().includes(search.toLowerCase()) ||
                         user.email?.toLowerCase().includes(search.toLowerCase()) ||
                         userDebugId.toLowerCase().includes(search.toLowerCase()));
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    const userOrganisationIds = [
      user.organisation_id,
      user.organisationId,
      user.primaryOrganisationId,
      ...(Array.isArray(user.organisations) ? user.organisations.map((org) => org.id || org.organisation_id) : []),
    ].filter(Boolean).map(String);
    const matchesOrganisation = organisationFilter === 'all' || userOrganisationIds.includes(String(organisationFilter));
    return matchesSearch && matchesRole && matchesStatus && matchesOrganisation;
  });

  const loadRolePolicy = async () => {
    if (activeTopTab !== 'roles') return;

    // M004-F0696: system permissions stay super-admin only. The organisation
    // matrix is read for any account that reaches this page; Core decides what
    // it returns (an org admin gets the app-key section, D033 D6) and what the
    // account may edit, so a refusal below degrades to the read-only notice.
    if (currentUser?.role !== 'super_admin' && roleManagementMode !== 'organisation') {
      setRolePolicyRows([]);
      setApplicationPermissions([]);
      setRolePolicyRequiresSal(false);
      setRolePolicyError('Role permissions are read-only for this account.');
      return;
    }

    if (roleManagementMode === 'organisation' && !selectedPermissionOrganisationId) {
      setRolePolicyRows([]);
      setRolePolicyRequiresSal(false);
      setRolePolicyError('Select an organisation before loading organisation permissions.');
      return;
    }

    setRolePolicyLoading(true);
    setRolePolicyError('');
    setRolePolicyRequiresSal(false);
    setPermissionSaveStatus('');

    try {
      const response = roleManagementMode === 'system'
        ? await permissionApi.getSystemPermissions()
        : await permissionApi.getOrganisationPermissions(selectedPermissionOrganisationId);
      setRolePolicyRows(response.data?.data?.permissions || []);
      setApplicationPermissions(roleManagementMode === 'organisation'
        ? normaliseApplicationPermissions(response.data?.data?.applicationPermissions)
        : []);
      setPermissionPendingChanges({});
      setAppPermissionPending({});
    } catch (err) {
      const status = err.response?.status;
      const errorCode = err.response?.data?.error || err.response?.data?.code || '';
      const message = err.response?.data?.message || err.response?.data?.details?.message || '';
      const salRequired = [401, 403].includes(status) && /SAL|STEP_UP|ELEVATED|2SV|TWO_SV|2-step|ASSURANCE/i.test(`${errorCode} ${message}`);
      const genericInternalError = status >= 500 && /INTERNAL_ERROR|unexpected error/i.test(`${errorCode} ${message}`);

      // M004-F0696 / D033 §8.3: Core reads the matrix at SAL-4 for a super admin. When
      // the refusal carries a real step-up challenge, offer it inline and re-read.
      const readChallenge = getStepUpChallengeFromError(err);
      if (readChallenge?.id) {
        setMatrixStepUp({ challenge: readChallenge, retry: loadRolePolicy });
      }

      setRolePolicyRows([]);
      setApplicationPermissions([]);
      setRolePolicyRequiresSal(salRequired);
      setRolePolicyError(
        salRequired
          ? 'SAL 4 is required before permission policies can be edited.'
          : (genericInternalError
            ? 'Permission policies are temporarily unavailable. Editing controls are disabled until the policy service returns a valid response.'
            : (message || 'Failed to load role permission policies'))
      );
    } finally {
      setRolePolicyLoading(false);
    }
  };

  const rolePolicyOrganisationDependency = roleManagementMode === 'organisation'
    ? selectedPermissionOrganisationId
    : '';

  useEffect(() => {
    if (activeTopTab === 'roles') {
      loadRolePolicy();
    }
  }, [activeTopTab, currentUser?.role, roleManagementMode, rolePolicyOrganisationDependency]);

  const loadAppAccess = async () => {
    if (activeTopTab !== 'roles' || currentUser?.role !== 'super_admin') return;
    if (roleManagementMode === 'organisation' && !selectedPermissionOrganisationId) return;

    setAppAccessLoading(true);
    setAppAccessError('');
    setAppAccessSaveStatus('');
    try {
      const response = roleManagementMode === 'system'
        ? await permissionApi.getGlobalAppAccess()
        : await permissionApi.getOrganisationAppAccess(selectedPermissionOrganisationId);
      setAppAccessRows(response.data?.data?.access || []);
      setAppAccessPendingChanges({});
    } catch (err) {
      setAppAccessRows([]);
      setAppAccessError(err.response?.data?.message || 'Failed to load application access policy');
    } finally {
      setAppAccessLoading(false);
    }
  };

  useEffect(() => {
    loadAppAccess();
  }, [activeTopTab, currentUser?.role, roleManagementMode, rolePolicyOrganisationDependency]);

  useEffect(() => {
    setSelectedAppAccessUserId('');
    setUserAppAccessRows([]);
    setUserAppAccessPendingChanges({});
  }, [selectedPermissionOrganisationId]);

  useEffect(() => {
    const loadUserAppAccess = async () => {
      if (roleManagementMode !== 'organisation' || !selectedPermissionOrganisationId || !selectedAppAccessUserId) {
        setUserAppAccessRows([]);
        return;
      }
      try {
        const response = await permissionApi.getUserAppAccess(selectedPermissionOrganisationId, selectedAppAccessUserId);
        setUserAppAccessRows(response.data?.data?.access || []);
        setUserAppAccessPendingChanges({});
      } catch (err) {
        setUserAppAccessRows([]);
        setAppAccessError(err.response?.data?.message || 'Failed to load organisation user application access');
      }
    };
    loadUserAppAccess();
  }, [roleManagementMode, selectedPermissionOrganisationId, selectedAppAccessUserId]);

  // M004-F0696 (D033 D6): an org admin edits their own org's app-key cells when
  // THE SERVER says they hold `access_control_manage` there (my-permissions) --
  // never from the role name.
  useEffect(() => {
    let cancelled = false;
    setMyOrganisationPermissions({});
    if (activeTopTab !== 'roles' || roleManagementMode !== 'organisation' || !selectedPermissionOrganisationId) return undefined;
    (async () => {
      try {
        const permissions = typeof getMyEffectiveOrganisationPermissions === 'function'
          ? await getMyEffectiveOrganisationPermissions(selectedPermissionOrganisationId)
          : {};
        if (!cancelled) setMyOrganisationPermissions(permissions || {});
      } catch {
        if (!cancelled) setMyOrganisationPermissions({});
      }
    })();
    return () => { cancelled = true; };
  }, [activeTopTab, roleManagementMode, selectedPermissionOrganisationId]);

  const pageStart = pagination.total === 0 ? 0 : ((pagination.page - 1) * pagination.limit) + 1;
  const pageEnd = Math.min(pagination.page * pagination.limit, pagination.total);
  const canGoPrevious = pagination.page > 1;
  const canGoNext = pagination.page < pagination.totalPages;
  const rolePolicyEditMode = currentUser?.role === 'super_admin' && !rolePolicyRequiresSal && !rolePolicyError && rolePolicyRows.length > 0;
  const permissionKeys = roleManagementMode === 'system' ? SYSTEM_PERMISSION_KEYS : ORGANISATION_PERMISSION_KEYS;
  const pendingAuditRows = Object.values(permissionPendingChanges).flatMap((change) => (
    Object.entries(change.permissions).map(([permission, nextValue]) => ({
      role: change.role,
      permission,
      before: change.before?.[permission] === true,
      after: nextValue === true,
    }))
  ));
  const appPermissionEditable = roleManagementMode === 'organisation'
    && !rolePolicyLoading
    && (rolePolicyEditMode || myOrganisationPermissions.access_control_manage === true);
  const appPermissionDeclarable = roleManagementMode === 'organisation' && rolePolicyEditMode;
  const appPermissionPendingList = Object.values(appPermissionPending);
  const appPermissionAuditRows = appPermissionPendingList.map((change) => ({
    role: change.role,
    permission: change.name,
    before: change.beforeLabel,
    after: change.value === null ? 'Inherit' : (change.value ? 'Allowed' : 'Denied'),
  }));
  const canSaveRolePolicy = (rolePolicyEditMode || appPermissionEditable)
    && (pendingAuditRows.length > 0 || appPermissionPendingList.length > 0)
    && permissionChangeReason.trim().length > 0
    && !permissionSaveLoading;

  const appAccessEditMode = currentUser?.role === 'super_admin' && !rolePolicyRequiresSal && !appAccessError;
  const formatAccessValue = (value) => (value === null || value === undefined ? 'Inherit' : (value ? 'Allowed' : 'Denied'));
  const appAccessUsers = users.filter((candidate) => {
    const organisationIds = [
      candidate.organisation_id,
      candidate.organisationId,
      candidate.primaryOrganisationId,
      ...(Array.isArray(candidate.organisations)
        ? candidate.organisations.map((organisation) => organisation.id || organisation.organisation_id)
        : []),
    ].filter(Boolean).map(String);
    return organisationIds.includes(String(selectedPermissionOrganisationId));
  });
  const appAccessPendingCount = Object.keys(appAccessPendingChanges).length
    + Object.keys(userAppAccessPendingChanges).length;
  const canSaveAppAccess = appAccessEditMode
    && appAccessPendingCount > 0
    && appAccessChangeReason.trim().length > 0
    && !appAccessSaveLoading;

  const appAccessKey = (row) => `${row.appCode}:${row.role}`;
  const displayedAppAccessValue = (row) => {
    const key = appAccessKey(row);
    if (Object.prototype.hasOwnProperty.call(appAccessPendingChanges, key)) return appAccessPendingChanges[key].allowed;
    return roleManagementMode === 'system' ? row.globalDefault : (row.organisationValue ?? null);
  };

  const updateAppAccessPending = (row, rawValue) => {
    const allowed = rawValue === 'inherit' ? null : rawValue === 'allow';
    const original = roleManagementMode === 'system' ? row.globalDefault : (row.organisationValue ?? null);
    const key = appAccessKey(row);
    setAppAccessSaveStatus('');
    setAppAccessPendingChanges((current) => {
      const next = { ...current };
      if (allowed === original) delete next[key];
      else next[key] = { appCode: row.appCode, role: row.role, allowed };
      return next;
    });
  };

  const displayedUserAppAccessValue = (row) => (
    Object.prototype.hasOwnProperty.call(userAppAccessPendingChanges, row.appCode)
      ? userAppAccessPendingChanges[row.appCode].allowed
      : (row.userValue ?? null)
  );

  const updateUserAppAccessPending = (row, rawValue) => {
    const allowed = rawValue === 'inherit' ? null : rawValue === 'allow';
    const original = row.userValue ?? null;
    setAppAccessSaveStatus('');
    setUserAppAccessPendingChanges((current) => {
      const next = { ...current };
      if (allowed === original) delete next[row.appCode];
      else next[row.appCode] = { appCode: row.appCode, allowed };
      return next;
    });
  };

  const saveAppAccessChanges = async () => {
    if (!canSaveAppAccess) return;
    setAppAccessSaveLoading(true);
    setAppAccessSaveStatus('');
    const reason = appAccessChangeReason.trim();
    try {
      for (const change of Object.values(appAccessPendingChanges)) {
        if (roleManagementMode === 'system') {
          await permissionApi.updateGlobalAppAccess(change.appCode, change.role, { allowed: change.allowed, reason });
        } else {
          await permissionApi.updateOrganisationAppAccess(
            selectedPermissionOrganisationId,
            change.appCode,
            change.role,
            { allowed: change.allowed, reason },
          );
        }
      }
      for (const change of Object.values(userAppAccessPendingChanges)) {
        await permissionApi.updateUserAppAccess(
          selectedPermissionOrganisationId,
          selectedAppAccessUserId,
          change.appCode,
          { allowed: change.allowed, reason },
        );
      }
      await loadAppAccess();
      if (selectedAppAccessUserId) {
        const response = await permissionApi.getUserAppAccess(selectedPermissionOrganisationId, selectedAppAccessUserId);
        setUserAppAccessRows(response.data?.data?.access || []);
        setUserAppAccessPendingChanges({});
      }
      setAppAccessChangeReason('');
      setAppAccessSaveStatus('Application access changes saved and reloaded from the live policy.');
    } catch (err) {
      setAppAccessSaveStatus(err.response?.data?.message || 'Application access save failed. Pending changes were kept.');
    } finally {
      setAppAccessSaveLoading(false);
    }
  };

  const getPolicyRowKey = (row) => buildRolePolicyRowKey(roleManagementMode, row, selectedPermissionOrganisationId);

  const getDisplayedPermissionValue = (row, permission) => {
    const rowKey = getPolicyRowKey(row);
    const pendingPermissions = permissionPendingChanges[rowKey]?.permissions || {};
    if (Object.prototype.hasOwnProperty.call(pendingPermissions, permission)) {
      return pendingPermissions[permission] === true;
    }
    return row.permissions?.[permission] === true;
  };

  const updatePermissionPendingChange = (row, permission, checked) => {
    const rowKey = getPolicyRowKey(row);
    const beforePermissions = row.permissions || {};

    setPermissionSaveStatus('');
    setPermissionPendingChanges((current) => {
      const next = { ...current };
      const currentChange = next[rowKey] || {
        role: row.role,
        before: beforePermissions,
        permissions: {},
      };
      const nextPermissions = { ...currentChange.permissions };

      if (checked === (beforePermissions[permission] === true)) {
        delete nextPermissions[permission];
      } else {
        nextPermissions[permission] = checked;
      }

      if (Object.keys(nextPermissions).length === 0) {
        delete next[rowKey];
      } else {
        next[rowKey] = {
          ...currentChange,
          permissions: nextPermissions,
        };
      }

      return next;
    });
  };

  const updateAppPermissionPending = (key, role, value) => {
    const original = key.cells[role]?.override ?? null;
    const id = appPermissionPendingKey(key.name, role);
    setPermissionSaveStatus('');
    setAppPermissionPending((current) => {
      const next = { ...current };
      if (value === original) {
        delete next[id];
      } else {
        const cell = key.cells[role] || {};
        next[id] = {
          name: key.name,
          role,
          value,
          beforeLabel: original === null ? 'Inherit' : (original ? 'Allowed' : 'Denied'),
          cellDefault: cell.default === true,
        };
      }
      return next;
    });
  };

  // One SAL-4 step-up path for every write on the matrix (D033 §8.3): the page
  // keeps its pending edits, asks for the second factor, then replays the write.
  const completeMatrixStepUp = async () => {
    const retry = matrixStepUp?.retry;
    setMatrixStepUp(null);
    if (retry) await retry();
  };

  const cancelMatrixStepUp = () => {
    setMatrixStepUp(null);
    setPermissionSaveStatus('Verification was cancelled. Pending changes were kept.');
  };

  const saveAppDeclaration = async (appCode, key, body) => {
    try {
      await permissionApi.updateApplicationPermissionDefinition(appCode, key, body);
    } catch (err) {
      const challenge = getStepUpChallengeFromError(err);
      if (challenge?.id) {
        setMatrixStepUp({ challenge, retry: () => saveAppDeclaration(appCode, key, body) });
        return { status: 'step-up' };
      }
      return {
        status: 'error',
        message: err.response?.data?.message || err.response?.data?.details?.message || 'Declaration save failed.',
      };
    }
    setDeclarationAppCode('');
    await loadRolePolicy();
    setPermissionSaveStatus('Application permission declaration saved and reloaded from the live policy.');
    return { status: 'saved' };
  };

  const saveRolePolicyChanges = async () => {
    if (!canSaveRolePolicy) return;

    setPermissionSaveLoading(true);
    setPermissionSaveStatus('');

    try {
      const reason = permissionChangeReason.trim();
      const changes = Object.values(permissionPendingChanges);
      const appByRole = appPermissionsByRole(appPermissionPending);

      for (const change of changes) {
        if (roleManagementMode === 'system') {
          await permissionApi.updateSystemPermission(change.role, {
            reason,
            permissions: change.permissions,
          });
        } else {
          await permissionApi.updateOrganisationPermission(selectedPermissionOrganisationId, change.role, {
            reason,
            // Core takes ONE `permissions` map: core keys and app keys together,
            // partitioned server-side (admin.routes.ts, D033 §5).
            permissions: { ...change.permissions, ...(appByRole[change.role] || {}) },
          });
          delete appByRole[change.role];
        }
      }
      for (const [role, appPermissions] of Object.entries(appByRole)) {
        await permissionApi.updateOrganisationPermission(selectedPermissionOrganisationId, role, {
          reason,
          permissions: appPermissions,
        });
      }

      await loadRolePolicy();
      setPermissionChangeReason('');
      setPermissionSaveStatus('Permission changes saved and reloaded from the live policy.');
    } catch (err) {
      const challenge = getStepUpChallengeFromError(err);
      if (challenge?.id) {
        setMatrixStepUp({ challenge, retry: saveRolePolicyChanges });
        setPermissionSaveStatus('A fresh 2SV step-up is required. Pending changes were kept.');
        return;
      }
      const status = err.response?.status;
      const errorCode = err.response?.data?.error || err.response?.data?.code || '';
      const message = err.response?.data?.message || err.response?.data?.details?.message || '';
      const salRequired = [401, 403].includes(status) && /SAL|STEP_UP|ELEVATED|2SV|TWO_SV|2-step|ASSURANCE/i.test(`${errorCode} ${message}`);
      const genericInternalError = status >= 500 && /INTERNAL_ERROR|unexpected error/i.test(`${errorCode} ${message}`);

      if (salRequired) {
        setRolePolicyRequiresSal(true);
      }

      setPermissionSaveStatus(
        salRequired
          ? 'Save failed because SAL 4 is required. Pending changes were kept.'
          : `${genericInternalError ? 'Save failed because the permission service is temporarily unavailable' : (message || 'Save failed')}. Pending changes were kept.`
      );
    } finally {
      setPermissionSaveLoading(false);
    }
  };

  const switchTopTab = (tab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tab);
    setSearchParams(nextParams, { replace: true });
  };

  const formatUserDebugId = (user) => {
    const id = user.id || user.uuid;
    if (!id) return '';

    const idText = String(id);
    const normalizedId = /^\d+$/.test(idText) ? idText.padStart(4, '0') : idText;
    return `#U${normalizedId}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return String(dateStr);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDetailDate = (dateStr) => {
    if (!dateStr) return 'Not recorded';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return String(dateStr);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatLoginMethod = (method) => {
    if (!method) return 'Unknown';
    const provider = method.provider || method.type || method.authMethod || method.loginType || 'unknown';
    return String(provider).replace(/[_-]+/g, ' ');
  };

  const formatSessionMethod = (session) => {
    const method = session.authMethod || session.loginType || session.auth_method || session.provider || session.devicePlatform || 'unknown';
    return String(method).replace(/[_-]+/g, ' ');
  };

  const getApiErrorMessage = (err, fallback) => (
    err.response?.data?.message
    || err.response?.data?.error?.message
    || err.response?.data?.error
    || err.response?.data?.code
    || err.message
    || fallback
  );

  // M004-F0620 / T-M004-0029: the machine-readable code companion to
  // `getApiErrorMessage`, kept for a `title`/aria-description on the error
  // banner rather than shown as the message itself (the owner-reported bare
  // "FORBIDDEN" defect).
  const getApiErrorCode = (err) => (
    (typeof err.response?.data?.error === 'string' ? err.response?.data?.error : err.response?.data?.error?.code)
    || err.response?.data?.code
    || ''
  );

  const getOrganisationId = (organisation) => (
    organisation?.id ||
    organisation?.uuid ||
    organisation?.organisation_id ||
    organisation?.organisationId
  );

  const getUserNumericId = (user) => user?.id || user?.user_id || user?.userId;

  const getMembershipForOrganisation = (organisationId) => (
    drawerMemberships.find((membership) => String(membership.organisationId) === String(organisationId))
  );

  const selectedMembership = getMembershipForOrganisation(membershipForm.organisationId);

  const syncMembershipForm = (organisationId, memberships = drawerMemberships) => {
    const membership = memberships.find((item) => String(item.organisationId) === String(organisationId));
    setMembershipForm({
      organisationId: organisationId || '',
      role: membership?.role || 'student',
      isPrimary: Boolean(membership?.isPrimary),
    });
  };

  // Membership discovery has no single-request source: Core exposes the target
  // user's organisations only as `GET /organisations/:id/users` per organisation
  // (see M004-D001; `GET /users/:uuid` carries no membership data), so the drawer
  // must probe every organisation the acting admin can see. On TestDeploy that is
  // 63 requests and the browser only runs 6 concurrently, so the LAST answer lands
  // ~4s after the drawer opens even though the answer is usually known from the
  // first batch.
  //
  // Two consequences were fixed here and are what these guards protect:
  //   * `Promise.all` REJECTS on the first failed organisation, so one 502 from a
  //     restarting Core discarded all 62 successful lookups and rendered "Failed
  //     to load organisation memberships" with zero rows — a total loss of a list
  //     that was almost entirely available.
  //   * Nothing at all was rendered until every organisation had answered, so a
  //     membership known at 300ms stayed invisible for 4s.
  // Results are now published as they arrive and per-organisation failures are
  // reported as a partial result instead of destroying the whole list.
  const membershipLoadTokenRef = useRef(0);

  // M004-F0620 / T-M004-0028: same "resolve while the dialog may already have
  // moved on" hazard as the membership drawer above — a stale resolution for
  // a previously-open user must never land on a since-reopened dialog.
  const editRoleAuthorityTokenRef = useRef(0);

  // Resolves which write (if any) the CURRENT actor may use to change
  // `user`'s role from the Edit User dialog. Never adds a new endpoint —
  // reuses `organisationApi.getUsers` (the same per-organisation probe the
  // Organisation Memberships drawer already runs) and
  // `getMyEffectiveOrganisationPermissions` (M001-D022-F029's
  // `GET /organisations/:id/my-permissions`).
  const resolveEditRoleAuthority = async (user) => {
    if (currentUser?.role === 'super_admin') {
      return { mode: 'global', organisationId: null, reason: '' };
    }

    const targetUserId = getUserNumericId(user);
    if (!targetUserId) {
      return { mode: 'none', organisationId: null, reason: 'This user record does not include a numeric ID for a role change.' };
    }

    // A non-super-admin caller's own `organisationApi.getMine()` result
    // (state: `organisations`) IS already the actor's own membership scope —
    // no separate "organisations I administer" lookup is needed.
    for (const organisation of organisations) {
      const organisationId = getOrganisationId(organisation);
      if (!organisationId) continue;

      let organisationUsers;
      try {
        const response = await organisationApi.getUsers(organisationId);
        organisationUsers = response.data?.data || [];
      } catch {
        continue;
      }

      const isMember = organisationUsers.some((orgUser) => (
        String(orgUser.id || orgUser.user_id || orgUser.userId) === String(targetUserId)
        || (user.uuid && String(orgUser.uuid || orgUser.user_uuid) === String(user.uuid))
      ));
      if (!isMember) continue;

      const permissions = await getMyEffectiveOrganisationPermissions(organisationId);
      if (permissions.organisation_users_role_change) {
        return { mode: 'organisation', organisationId, reason: '' };
      }
      return { mode: 'none', organisationId: null, reason: 'Your organisation role does not permit changing member roles.' };
    }

    return { mode: 'none', organisationId: null, reason: 'You do not manage an organisation this member belongs to.' };
  };

  const loadUserOrganisationMemberships = async (user) => {
    const targetUserId = getUserNumericId(user);
    const loadToken = membershipLoadTokenRef.current + 1;
    membershipLoadTokenRef.current = loadToken;
    const isStaleLoad = () => membershipLoadTokenRef.current !== loadToken;

    if (!targetUserId || organisations.length === 0) {
      setDrawerMemberships([]);
      setDrawerMembershipError(targetUserId ? '' : 'This user record does not include a numeric ID for membership changes.');
      return;
    }

    setDrawerMembershipsLoading(true);
    setDrawerMembershipError('');

    const discovered = new Map();
    const failures = [];

    const publishDiscovered = () => {
      const ordered = organisations
        .map((organisation) => discovered.get(String(getOrganisationId(organisation))))
        .filter(Boolean);
      setDrawerMemberships(ordered);
      return ordered;
    };

    try {
      await Promise.all(
        organisations.map(async (organisation) => {
          const organisationId = getOrganisationId(organisation);
          if (!organisationId) return;

          let response;
          try {
            response = await organisationApi.getUsers(organisationId);
          } catch (err) {
            failures.push(err);
            console.error(`Failed to fetch memberships for organisation ${organisationId}:`, err);
            return;
          }

          if (isStaleLoad()) return;

          const organisationUsers = response.data?.data || [];
          const match = organisationUsers.find((orgUser) => (
            String(orgUser.id || orgUser.user_id || orgUser.userId) === String(targetUserId) ||
            (user.uuid && String(orgUser.uuid || orgUser.user_uuid) === String(user.uuid))
          ));

          if (!match) return;

          discovered.set(String(organisationId), {
            organisationId,
            organisationName: organisation.name || match.organisation_name || `Organisation ${organisationId}`,
            role: match.org_role || match.user_role || match.role || 'student',
            isPrimary: Boolean(match.is_primary || match.isPrimary),
            joinedAt: match.joined_at || match.joinedAt,
            userId: match.id || match.user_id || targetUserId,
          });

          // Render what is already known instead of waiting for the slowest of
          // the remaining organisations.
          publishDiscovered();
        })
      );

      if (isStaleLoad()) return;

      const nextMemberships = publishDiscovered();

      if (failures.length) {
        const [firstFailure] = failures;
        setDrawerMembershipError(
          failures.length === organisations.length
            ? (firstFailure.response?.data?.message || firstFailure.response?.data?.error || 'Failed to load organisation memberships')
            : `${failures.length} of ${organisations.length} organisations could not be checked, so this list may be incomplete.`
        );
      }

      const currentOrganisationStillExists = organisations.some((organisation) => (
        String(getOrganisationId(organisation)) === String(membershipForm.organisationId)
      ));
      const nextOrganisationId = currentOrganisationStillExists
        ? membershipForm.organisationId
        : (nextMemberships[0]?.organisationId || getOrganisationId(organisations[0]) || '');

      syncMembershipForm(nextOrganisationId, nextMemberships);
    } catch (err) {
      console.error('Failed to fetch user organisation memberships:', err);
      if (!isStaleLoad()) {
        setDrawerMembershipError(err.response?.data?.message || err.response?.data?.error || 'Failed to load organisation memberships');
      }
    } finally {
      if (!isStaleLoad()) {
        setDrawerMembershipsLoading(false);
      }
    }
  };

  const handleMembershipOrganisationChange = (event) => {
    syncMembershipForm(event.target.value);
  };

  const handleMembershipFieldChange = (event) => {
    const { name, value, checked, type } = event.target;
    setMembershipForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveMembership = async () => {
    if (!detailDrawerUser || !membershipForm.organisationId) return;

    const targetUserId = getUserNumericId(detailDrawerUser);
    if (!targetUserId) {
      setDrawerMembershipError('This user record does not include a numeric ID for membership changes.');
      return;
    }

    setMembershipActionLoading(true);
    setDrawerMembershipError('');

    try {
      if (selectedMembership) {
        if (selectedMembership.role !== membershipForm.role) {
          await organisationApi.updateUserRole(membershipForm.organisationId, targetUserId, membershipForm.role);
        }
        if (membershipForm.isPrimary && !selectedMembership.isPrimary) {
          await organisationApi.setPrimary(membershipForm.organisationId, targetUserId);
        }
      } else {
        await organisationApi.addUser(
          membershipForm.organisationId,
          targetUserId,
          membershipForm.role,
          membershipForm.isPrimary
        );
      }

      await loadUserOrganisationMemberships(detailDrawerUser);
      await fetchUsers();
    } catch (err) {
      console.error('Failed to save organisation membership:', err);
      setDrawerMembershipError(err.response?.data?.message || err.response?.data?.error || 'Failed to save organisation membership');
    } finally {
      setMembershipActionLoading(false);
    }
  };

  const handleRemoveMembership = () => removeMembership(selectedMembership);

  const removeMembership = async (membership) => {
    if (!detailDrawerUser || !membership) return;
    const targetUserId = getUserNumericId(detailDrawerUser);
    if (!targetUserId) {
      setDrawerMembershipError('This user record does not include a numeric ID for membership changes.');
      return;
    }

    if (!window.confirm(buildRemoveFromOrganisationConfirm(
      detailDrawerUser.displayName || detailDrawerUser.email,
      membership.organisationName,
      membership.isPrimary,
    ))) {
      return;
    }

    setMembershipActionLoading(true);
    setDrawerMembershipError('');

    try {
      await organisationApi.removeUser(membership.organisationId, targetUserId);
      await loadUserOrganisationMemberships(detailDrawerUser);
      await fetchUsers();
    } catch (err) {
      console.error('Failed to remove organisation membership:', err);
      setDrawerMembershipError(err.response?.data?.message || err.response?.data?.error || 'Failed to remove organisation membership');
    } finally {
      setMembershipActionLoading(false);
    }
  };

  const getSessionKey = (session) => (
    session.sessionKey ||
    session.sessionId ||
    session.id ||
    session.key ||
    'redacted'
  );

  const getSessionTimestamp = (session) => (
    session.lastActivityAt ||
    session.lastActivity ||
    session.updatedAt ||
    session.createdAt ||
    session.issuedAt
  );

  const renderSessionSecurity = (user) => {
    const activeSessions = Array.isArray(user.activeSessions) ? user.activeSessions : [];
    const loginMethods = Array.isArray(user.loginMethods) ? user.loginMethods : [];
    const sessionCount = Number.isFinite(Number(user.sessionCount))
      ? Number(user.sessionCount)
      : activeSessions.length;
    const effectiveSal = user.effectiveMaxSal ?? user.maxSal ?? user.sal ?? 'Unknown';
    const twoSvEnforced = isTwoSvEnforced(user);

    return (
      <div className="user-security" data-testid="admin-user-session-security">
        <div className="user-security-summary">
          <span className="security-chip" data-testid="admin-user-session-count">
            {sessionCount} active {sessionCount === 1 ? 'session' : 'sessions'}
          </span>
          <span className="security-chip security-chip-strong" data-testid="admin-user-sal">
            SAL {effectiveSal}
          </span>
          <span
            className={`security-chip ${twoSvEnforced ? 'security-chip-strong' : 'security-chip-muted'}`}
            data-testid="admin-user-2sv-enforced"
          >
            {twoSvEnforced ? '2SV enforced' : '2SV optional'}
          </span>
        </div>

        <div className="user-login-methods" data-testid="admin-user-login-methods">
          {loginMethods.length > 0 ? (
            loginMethods.map((method, index) => (
              <span className="login-method-pill" key={`${method.provider || method.type || 'method'}-${index}`}>
                {formatLoginMethod(method)}
                {method.isVerified === false ? ' unverified' : ''}
              </span>
            ))
          ) : (
            <span className="login-method-pill login-method-pill-muted">No login methods</span>
          )}
        </div>

        {activeSessions.length > 0 ? (
          <details className="user-session-details">
            <summary>View session details</summary>
            <ul>
              {activeSessions.map((session, index) => (
                <li key={`${getSessionKey(session)}-${index}`}>
                  <code>{getSessionKey(session)}</code>
                  <span>{formatSessionMethod(session)}</span>
                  <span>{formatDateTime(getSessionTimestamp(session))}</span>
                </li>
              ))}
            </ul>
          </details>
        ) : (
          <span className="user-session-empty">No active session detail</span>
        )}
      </div>
    );
  };

  const openAddModal = () => {
    setModalMode('add');
    setSelectedUser(null);
    // M004-F0613: restore any draft left from a previously-incidental close of this
    // same dialog ("new"). The Add User form has no password field (M001-F0372), so
    // a draft can never carry one.
    const draft = getDraftForScope(buildDraftScopeKey('admin-user-new', 'new'));
    setFormData({
      email: '',
      displayName: '',
      phone: '',
      role: 'student',
      organisation_id: organisations.length > 0 ? organisations[0].id : '',
      forceEmailVerification: false,
      ...draft,
    });
    setFormError('');
    setFormErrorCode('');
    setEditRoleAuthority({ mode: 'add', organisationId: null, reason: '' });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    // M004-F0613: scoped to this specific user's uuid, so it can never show a draft
    // left for a different user (or for the "new" dialog).
    const draft = getDraftForScope(buildDraftScopeKey('admin-user-edit', user.uuid));
    setFormData({
      email: user.email || '',
      displayName: user.displayName || '',
      phone: user.phone || '',
      role: user.role || 'student',
      ...draft,
    });
    setFormError('');
    setFormErrorCode('');
    setShowModal(true);

    // M004-F0620 / T-M004-0028: resolve which write the Role field may use
    // for THIS user before the actor can submit one. Guarded against a race
    // with a since-reopened dialog the same way `loadUserOrganisationMemberships`
    // guards its own probe, above.
    setEditRoleAuthority({ mode: 'loading', organisationId: null, reason: '' });
    const authorityToken = editRoleAuthorityTokenRef.current + 1;
    editRoleAuthorityTokenRef.current = authorityToken;
    resolveEditRoleAuthority(user).then((authority) => {
      if (editRoleAuthorityTokenRef.current === authorityToken) {
        setEditRoleAuthority(authority);
      }
    });
  };

  const openDetailDrawer = (user) => {
    setDetailDrawerUser(user);
    setDrawerMemberships([]);
    setDrawerMembershipError('');
    setAdminSecurityLoading('');
    setAdminSecurityMessage('');
    setAdminSecurityError('');
    setAdminSecurityStepUpChallenge(null);
    setAdminSecurityStepUpRetry(null);
    setRecoveryEvidenceRef(`Admin recovery for ${user.email || user.uuid || 'selected user'}`);
    setShowPasswordResetConfirm(false);
    const firstOrganisationId = getOrganisationId(organisations[0]) || '';
    setMembershipForm({
      organisationId: firstOrganisationId,
      role: 'student',
      isPrimary: false,
    });
  };

  const closeDetailDrawer = () => {
    setDetailDrawerUser(null);
    setAdminSecurityStepUpChallenge(null);
    setAdminSecurityStepUpRetry(null);
  };

  // M004-F0646 (design §3.4): "New plan..." in the Add-plan picker deep links
  // to Bookings Hub > Price plans with returnTo=/app/admin/users?user=<id>;
  // coming back re-opens that user's drawer. Only with the flag on, and only
  // once per requested user, so closing the drawer never re-opens it.
  const deepLinkedUserHandledRef = useRef('');
  const deepLinkedUserId = searchParams.get('user');
  useEffect(() => {
    if (!isMemberPricePlansBackendAvailable() || !deepLinkedUserId) return;
    if (deepLinkedUserHandledRef.current === deepLinkedUserId || organisations.length === 0) return;
    const match = users.find((candidate) => String(candidate.id) === deepLinkedUserId || String(candidate.uuid) === deepLinkedUserId);
    if (match) {
      deepLinkedUserHandledRef.current = deepLinkedUserId;
      openDetailDrawer(match);
    }
    // openDetailDrawer is recreated each render; the ref makes this one-shot
  }, [deepLinkedUserId, users, organisations]);

  const editFromDetailDrawer = () => {
    if (!detailDrawerUser) return;
    const user = detailDrawerUser;
    closeDetailDrawer();
    openEditModal(user);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setFormError('');
    setFormErrorCode('');
    editRoleAuthorityTokenRef.current += 1; // invalidate any in-flight resolution
    setEditRoleAuthority({ mode: 'add', organisationId: null, reason: '' });
  };

  // M004-F0612 / T-M004-0019: dismiss only on a genuine outside press-and-release;
  // a text-selection drag from inside a dialog to outside it must not close it.
  const detailDrawerDismissHandlers = useDismissOnOutsideClick(closeDetailDrawer);
  const userModalDismissHandlers = useDismissOnOutsideClick(closeModal);
  const bulkDeleteModalDismissHandlers = useDismissOnOutsideClick(() => setShowBulkDeleteModal(false));
  const passwordResetConfirmDismissHandlers = useDismissOnOutsideClick(() => setShowPasswordResetConfirm(false));

  // M004-D016 §4.1 item 2 / T-M004-0019 case 4: Escape dismisses the reported
  // Add/Edit User dialog regardless of which field currently has focus. Scoped
  // to this one dialog (the reported defect's own surface, and the dialog
  // T-M004-0019's required evidence names explicitly) rather than added
  // app-wide, to keep this fix's diff to dismissal semantics.
  useEffect(() => {
    if (!showModal) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showModal]); // eslint-disable-line

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    // For checkboxes, use 'checked' property; for other inputs use 'value'
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormErrorCode('');
    setFormLoading(true);
    // Captured before closeModal() resets selectedUser back to null.
    const editRecordId = selectedUser?.uuid;

    try {
      if (modalMode === 'add') {
        // Create user via registration endpoint
        const response = await userApi.createUser({
          email: formData.email,
          // M001-F0372 / Core #242 + #437: an administrator never chooses another
          // person's password. No `password` key is ever sent; Core generates one
          // server-side and emails the person a set-password link.
          displayName: formData.displayName,
          phone: formData.phone || undefined,
          role: formData.role,
          organisation_id: parseInt(formData.organisation_id, 10),
          forceEmailVerification: formData.forceEmailVerification,
        });
        if (response.data.success) {
          const created = response.data.data || {};
          if (created.password_generated) {
            setCreateNotice(created.set_password_email === 'sent'
              ? { kind: 'success', text: 'Account created. We emailed them a link to set their password.' }
              : {
                kind: 'warning',
                text: "Account created, but the set-password email was not confirmed sent. Use 'Send password reset email' in the user drawer.",
              });
          } else {
            setCreateNotice(null);
          }
          closeModal();
          fetchUsers();
          // M004-F0613: a draft is cleared only on successful submit, never merely
          // because the dialog closed.
          clearDraftForScope(buildDraftScopeKey('admin-user-new', 'new'));
        }
      } else {
        // M004-F0620 / T-M004-0028: the global `PUT /users/:uuid` route is
        // gated unconditionally on `users_role_change_global`
        // (studioaeye-Core `user.routes.ts`) — an org admin holds
        // `organisation_users_role_change` instead, on the organisation, not
        // globally. `editRoleAuthority` (resolved when this dialog opened)
        // says which write the Role field may use for THIS target user; a
        // role change is sent through exactly one of them, never both, and
        // never through the global route when the actor is not authorised
        // to change it globally (even when unchanged — the whole global
        // route requires that capability, not just a changed `role` field).
        const roleChanged = formData.role !== selectedUser.role;
        const canChangeRoleGlobally = editRoleAuthority.mode === 'global';
        const canChangeRoleViaOrganisation = editRoleAuthority.mode === 'organisation' && editRoleAuthority.organisationId;

        const nonRoleFieldsChanged = formData.displayName !== selectedUser.displayName
          || (formData.phone || '') !== (selectedUser.phone || '');

        const updateData = {
          displayName: formData.displayName,
          phone: formData.phone || undefined,
        };
        if (canChangeRoleGlobally && roleChanged) {
          updateData.role = formData.role;
        }

        // Only call the global route when it has something authorised to do:
        // Display Name/phone always go through it (Core exposes no other
        // route for those fields yet); `role` only when the actor holds
        // `users_role_change_global`. Skipping the call entirely when it
        // would carry nothing but an omitted/unauthorised role is exactly
        // what stops a role-only edit from tripping the global gate for an
        // org admin.
        const needsGlobalCall = nonRoleFieldsChanged || (canChangeRoleGlobally && roleChanged);

        if (needsGlobalCall) {
          await userApi.updateUser(selectedUser.uuid, updateData);
        }
        if (canChangeRoleViaOrganisation && roleChanged) {
          await organisationApi.updateUserRole(editRoleAuthority.organisationId, getUserNumericId(selectedUser), formData.role);
        }

        closeModal();
        fetchUsers();
        clearDraftForScope(buildDraftScopeKey('admin-user-edit', editRecordId));
      }
    } catch (err) {
      console.error('Form submission error:', err);
      // M004-F0620 / T-M004-0029: show the API's human message
      // (`data.message`) rather than its bare error code (`data.error`) —
      // the owner-reported "FORBIDDEN" defect. The code is not discarded, it
      // moves to `formErrorCode` for the banner's title/aria-description.
      setFormError(getApiErrorMessage(err, 'Operation failed'));
      setFormErrorCode(getApiErrorCode(err));
    } finally {
      setFormLoading(false);
    }
  };

  const handleSuspend = async (user) => {
    if (!window.confirm(`Are you sure you want to ${user.status === 'suspended' ? 'activate' : 'suspend'} ${user.displayName}?`)) {
      return;
    }
    try {
      setUserActionLoading(user.uuid);
      if (user.status === 'suspended') {
        await userApi.reactivateUser(user.uuid);
      } else {
        await userApi.suspendUserAdmin(user.uuid);
      }
      fetchUsers();
    } catch (err) {
      console.error('Failed to update user status:', err);
      alert(err.response?.data?.error || 'Failed to update user status');
    } finally {
      setUserActionLoading(null);
    }
  };

  const handleApproveUser = async (user) => {
    if (!window.confirm(`Approve ${user.displayName || user.email}?`)) {
      return;
    }
    try {
      setUserActionLoading(user.uuid);
      await userApi.approveUser(user.uuid);
      fetchUsers();
    } catch (err) {
      console.error('Failed to approve user:', err);
      alert(err.response?.data?.error || 'Failed to approve user');
    } finally {
      setUserActionLoading(null);
    }
  };

  const handleRejectUser = async (user) => {
    if (!window.confirm(`Reject ${user.displayName || user.email}?`)) {
      return;
    }
    try {
      setUserActionLoading(user.uuid);
      await userApi.rejectUser(user.uuid);
      fetchUsers();
    } catch (err) {
      console.error('Failed to reject user:', err);
      alert(err.response?.data?.error || 'Failed to reject user');
    } finally {
      setUserActionLoading(null);
    }
  };

  const handleReconsiderUser = async (user) => {
    if (!window.confirm(`Move ${user.displayName || user.email} back to pending?`)) {
      return;
    }
    try {
      setUserActionLoading(user.uuid);
      await userApi.reconsiderUser(user.uuid);
      fetchUsers();
    } catch (err) {
      console.error('Failed to reconsider user:', err);
      alert(err.response?.data?.error || 'Failed to reconsider user');
    } finally {
      setUserActionLoading(null);
    }
  };

  const handleDelete = async (user) => {
    if (!canDeleteUsersNow) return;
    if (!window.confirm(buildDeleteUserConfirm(user.displayName))) {
      return;
    }
    try {
      await userApi.deleteUser(user.uuid);
      fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleVerifyEmail = async (user) => {
    if (!window.confirm(`Mark ${user.displayName}'s email as verified?`)) {
      return;
    }
    try {
      setUserActionLoading(user.uuid);
      await userApi.verifyUserEmail(user.uuid);
      fetchUsers();
    } catch (err) {
      console.error('Failed to verify email:', err);
      alert(err.response?.data?.error || 'Failed to verify email');
    } finally {
      setUserActionLoading(null);
    }
  };

  const runAdminSecurityAction = async (actionName, action, retryFactory, successMessage) => {
    setAdminSecurityLoading(actionName);
    setAdminSecurityMessage('');
    setAdminSecurityError('');

    try {
      await action();
      setAdminSecurityMessage(successMessage);
      setAdminSecurityStepUpChallenge(null);
      setAdminSecurityStepUpRetry(null);
    } catch (err) {
      const challenge = getStepUpChallengeFromError(err);
      if (challenge?.id) {
        setAdminSecurityStepUpChallenge(challenge);
        setAdminSecurityStepUpRetry(() => retryFactory);
        setAdminSecurityError('A fresh 2SV step-up is required before this admin action can continue.');
        return;
      }

      setAdminSecurityError(getApiErrorMessage(err, 'Admin account security action failed.'));
    } finally {
      setAdminSecurityLoading('');
    }
  };

  const completeAdminSecurityStepUp = async () => {
    const retry = adminSecurityStepUpRetry;
    setAdminSecurityStepUpChallenge(null);
    setAdminSecurityStepUpRetry(null);
    if (retry) {
      await retry();
    }
  };

  const closeAdminSecurityStepUp = () => {
    setAdminSecurityStepUpChallenge(null);
    setAdminSecurityStepUpRetry(null);
  };

  const handleIssueRecoveryAssertion = async () => {
    if (!detailDrawerUser?.uuid) return;
    const evidenceRef = recoveryEvidenceRef.trim();
    if (evidenceRef.length < 3) {
      setAdminSecurityMessage('');
      setAdminSecurityError('Enter an evidence reference before issuing recovery.');
      return;
    }

    const action = () => userApi.issueRecoveryAssertion(detailDrawerUser.uuid, {
      evidence_ref: evidenceRef,
    });

    await runAdminSecurityAction(
      'recovery',
      action,
      handleIssueRecoveryAssertion,
      'Recovery assertion issued. The user can re-enrol 2SV from the recovery email.',
    );
  };

  const handleDisableLoginTwoSv = async () => {
    if (!detailDrawerUser?.uuid) return;
    if (!window.confirm(`Turn off login-time 2SV requirement for ${detailDrawerUser.displayName || detailDrawerUser.email}?`)) {
      return;
    }

    const action = async () => {
      const response = await userApi.updateUser(detailDrawerUser.uuid, { two_sv_enforced: false });
      const updatedUser = response.data?.data?.user || { ...detailDrawerUser, two_sv_enforced: false, twoSvEnforced: false };
      setDetailDrawerUser(updatedUser);
      await fetchUsers();
    };

    await runAdminSecurityAction(
      'disable-login-2sv',
      action,
      handleDisableLoginTwoSv,
      'Login-time 2SV requirement turned off for this user.',
    );
  };

  const handleSendPasswordResetEmail = async () => {
    if (!detailDrawerUser?.uuid) return;
    setShowPasswordResetConfirm(false);

    await runAdminSecurityAction(
      'password-reset-email',
      () => userApi.sendPasswordResetEmail(detailDrawerUser.uuid),
      handleSendPasswordResetEmail,
      'Password reset email sent to the user’s registered address.',
    );
  };

  // Bulk selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allUuids = new Set(filteredUsers.map(u => u.uuid));
      setSelectedUsers(allUuids);
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleToggleSelection = (uuid) => {
    const newSelection = new Set(selectedUsers);
    if (newSelection.has(uuid)) {
      newSelection.delete(uuid);
    } else {
      newSelection.add(uuid);
    }
    setSelectedUsers(newSelection);
  };

  const isAllSelected = filteredUsers.length > 0 && selectedUsers.size === filteredUsers.length;
  const isPartiallySelected = selectedUsers.size > 0 && selectedUsers.size < filteredUsers.length;
  const canManageSelectedUserBotKeys = currentUser?.role === 'super_admin'
    && detailDrawerUser
    && !isAppUserRole(detailDrawerUser.role);
  const canManageSelectedUserSecurity = currentUser?.role === 'super_admin'
    && detailDrawerUser
    && !isAppUserRole(detailDrawerUser.role);

  useEffect(() => {
    if (detailDrawerUser) {
      loadUserOrganisationMemberships(detailDrawerUser);
    }
  }, [detailDrawerUser, organisations]);

  // Resolve, per membership organisation, whether the CALLER holds the org
  // capability Core's DELETE /organisations/:id/users/:userId asserts
  // (`organisation_users_remove`). Read from my-permissions, which fails
  // closed ({}) on any error; never from a role name. Re-asked each time a
  // different user's drawer opens so a changed grant is not served stale.
  const drawerUserKey = detailDrawerUser ? (detailDrawerUser.uuid || getUserNumericId(detailDrawerUser)) : null;
  useEffect(() => {
    removePermissionGenerationRef.current += 1;
    removePermissionRequestedRef.current = new Set();
    setDrawerRemovePermitted({});
  }, [drawerUserKey]);

  useEffect(() => {
    const generation = removePermissionGenerationRef.current;
    drawerMemberships.forEach((membership) => {
      const key = String(membership.organisationId);
      if (removePermissionRequestedRef.current.has(key)) return;
      removePermissionRequestedRef.current.add(key);
      Promise.resolve()
        .then(() => getMyEffectiveOrganisationPermissions(membership.organisationId))
        .catch(() => ({}))
        .then((permissions) => {
          if (removePermissionGenerationRef.current !== generation) return;
          setDrawerRemovePermitted((prev) => ({ ...prev, [key]: canRemoveFromOrganisation(permissions) }));
        });
    });
  }, [drawerMemberships]);

  const handleBulkDelete = async () => {
    if (selectedUsers.size === 0 || !canDeleteUsersNow) return;

    setShowBulkDeleteModal(false);
    setLoading(true);

    try {
      const deletePromises = Array.from(selectedUsers).map(uuid =>
        userApi.deleteUser(uuid)
      );
      await Promise.all(deletePromises);
      setSelectedUsers(new Set());
      await fetchUsers();
    } catch (err) {
      console.error('Failed to delete users:', err);
      alert(err.response?.data?.error || 'Failed to delete some users');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  return (
    <Layout>
      <div className="admin-users">
        <header className="page-header">
          <div>
            <h1>User Management</h1>
            <p className="subtitle">
              {pagination.total} total users
              {selectedUsers.size > 0 && ` • ${selectedUsers.size} selected`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {canDeleteUsersNow && selectedUsers.size > 0 && (
              <button
                className="btn btn-danger"
                onClick={() => setShowBulkDeleteModal(true)}
                title={`Delete ${selectedUsers.size} selected user(s)`}
              >
                🗑️ Delete {selectedUsers.size}
              </button>
            )}
            <button className="btn btn-primary" onClick={openAddModal}>+ Add User</button>
          </div>
        </header>

        {createNotice && (
          <div
            className={createNotice.kind === 'warning' ? 'form-error' : 'form-success'}
            role={createNotice.kind === 'warning' ? 'alert' : 'status'}
            data-testid="admin-add-user-create-notice"
          >
            {createNotice.text}{' '}
            <button type="button" className="btn btn-link" onClick={() => setCreateNotice(null)}>Dismiss</button>
          </div>
        )}

        <div className="admin-user-tabs" role="tablist" aria-label="Admin user management sections">
          <button
            type="button"
            role="tab"
            aria-selected={activeTopTab === 'users'}
            className={`admin-user-tab ${activeTopTab === 'users' ? 'active' : ''}`}
            onClick={() => switchTopTab('users')}
          >
            User Management
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTopTab === 'roles'}
            className={`admin-user-tab ${activeTopTab === 'roles' ? 'active' : ''}`}
            onClick={() => switchTopTab('roles')}
          >
            Role Management
          </button>
        </div>

        {activeTopTab === 'users' ? (
          <>
            <div className="filters-bar">
              <input
                type="text"
                placeholder="Search users..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
                data-testid="admin-users-search"
              />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="filter-select"
                aria-label="Role filter"
                data-testid="admin-users-role-filter"
              >
                <option value="all">All Roles</option>
                <option value="super_admin">Super Admin</option>
                <option value="admin">Admin</option>
                <option value="teacher">Teacher</option>
                <option value="student">Student</option>
                <option value="event_simple">Event Simple</option>
                <option value="simple_room_controls">Simple Room Controls</option>
                <option value="app">App Role Family</option>
                <option value="App_Test">App Test</option>
                <option value="App_A">App A</option>
                <option value="App_B">App B</option>
                <option value="App_Bot_A">App Bot A</option>
                <option value="App_Bot_B">App Bot B</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
                aria-label="Status filter"
                data-testid="admin-users-status-filter"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
                <option value="rejected">Rejected</option>
                <option value="archived">Archived</option>
                <option value="disabled">Disabled</option>
                <option value="deleted">Deleted</option>
              </select>
              <select
                value={organisationFilter}
                onChange={(e) => setOrganisationFilter(e.target.value)}
                className="filter-select"
                aria-label="Organisation filter"
                data-testid="admin-users-organisation-filter"
              >
                <option value="all">All Organisations</option>
                {organisations.map((org) => (
                  <option key={org.id || org.uuid} value={org.id || org.uuid}>{org.name}</option>
                ))}
              </select>
            </div>

            {error && <div className="error-message">{error}</div>}

            {loading ? (
              <div className="loading-spinner">Loading users...</div>
            ) : (
              <>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        {canDeleteUsersNow && (
                        <th style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={isAllSelected}
                            ref={input => {
                              if (input) {
                                input.indeterminate = isPartiallySelected;
                              }
                            }}
                            onChange={handleSelectAll}
                            aria-label="Select all users"
                          />
                        </th>
                        )}
                        <th>User</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Sessions / SAL / Login</th>
                        <th>Last Login</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.length === 0 ? (
                        <tr><td colSpan={canDeleteUsersNow ? 7 : 6} className="no-data">No users found</td></tr>
                      ) : (
                        filteredUsers.map((user) => {
                          const isServiceIdentity = isAppUserRole(user.role);

                          return (
                          <tr
                            key={user.id || user.uuid}
                            className={selectedUsers.has(user.uuid) ? 'bg-blue-50' : ''}
                            style={selectedUsers.has(user.uuid) ? { backgroundColor: '#eff6ff' } : {}}
                            data-testid={isServiceIdentity ? 'admin-user-service-row' : undefined}
                          >
                            {canDeleteUsersNow && (
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedUsers.has(user.uuid)}
                                onChange={() => handleToggleSelection(user.uuid)}
                                aria-label={`Select ${user.displayName}`}
                              />
                            </td>
                            )}
                            <td>
                              <div className="user-cell">
                                <div className="user-avatar">{(user.displayName || user.email)?.[0]?.toUpperCase() || '?'}</div>
                                <div className="user-info">
                                  <span className="user-name">
                                    {user.displayName || 'No name'}
                                    {formatUserDebugId(user) ? (
                                      <span className="user-debug-id" data-testid="admin-user-id">
                                        {' '}({formatUserDebugId(user)})
                                      </span>
                                    ) : null}
                                  </span>
                                  <span className="user-email">{user.email}</span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="role-cell">
                                <span className={`role-badge ${isServiceIdentity ? 'app' : user.role}`}>{user.role}</span>
                                {isServiceIdentity && (
                                  <span className="service-identity-chip" data-testid="admin-user-service-identity">
                                    Service identity
                                  </span>
                                )}
                              </div>
                            </td>
                            <td><span className={`status-badge ${user.status}`}>{user.status}</span></td>
                            <td>{renderSessionSecurity(user)}</td>
                            <td>{formatDate(user.lastLoginAt)}</td>
                            <td>
                              <div className="action-buttons">
                                <button className="btn btn-sm btn-outline" title="View" onClick={() => openDetailDrawer(user)}>View</button>
                                {!isServiceIdentity && (
                                  <button className="btn-icon" title="Edit" onClick={() => openEditModal(user)}>✏️</button>
                                )}
                                {!isServiceIdentity && needsEmailVerificationAction(user) && (
                                  <button
                                    className="btn-icon btn-verify-email"
                                    data-testid="admin-user-verify-email"
                                    title="Verify Email"
                                    onClick={() => handleVerifyEmail(user)}
                                    disabled={userActionLoading === user.uuid}
                                    style={{
                                      backgroundColor: '#ef4444',
                                      color: 'white',
                                      borderRadius: '4px',
                                      padding: '4px 8px',
                                      fontWeight: 'bold',
                                      border: '2px solid #dc2626'
                                    }}
                                  >
                                    Verify Email
                                  </button>
                                )}
                                {!isServiceIdentity && user.status === 'pending' && (
                                  <>
                                    <button
                                      className="btn btn-sm btn-primary"
                                      title="Approve User"
                                      onClick={() => handleApproveUser(user)}
                                      disabled={userActionLoading === user.uuid}
                                    >
                                      Approve
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline"
                                      title="Reject User"
                                      onClick={() => handleRejectUser(user)}
                                      disabled={userActionLoading === user.uuid}
                                    >
                                      Reject
                                    </button>
                                  </>
                                )}
                                {!isServiceIdentity && user.status === 'rejected' && (
                                  <button
                                    className="btn btn-sm btn-outline"
                                    title="Reconsider User"
                                    onClick={() => handleReconsiderUser(user)}
                                    disabled={userActionLoading === user.uuid}
                                  >
                                    Reconsider
                                  </button>
                                )}
                                {!isServiceIdentity && (user.status === 'active' || user.status === 'suspended') && (
                                  <button
                                    className="btn-icon"
                                    title={user.status === 'active' ? 'Suspend' : 'Reactivate'}
                                    onClick={() => handleSuspend(user)}
                                    disabled={userActionLoading === user.uuid}
                                  >
                                    {user.status === 'active' ? '🚫' : '✅'}
                                  </button>
                                )}
                                {!isServiceIdentity ? (
                                  canDeleteUsersNow ? (
                                    <button className="btn-icon" title="Delete" data-testid="admin-user-delete" onClick={() => handleDelete(user)}>🗑️</button>
                                  ) : null
                                ) : (
                                  <span className="restricted-actions-note" data-testid="admin-user-service-actions">
                                    Restricted actions
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="pagination-bar" data-testid="admin-users-pagination">
                  <div className="pagination-info" data-testid="admin-users-page-info">
                    Page {pagination.page} of {pagination.totalPages} ({pageStart}-{pageEnd} of {pagination.total} users)
                  </div>
                  <div className="pagination-controls">
                    <label htmlFor="admin-users-page-size">Rows</label>
                    <select
                      id="admin-users-page-size"
                      className="filter-select pagination-page-size"
                      value={pageSize}
                      onChange={(event) => setPageSize(Number(event.target.value))}
                      data-testid="admin-users-page-size"
                    >
                      {[10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={!canGoPrevious}
                      data-testid="admin-users-prev-page"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => setCurrentPage((page) => Math.min(pagination.totalPages, page + 1))}
                      disabled={!canGoNext}
                      data-testid="admin-users-next-page"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <section className="role-management-panel" aria-labelledby="role-management-heading">
            <div className="role-management-header">
              <div>
                <h2 id="role-management-heading">Role Management</h2>
                <p className="subtitle">Permission policy</p>
              </div>
              <span className={`status-badge ${rolePolicyEditMode ? 'success' : 'info'}`} data-testid="role-management-access-state">
                {rolePolicyEditMode ? 'Edit mode' : 'Read only'}
              </span>
            </div>
            {rolePolicyRequiresSal && (
              <div className="role-management-notice" data-testid="role-management-sal-required">
                <strong>SAL 4 required</strong>
                <span>Permission editing is read-only until an elevated 2SV step-up is completed.</span>
              </div>
            )}
            {!rolePolicyRequiresSal && rolePolicyError && (
              <div className="role-management-notice" data-testid="role-management-readonly-notice">
                <strong>Read-only</strong>
                <span>{rolePolicyError}</span>
              </div>
            )}
            <div className="permission-mode-tabs" role="tablist" aria-label="Role management permission modes">
              <button
                type="button"
                role="tab"
                aria-selected={roleManagementMode === 'system'}
                className={`permission-mode-tab ${roleManagementMode === 'system' ? 'active' : ''}`}
                onClick={() => setRoleManagementMode('system')}
              >
                System Permissions
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={roleManagementMode === 'organisation'}
                className={`permission-mode-tab ${roleManagementMode === 'organisation' ? 'active' : ''}`}
                onClick={() => setRoleManagementMode('organisation')}
              >
                Organisation Permissions
              </button>
            </div>
            <div className="permission-mode-panel">
              <h3>{roleManagementMode === 'system' ? 'System Permissions' : 'Organisation Permissions'}</h3>
              {roleManagementMode === 'organisation' && (
                <label className="permission-organisation-field" htmlFor="permissionOrganisation">
                  Organisation
                  <select
                    id="permissionOrganisation"
                    value={selectedPermissionOrganisationId}
                    onChange={(event) => setSelectedPermissionOrganisationId(event.target.value)}
                    data-testid="role-management-organisation-select"
                  >
                    {organisations.map((organisation) => (
                      <option key={organisation.id} value={organisation.id}>
                        {organisation.name || organisation.displayName || `Organisation ${organisation.id}`}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {rolePolicyLoading ? (
                <div className="loading-spinner">Loading permission policy...</div>
              ) : rolePolicyRows.length > 0 ? (
                <div className="permission-matrix-wrap" data-testid="role-management-permission-mode">
                  <table className="permission-matrix">
                    <thead>
                      <tr>
                        <th scope="col">Role</th>
                        {permissionKeys.map((permission) => (
                          <th key={permission} scope="col">{formatPermissionLabel(permission)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rolePolicyRows.map((row) => (
                        <tr key={getPolicyRowKey(row)}>
                          <th scope="row">{row.role}</th>
                          {permissionKeys.map((permission) => (
                            <td key={permission}>
                              <label className="permission-toggle" title={`${row.role} ${formatPermissionLabel(permission)}`}>
                                <input
                                  type="checkbox"
                                  checked={getDisplayedPermissionValue(row, permission)}
                                  disabled={!rolePolicyEditMode}
                                  onChange={(event) => updatePermissionPendingChange(row, permission, event.target.checked)}
                                  data-testid={`permission-toggle-${roleManagementMode}-${row.role}-${permission}`}
                                />
                                <span>{getDisplayedPermissionValue(row, permission) ? 'Allowed' : 'Denied'}</span>
                              </label>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="permission-placeholder-grid" data-testid="role-management-permission-mode">
                  {PERMISSION_PLACEHOLDER_ROLES.map((role) => (
                    <span key={role} className="permission-role-chip">{role}</span>
                  ))}
                </div>
              )}
              {roleManagementMode === 'organisation' && !rolePolicyLoading && (
                <AppPermissionMatrix
                  applications={applicationPermissions}
                  pendingChanges={appPermissionPending}
                  editable={appPermissionEditable}
                  canDeclare={appPermissionDeclarable}
                  onChange={updateAppPermissionPending}
                  onOpenDeclaration={(app) => setDeclarationAppCode(app.appCode)}
                />
              )}
              <section className="app-access-policy" aria-labelledby="app-access-policy-heading">
                <div className="app-access-policy-heading">
                  <div>
                    <h4 id="app-access-policy-heading">Application Access</h4>
                    <p>Resolution order: Global default → Organisation value → Organisation user value.</p>
                  </div>
                  <span className="status-badge info">{appAccessPendingCount} pending</span>
                </div>
                {appAccessError && <div className="role-management-notice">{appAccessError}</div>}
                {appAccessLoading ? (
                  <div className="loading-spinner">Loading application access...</div>
                ) : appAccessRows.length > 0 ? (
                  <div className="permission-matrix-wrap" data-testid="app-access-role-matrix">
                    <table className="permission-matrix app-access-matrix">
                      <thead>
                        <tr>
                          <th scope="col">Application</th>
                          <th scope="col">Role</th>
                          <th scope="col">Global default</th>
                          {roleManagementMode === 'organisation' && <th scope="col">Organisation value</th>}
                          <th scope="col">Effective</th>
                          <th scope="col">Source</th>
                          <th scope="col">Set value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {appAccessRows.map((row) => {
                          const value = displayedAppAccessValue(row);
                          const resolved = roleManagementMode === 'system'
                            ? resolveAppAccess(value)
                            : resolveAppAccess(row.globalDefault, value);
                          return (
                            <tr key={appAccessKey(row)}>
                              <th scope="row">{row.appCode}</th>
                              <td>{row.role}</td>
                              <td>{formatAccessValue(row.globalDefault)}</td>
                              {roleManagementMode === 'organisation' && <td>{formatAccessValue(row.organisationValue)}</td>}
                              <td>{formatAccessValue(resolved.allowed)}</td>
                              <td>{resolved.source}</td>
                              <td>
                                <select
                                  value={appAccessSelectValue(value)}
                                  disabled={!appAccessEditMode}
                                  onChange={(event) => updateAppAccessPending(row, event.target.value)}
                                  data-testid={`app-access-${roleManagementMode}-${row.appCode}-${row.role}`}
                                >
                                  {roleManagementMode === 'organisation' && <option value="inherit">Inherit global</option>}
                                  <option value="allow">Allowed</option>
                                  <option value="deny">Denied</option>
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="permission-audit-empty">No registered application access policies.</p>
                )}

                {roleManagementMode === 'organisation' && (
                  <div className="app-access-user-policy">
                    <label className="permission-organisation-field" htmlFor="appAccessUser">
                      Organisation user override
                      <select
                        id="appAccessUser"
                        value={selectedAppAccessUserId}
                        onChange={(event) => setSelectedAppAccessUserId(event.target.value)}
                        data-testid="app-access-user-select"
                      >
                        <option value="">Select a user</option>
                        {appAccessUsers.map((candidate) => (
                          <option key={candidate.id || candidate.uuid} value={candidate.id}>
                            {candidate.displayName || candidate.email || `User ${candidate.id}`}
                          </option>
                        ))}
                      </select>
                    </label>
                    {selectedAppAccessUserId && userAppAccessRows.length > 0 && (
                      <div className="permission-matrix-wrap" data-testid="app-access-user-matrix">
                        <table className="permission-matrix app-access-matrix">
                          <thead>
                            <tr>
                              <th scope="col">Application</th>
                              <th scope="col">Role</th>
                              <th scope="col">Global default</th>
                              <th scope="col">Organisation value</th>
                              <th scope="col">User value</th>
                              <th scope="col">Effective</th>
                              <th scope="col">Source</th>
                              <th scope="col">Set user value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userAppAccessRows.map((row) => {
                              const value = displayedUserAppAccessValue(row);
                              const resolved = resolveAppAccess(row.globalDefault, row.organisationValue, value);
                              return (
                                <tr key={`user:${row.appCode}`}>
                                  <th scope="row">{row.appCode}</th>
                                  <td>{row.role}</td>
                                  <td>{formatAccessValue(row.globalDefault)}</td>
                                  <td>{formatAccessValue(row.organisationValue)}</td>
                                  <td>{formatAccessValue(row.userValue)}</td>
                                  <td>{formatAccessValue(resolved.allowed)}</td>
                                  <td>{resolved.source}</td>
                                  <td>
                                    <select
                                      value={appAccessSelectValue(value)}
                                      disabled={!appAccessEditMode}
                                      onChange={(event) => updateUserAppAccessPending(row, event.target.value)}
                                      data-testid={`app-access-user-${row.appCode}`}
                                    >
                                      <option value="inherit">Inherit organisation/role</option>
                                      <option value="allow">Allowed</option>
                                      <option value="deny">Denied</option>
                                    </select>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {appAccessSaveStatus && (
                  <div className={`permission-save-status ${/failed/i.test(appAccessSaveStatus) ? 'error' : 'success'}`}>
                    {appAccessSaveStatus}
                  </div>
                )}
                <div className="permission-save-panel app-access-save-panel">
                  <label className="permission-reason-field" htmlFor="appAccessChangeReason">
                    Application access change reason
                    <textarea
                      id="appAccessChangeReason"
                      value={appAccessChangeReason}
                      onChange={(event) => setAppAccessChangeReason(event.target.value)}
                      placeholder="Describe why this application access change is needed"
                      data-testid="app-access-change-reason"
                    />
                  </label>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!canSaveAppAccess}
                    onClick={saveAppAccessChanges}
                    data-testid="app-access-save"
                  >
                    {appAccessSaveLoading ? 'Saving...' : 'Save Application Access'}
                  </button>
                </div>
              </section>
              <div className="permission-preview-grid" data-testid="role-management-permission-preview">
                {ROLE_PERMISSION_PREVIEW.map((permission) => (
                  <span key={permission} className="permission-preview-chip">
                    {permission}
                  </span>
                ))}
              </div>
              <div className="permission-audit-preview" data-testid="role-management-audit-preview">
                <h4>Audit Preview</h4>
                <div className="permission-audit-table" role="table" aria-label="Permission audit preview">
                  <div className="permission-audit-row permission-audit-header" role="row">
                    <span role="columnheader">Role</span>
                    <span role="columnheader">Permission</span>
                    <span role="columnheader">Before</span>
                    <span role="columnheader">After</span>
                  </div>
                  {pendingAuditRows.length > 0 || appPermissionAuditRows.length > 0 ? (
                    <>
                    {appPermissionAuditRows.map((change) => (
                      <div
                        key={`app-${change.role}-${change.permission}`}
                        className="permission-audit-row"
                        role="row"
                        data-testid="role-management-audit-row"
                      >
                        <span role="cell">{change.role}</span>
                        <span role="cell">{change.permission}</span>
                        <span role="cell">{change.before}</span>
                        <span role="cell">{change.after}</span>
                      </div>
                    ))}
                    {pendingAuditRows.map((change) => (
                      <div
                        key={`${change.role}-${change.permission}`}
                        className="permission-audit-row"
                        role="row"
                        data-testid="role-management-audit-row"
                      >
                        <span role="cell">{change.role}</span>
                        <span role="cell">{change.permission}</span>
                        <span role="cell">{change.before ? 'Allowed' : 'Denied'}</span>
                        <span role="cell">{change.after ? 'Allowed' : 'Denied'}</span>
                      </div>
                    ))}
                    </>
                  ) : (
                    <div className="permission-audit-empty" role="row">
                      <span>No pending permission changes</span>
                    </div>
                  )}
                </div>
              </div>
              {permissionSaveStatus && (
                <div
                  className={`permission-save-status ${permissionSaveStatus.includes('failed') ? 'error' : 'success'}`}
                  data-testid="role-management-save-status"
                >
                  {permissionSaveStatus}
                </div>
              )}
              <div className="permission-save-panel" data-testid="role-management-save-panel">
                <label className="permission-reason-field" htmlFor="permissionChangeReason">
                  Change reason
                  <textarea
                    id="permissionChangeReason"
                    value={permissionChangeReason}
                    onChange={(event) => setPermissionChangeReason(event.target.value)}
                    placeholder="Describe why this permission change is needed"
                    data-testid="role-management-change-reason"
                  />
                </label>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!canSaveRolePolicy}
                  onClick={saveRolePolicyChanges}
                  data-testid="role-management-save"
                >
                  {permissionSaveLoading ? 'Saving...' : 'Save Permission Changes'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={!rolePolicyRequiresSal}
                  data-testid="role-management-elevate"
                >
                  Complete 2SV Step-up
                </button>
              </div>
            </div>
          </section>
        )}

        {detailDrawerUser && (
          <div className="drawer-overlay" {...detailDrawerDismissHandlers}>
            <aside
              className="user-detail-drawer"
              aria-label="User detail drawer"
              data-testid="admin-user-detail-drawer"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="drawer-header">
                <div>
                  <h2>User Details</h2>
                  <p className="subtitle">{detailDrawerUser.displayName || detailDrawerUser.email}</p>
                </div>
                <button className="modal-close" onClick={closeDetailDrawer} aria-label="Close user detail drawer">&times;</button>
              </div>

              <dl className="user-detail-list">
                <div>
                  <dt>Name</dt>
                  <dd>{detailDrawerUser.displayName || 'No name'}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{detailDrawerUser.email || 'No email'}</dd>
                </div>
                <div>
                  <dt>User ID</dt>
                  <dd>{formatUserDebugId(detailDrawerUser) || 'Not recorded'}</dd>
                </div>
                <div>
                  <dt>Role</dt>
                  <dd><span className={`role-badge ${detailDrawerUser.role}`}>{detailDrawerUser.role || 'unknown'}</span></dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd><span className={`status-badge ${detailDrawerUser.status}`}>{detailDrawerUser.status || 'unknown'}</span></dd>
                </div>
                <div>
                  <dt>Created</dt>
                  <dd>{formatDetailDate(detailDrawerUser.createdAt || detailDrawerUser.created_at)}</dd>
                </div>
                <div>
                  <dt>Last login</dt>
                  <dd>{formatDetailDate(detailDrawerUser.lastLoginAt || detailDrawerUser.last_login_at)}</dd>
                </div>
              </dl>

              {isAppUserRole(detailDrawerUser.role) && (
                <section className="service-identity-panel" data-testid="admin-user-service-detail">
                  <h3>Service Identity</h3>
                  <p>App role users cannot sign in through the human web UI. Human-login controls are hidden.</p>
                </section>
              )}

              <AccountOriginAudit
                userId={detailDrawerUser.uuid || detailDrawerUser.id}
                authorized={(currentUser?.role === 'super_admin' || currentUser?.permissions?.audit_view === true) && !isAppUserRole(detailDrawerUser.role)}
              />

              {canManageSelectedUserSecurity && (
                <section className="admin-user-security-panel" data-testid="admin-user-security-recovery">
                  <div className="admin-user-security-header">
                    <div>
                      <h3>Account Security Recovery</h3>
                      <p className="subtitle">
                        {isTwoSvEnrolled(detailDrawerUser) ? '2SV enrolled' : 'No confirmed 2SV enrolment'}
                        {' · '}
                        {isTwoSvEnforced(detailDrawerUser) ? 'Login 2SV required' : 'Login 2SV optional'}
                      </p>
                    </div>
                    <span
                      className={`security-chip ${isTwoSvEnforced(detailDrawerUser) ? 'security-chip-strong' : 'security-chip-muted'}`}
                      data-testid="admin-user-detail-2sv-state"
                    >
                      {isTwoSvEnforced(detailDrawerUser) ? '2SV enforced' : '2SV optional'}
                    </span>
                  </div>

                  {adminSecurityMessage && (
                    <div className="form-success" data-testid="admin-user-security-success">
                      {adminSecurityMessage}
                    </div>
                  )}
                  {adminSecurityError && (
                    <div className="form-error" data-testid="admin-user-security-error">
                      {adminSecurityError}
                    </div>
                  )}

                  <div className="admin-security-action-grid">
                    <label htmlFor="adminRecoveryEvidence">
                      Recovery evidence
                      <input
                        id="adminRecoveryEvidence"
                        type="text"
                        value={recoveryEvidenceRef}
                        onChange={(event) => setRecoveryEvidenceRef(event.target.value)}
                        data-testid="admin-user-recovery-evidence"
                      />
                    </label>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleIssueRecoveryAssertion}
                      disabled={Boolean(adminSecurityLoading)}
                      data-testid="admin-user-issue-recovery"
                    >
                      {adminSecurityLoading === 'recovery' ? 'Issuing...' : 'Issue Recovery'}
                    </button>
                  </div>

                  <div className="admin-security-action-grid">
                    <div>
                      <p className="subtitle" data-testid="admin-user-password-reset-help">
                        A single-use, time-bound password reset link is emailed to this
                        user’s own registered address. Administrators never see the link,
                        token, or password.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => setShowPasswordResetConfirm(true)}
                      disabled={Boolean(adminSecurityLoading)}
                      data-testid="admin-user-send-password-reset"
                    >
                      {adminSecurityLoading === 'password-reset-email' ? 'Sending...' : 'Send password reset email'}
                    </button>
                  </div>

                  <div className="admin-security-action-grid admin-security-action-grid-inline">
                    <span>Login-time 2SV requirement</span>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={handleDisableLoginTwoSv}
                      disabled={Boolean(adminSecurityLoading) || !isTwoSvEnforced(detailDrawerUser)}
                      data-testid="admin-user-disable-login-2sv"
                    >
                      {adminSecurityLoading === 'disable-login-2sv' ? 'Updating...' : 'Turn Off Login 2SV'}
                    </button>
                  </div>
                </section>
              )}

              {!isAppUserRole(detailDrawerUser.role) && (
              <section className="organisation-memberships-panel" data-testid="admin-user-organisation-memberships">
                <div className="membership-panel-header">
                  <div>
                    <h3>Organisation Memberships</h3>
                    <p className="subtitle">{drawerMemberships.length} active memberships</p>
                  </div>
                  {drawerMembershipsLoading && <span className="status-badge info">Loading</span>}
                </div>

                {drawerMembershipError && (
                  <div className="form-error" data-testid="admin-user-membership-error">
                    {drawerMembershipError}
                  </div>
                )}

                <div className="membership-list" data-testid="admin-user-membership-list">
                  {drawerMemberships.length === 0 && !drawerMembershipsLoading ? (
                    <p className="text-muted">No organisation memberships found.</p>
                  ) : (
                    drawerMemberships.map((membership) => (
                      <div className="membership-row" key={`${membership.organisationId}-${membership.role}`} data-testid="admin-user-organisation-membership">
                        <div>
                          <strong>{membership.organisationName}</strong>
                          <span>{membership.role}</span>
                        </div>
                        {membership.isPrimary && <span className="status-badge active">Primary</span>}
                        {canRemoveFromOrganisation({ organisation_users_remove: drawerRemovePermitted[String(membership.organisationId)] }) && (
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => removeMembership(membership)}
                            disabled={membershipActionLoading}
                            title={`Remove from ${membership.organisationName}`}
                            data-testid="admin-user-membership-remove-from-org"
                          >
                            Remove from organisation
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {currentUser?.role === 'super_admin' ? (
                  <div className="membership-editor" data-testid="admin-user-membership-editor">
                    <label htmlFor="membershipOrganisation">Organisation</label>
                    <select
                      id="membershipOrganisation"
                      name="organisationId"
                      value={membershipForm.organisationId}
                      onChange={handleMembershipOrganisationChange}
                      data-testid="admin-user-membership-organisation-select"
                      disabled={organisations.length === 0 || membershipActionLoading}
                    >
                      {organisations.map((organisation) => {
                        const organisationId = getOrganisationId(organisation);
                        return (
                          <option key={organisationId} value={organisationId}>
                            {organisation.name}
                          </option>
                        );
                      })}
                    </select>

                    <label htmlFor="membershipRole">Organisation Role</label>
                    <select
                      id="membershipRole"
                      name="role"
                      value={membershipForm.role}
                      onChange={handleMembershipFieldChange}
                      data-testid="admin-user-membership-role-select"
                      disabled={!membershipForm.organisationId || membershipActionLoading}
                    >
                      <option value="student">Student</option>
                      <option value="teacher">Teacher</option>
                      <option value="admin">Admin</option>
                    </select>

                    <label className="membership-primary-option">
                      <input
                        type="checkbox"
                        name="isPrimary"
                        checked={membershipForm.isPrimary}
                        onChange={handleMembershipFieldChange}
                        data-testid="admin-user-membership-primary"
                        disabled={!membershipForm.organisationId || membershipActionLoading}
                      />
                      Primary organisation
                    </label>

                    <div className="membership-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSaveMembership}
                        disabled={!membershipForm.organisationId || membershipActionLoading}
                        data-testid="admin-user-membership-save"
                      >
                        {membershipActionLoading ? 'Saving...' : (selectedMembership ? 'Update Membership' : 'Add Membership')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={handleRemoveMembership}
                        disabled={!selectedMembership || selectedMembership.isPrimary || membershipActionLoading}
                        data-testid="admin-user-membership-remove"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted">Organisation membership changes require super-admin access.</p>
                )}
              </section>
              )}

              {/* M004-F0646/F0647: the canonical member price-plan surface (UI-3).
                  Behind REACT_APP_MEMBER_PRICE_PLANS_ENABLED (default OFF) until
                  Core M005-F0081/F0082 (Core MR !938) is deployed to production
                  - never ship this UI before its backend routes. Gated inside
                  by the EFFECTIVE pricing_plans_view / pricing_member_plan_assign
                  projection, never by user.role. */}
              {isMemberPricePlansBackendAvailable() && !isAppUserRole(detailDrawerUser.role) && getUserNumericId(detailDrawerUser) ? (
                <MemberPricePlansSection
                  key={`${getUserNumericId(detailDrawerUser)}`}
                  userId={getUserNumericId(detailDrawerUser)}
                  userName={detailDrawerUser.displayName || detailDrawerUser.email}
                  memberships={drawerMemberships}
                />
              ) : null}

              {canManageSelectedUserBotKeys && (
                <div className="admin-bot-key-panel-wrap">
                  <BotSessionKeysPanel
                    mode="admin"
                    targetUser={detailDrawerUser}
                    title="User Bot Keys"
                    description="Manage this user's static session keys, metadata, SAL limit, and read/write permissions."
                    testId="admin-user-bot-keys"
                  />
                </div>
              )}

              <div className="drawer-actions">
                <button className="btn btn-outline" onClick={closeDetailDrawer}>Close</button>
                {!isAppUserRole(detailDrawerUser.role) && (
                  <button className="btn btn-primary" onClick={editFromDetailDrawer}>Edit User</button>
                )}
              </div>
            </aside>
          </div>
        )}

        {/* Add/Edit User Modal */}
        {showModal && (
          <div className="modal-overlay" {...userModalDismissHandlers}>
            <div className="modal" ref={userDialogDraftRef} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{modalMode === 'add' ? 'Add New User' : 'Edit User'}</h2>
                <button className="modal-close" onClick={closeModal}>&times;</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {formError && (
                    // M004-F0620 / T-M004-0029: `formError` is the human message
                    // (`data.message`); the machine code (`data.error`, e.g.
                    // "FORBIDDEN") is kept here for support, not shown as the
                    // message itself.
                    <div className="form-error" title={formErrorCode || undefined} aria-description={formErrorCode || undefined}>
                      {formError}
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="displayName">Display Name *</label>
                    <input
                      type="text"
                      id="displayName"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleFormChange}
                      required
                      minLength={2}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      disabled={modalMode === 'edit'}
                    />
                  </div>

                  {modalMode === 'add' && (
                    // M001-F0372 / Core #437: no password field. The person sets their own
                    // via the emailed link; Core rejects any administrator-supplied password.
                    <p className="form-help" id="add-user-signin-help" data-testid="admin-add-user-signin-help">
                      We'll email them a link to sign in and set their own password.
                    </p>
                  )}

                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="role">Role *</label>
                    {/*
                      M004-F0620 / T-M004-0028: in "add" mode (global create
                      route) and in edit mode's 'global' authority, the full
                      role set is offered exactly as before. In edit mode's
                      'organisation' authority, a role change is sent through
                      `PUT /organisations/:id/users/:userId`, whose Core route
                      only accepts admin/teacher/student (see
                      docs/design/M004-F0620-Edit-User-Role-Capability-Routing.md)
                      — the options are narrowed to match, mirroring the
                      existing Organisation Memberships editor's own role
                      options. In 'none'/'loading' authority the field is
                      read-only; a one-line reason renders underneath.
                    */}
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleFormChange}
                      required
                      disabled={modalMode === 'edit' && editRoleAuthority.mode !== 'global' && editRoleAuthority.mode !== 'organisation'}
                      data-testid="admin-user-role-select"
                    >
                      {modalMode === 'edit' && editRoleAuthority.mode === 'organisation' ? (
                        <>
                          <option value="student">Student</option>
                          <option value="teacher">Teacher</option>
                          <option value="admin">Admin</option>
                        </>
                      ) : (
                        <>
                          <option value="student">Student</option>
                          <option value="teacher">Teacher</option>
                          <option value="admin">Admin</option>
                          {currentUser?.role === 'super_admin' && (
                            <option value="super_admin">Super Admin</option>
                          )}
                          <option value="event_simple">Event Simple</option>
                          <option value="simple_room_controls">Simple Room Controls</option>
                        </>
                      )}
                    </select>
                    {modalMode === 'edit' && editRoleAuthority.mode === 'loading' && (
                      <small className="text-muted" data-testid="admin-user-role-authority-status">
                        Checking permissions…
                      </small>
                    )}
                    {modalMode === 'edit' && editRoleAuthority.mode === 'none' && (
                      <small className="text-muted" data-testid="admin-user-role-authority-status">
                        {editRoleAuthority.reason || 'You are not authorised to change this member’s role.'}
                      </small>
                    )}
                  </div>

                  {modalMode === 'add' && (
                    <div className="form-group">
                      <label htmlFor="organisation_id">Organisation *</label>
                      <select
                        id="organisation_id"
                        name="organisation_id"
                        value={formData.organisation_id}
                        onChange={handleFormChange}
                        required
                      >
                        <option value="">Select Organisation</option>
                        {organisations.map(org => (
                          <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {modalMode === 'add' && (
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          name="forceEmailVerification"
                          checked={formData.forceEmailVerification || false}
                          onChange={handleFormChange}
                          style={{ width: 'auto' }}
                        />
                        Force Email Verification
                      </label>
                      <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                        If checked, the user's email will be marked as verified immediately.
                      </small>
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-outline" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={formLoading} data-testid="admin-user-save">
                    {formLoading ? 'Saving...' : (modalMode === 'add' ? 'Create User' : 'Save Changes')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Bulk Delete Confirmation Modal */}
        {showBulkDeleteModal && canDeleteUsersNow && (
          <div className="modal-overlay" {...bulkDeleteModalDismissHandlers}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header">
                <h2>Confirm Archive</h2>
                <button className="modal-close" onClick={() => setShowBulkDeleteModal(false)}>&times;</button>
              </div>
              <div className="modal-body">
                <p data-testid="admin-user-bulk-delete-confirm-text">{buildBulkDeleteUserConfirm(selectedUsers.size)}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowBulkDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleBulkDelete}
                  style={{ backgroundColor: '#dc2626', color: 'white' }}
                >
                  Delete {selectedUsers.size} User(s)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Send Password Reset Email Confirmation Modal */}
        {showPasswordResetConfirm && detailDrawerUser && (
          <div className="modal-overlay" {...passwordResetConfirmDismissHandlers}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header">
                <h2>Confirm Password Reset Email</h2>
                <button className="modal-close" onClick={() => setShowPasswordResetConfirm(false)}>&times;</button>
              </div>
              <div className="modal-body">
                <p>
                  Send a password reset email to{' '}
                  <strong>{detailDrawerUser.displayName || detailDrawerUser.email}</strong>
                  {' '}({detailDrawerUser.email})?
                </p>
                <p style={{ marginTop: '10px' }}>
                  A single-use, time-bound reset link will be emailed only to this user’s own
                  registered address. Administrators never see the link, token, or password.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setShowPasswordResetConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSendPasswordResetEmail}
                  disabled={Boolean(adminSecurityLoading)}
                  data-testid="admin-user-confirm-password-reset"
                >
                  {adminSecurityLoading === 'password-reset-email' ? 'Sending...' : 'Send password reset email'}
                </button>
              </div>
            </div>
          </div>
        )}

        {declarationAppCode && applicationPermissions.find((app) => app.appCode === declarationAppCode) && (
          <AppPermissionDeclarationDrawer
            key={declarationAppCode}
            app={applicationPermissions.find((app) => app.appCode === declarationAppCode)}
            hidden={Boolean(matrixStepUp)}
            onSave={saveAppDeclaration}
            onClose={() => setDeclarationAppCode('')}
          />
        )}
        <StepUpDialog
          challenge={matrixStepUp?.challenge || null}
          title="Verify this permission change"
          description="Confirm your identity before StudioAEye saves this permission change."
          onComplete={completeMatrixStepUp}
          onCancel={cancelMatrixStepUp}
        />
        <StepUpDialog
          challenge={adminSecurityStepUpChallenge}
          title="Admin action needs verification"
          description="Complete a fresh two-step verification before retrying this user security action."
          onComplete={completeAdminSecurityStepUp}
          onCancel={closeAdminSecurityStepUp}
        />
      </div>
    </Layout>
  );
};

const AdminUsers = UserManagementPage;

// ==================== Admin Rooms ====================
// ==================== Admin Rooms ====================
// Room Card Component with Climate Control
// M004-F0764 (WebUI #237): the room details MOVED here from the Rooms-tab card.
// [Overview] sub-tab of a room tab: read-only; edits stay in [Rooms].
const RoomOverviewDetails = ({ room }) => (
  <div className="room-details room-overview" data-testid="room-overview">
        {room.organisation_name && (
          <p>
            <strong>Organisation:</strong> {room.organisation_name}
            {room.is_orphaned && <em style={{ color: '#e74c3c', marginLeft: '5px' }}>(Deleted)</em>}
          </p>
        )}
        <p><strong>Capacity:</strong> {room.capacity} people</p>
        <p><strong>Hourly Rate:</strong> £{room.hourly_rate || 0}</p>
        <p><strong>Equipment:</strong> {Array.isArray(room.equipment) ? room.equipment.join(', ') : room.equipment || 'None'}</p>
        {room.description && <p><strong>Description:</strong> {room.description}</p>}
        {room.google_calendar_id && (
          <p>
            <strong>📅 Calendar:</strong>
            <a
              href={`https://calendar.google.com/calendar/embed?src=${room.google_calendar_id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginLeft: '5px', color: '#3498db' }}
            >
              View Calendar
            </a>
          </p>
        )}
  </div>
);

// The admin room Climate Controls tab. The generic room-controls panel (room state, room temperature and
// target) behind REACT_APP_ROOM_CONTROLS_RELAY_ENABLED. The legacy Home Assistant climate panel is removed (owner 2026-10-20).
// Sensors remain available in their own tab. Linking changes the room's observed state, so the controls panel is remounted
// (key) to re-read describe after a link or unlink.
const RoomClimateTab = ({ room }) => {
  const [linkRevision, setLinkRevision] = useState(0);
  return (
    <div className="room-climate" data-testid="room-climate">
      {!isRoomControlsRelayEnabled() ? (
        <p className="rc-muted" data-testid="room-climate-flag-off">Room controls are not enabled yet.</p>
      ) : (
        <>
          <RoomControlsPanel key={linkRevision} roomId={room.id} showSensors={false} />
          <RoomLinkedNodePanel roomId={room.id} organisationId={room.organisation_id} onLinksChanged={() => setLinkRevision((n) => n + 1)} />
          <section className="rc-section" data-testid="room-climate-placeholder-lights">
            <h4>Lights</h4>
            <p className="rc-muted">Room lights are not available yet (#269).</p>
          </section>
        </>
      )}
    </div>
  );
};

const RoomDevicesTab = ({ roomId }) => (
  <section className="room-linked-devices" data-testid="room-devices">
    <h3>All Linked Devices</h3>
    <RoomLinkedDevicesPanel key={roomId} roomId={roomId} />
  </section>
);

// The Rooms-tab card is now management only: name, status, Edit/Delete/Restore.
const RoomCard = ({ room, getStatusColor, openEditModal, handleDelete, handleRestore }) => {
  return (
    <div key={room.id} className={`room-card ${room.is_orphaned ? 'orphaned' : ''} ${room.is_deleted ? 'deleted' : ''}`}>
      <div className="room-header">
        <h3>{room.name}</h3>
        <div className="room-badges">
          {room.is_deleted && <span className="status-badge error">🗑️ Deleted</span>}
          {room.is_orphaned && <span className="status-badge warning">⚠️ Orphaned</span>}
          {!room.is_deleted && <span className={`status-badge ${getStatusColor(room.status)}`}>{room.status}</span>}
        </div>
      </div>

      <div className="room-actions">
        {room.is_deleted ? (
          <button className="btn btn-primary" onClick={() => handleRestore(room)}>
            Restore
          </button>
        ) : (
          <>
            <button className="btn btn-outline" title="Edit" onClick={() => openEditModal(room)}>
              {room.is_orphaned ? 'Reassign' : 'Edit'}
            </button>
            <button className="btn btn-outline" onClick={() => handleDelete(room)}>Delete</button>
          </>
        )}
      </div>
    </div>
  );
};

const ROOM_EDITOR_TABS = [
  { id: 'details', label: 'Details' },
  { id: 'hire', label: 'Room Hire' },
  { id: 'integrations', label: 'Integrations', superAdminOnly: true },
];

const splitRoomListInput = (value) =>
  String(value || '')
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);

const formatRoomListInput = (value) => {
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return typeof value === 'string' ? value : '';
};

const getRoomHireSettings = (room = {}) =>
  room?.settings?.public_hire ||
  room?.settings?.publicHire ||
  room?.public_hire ||
  room?.publicHire ||
  {};

const getRoomCoverPhotoUrl = (room = {}) => {
  const hireSettings = getRoomHireSettings(room);

  return room?.coverPhotoUrl || room?.cover_photo_url || hireSettings.coverPhotoUrl || hireSettings.cover_photo_url || '';
};

const buildRoomEditorFormData = (room = {}, fallbackOrganisationId = '') => {
  const hireSettings = getRoomHireSettings(room);

  return {
    name: room.name || '',
    organisation_id: room.organisation_id ?? fallbackOrganisationId ?? '',
    capacity: room.capacity ?? 20,
    hourly_rate: room.hourly_rate ?? 0,
    status: room.status || 'available',
    equipment: formatRoomListInput(room.equipment),
    description: room.description || '',
    floor_area: room.floor_area || room.floorArea || '',
    features: formatRoomListInput(room.features || hireSettings.features),
    suitable_for: formatRoomListInput(
      room.suitable_for || room.suitableFor || hireSettings.suitable_for || hireSettings.suitableFor
    ),
    public_pricing_text:
      room.public_pricing_text ||
      room.publicPricingText ||
      hireSettings.hourly_rate_text ||
      hireSettings.hourlyRateText ||
      '',
    public_status:
      room.public_status || room.publicStatus || hireSettings.public_status || hireSettings.publicStatus || '',
    public_status_note:
      room.public_status_note ||
      room.publicStatusNote ||
      hireSettings.public_status_note ||
      hireSettings.publicStatusNote ||
      '',
    public_notes:
      room.public_notes ||
      room.publicNotes ||
      hireSettings.public_notes ||
      hireSettings.publicNotes ||
      hireSettings.notes ||
      '',
    network_cidr: room.network_cidr || '',
    ha_url: room.ha_url || '',
    ha_token: room.ha_token || '',
    ha_token_inherited_from_room: room.ha_token_inherited_from_room || '',
    ha_climate_entity: room.ha_climate_entity || '',
    ha_occupancy_entity: room.ha_occupancy_entity || '',
    ha_lighting_index_entity: room.ha_lighting_index_entity || '',
    ha_lighting_name_entity: room.ha_lighting_name_entity || '',
    google_calendar_link:
      room.google_calendar_link ||
      (room.google_calendar_id ? `https://calendar.google.com/calendar/embed?src=${room.google_calendar_id}` : ''),
  };
};

const AdminRooms = () => {
  const { user } = useContext(AuthContext);
  const authScopeSubject = user?.uuid ?? user?.id ?? user?.email ?? '';
  // M004-F0764 (WebUI #237): management stays admin-only (no capability exists for it);
  // everyone else is admitted by the CAPABILITY room_devices_read, read-only.
  const isAdminUser = user?.role === 'admin' || user?.role === 'super_admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions: mySystemPermissions, ready: myPermissionsReady } = useMySystemPermissions(Boolean(authScopeSubject));
  const canViewRoomsAsViewer = !isAdminUser && myPermissionsReady && hasCapability(mySystemPermissions, ROOM_DEVICES_CAPABILITY);
  const canViewRoomDevices = isAdminUser || canViewRoomsAsViewer;
  const scopeOptions = { viewer: canViewRoomsAsViewer };
  const canResolveAdminRoomsScope = Boolean(authScopeSubject)
    && (isAdminUser || canViewRoomsAsViewer);
  const authScopeIdentity = [
    authScopeSubject || 'anonymous',
    user?.role ?? 'none',
    canViewRoomsAsViewer ? 'viewer' : '',
  ].map(String).join(':');
  const [rooms, setRooms] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrgFilter, setSelectedOrgFilter] = useState('');
  const [organisationScopeReady, setOrganisationScopeReady] = useState(false);
  const [organisationScopeSucceeded, setOrganisationScopeSucceeded] = useState(false);
  const [resolvedScopeIdentity, setResolvedScopeIdentity] = useState('');
  const [organisationScopeRevision, setOrganisationScopeRevision] = useState(0);
  const [showOrphaned, setShowOrphaned] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  // True once the room list for the current scope has been answered (a deep link is
  // judged unknown only after that, never while the list is still loading).
  const [roomsFetched, setRoomsFetched] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showHaToken, setShowHaToken] = useState(false);
  const [activeAdminRoomsTab, setActiveAdminRoomsTab] = useState('rooms');
  const [activeRoomTab, setActiveRoomTab] = useState('details');
  // M004-F0679 (WebUI #122, M004-F0617 amendment): the room editor's Pricing
  // tab is RETIRED. Plan management (including Set as default and Retire) lives
  // in the Bookings Hub Price plans drawer only.
  const [roomModalLoading, setRoomModalLoading] = useState(false);
  const [coverPhotoFile, setCoverPhotoFile] = useState(null);
  const [coverPhotoPreviewUrl, setCoverPhotoPreviewUrl] = useState('');
  const [removeCoverPhoto, setRemoveCoverPhoto] = useState(false);
  const [coverPhotoInputKey, setCoverPhotoInputKey] = useState(0);
  // M004-F0680 (WebUI #108/#81): the "Pending Booking Requests" card, its
  // state, fetch, handlers and reject modal are REMOVED from this page. Pending
  // approvals are the Bookings Hub Studio tab's "Pending" filter, and each
  // booking opens the ONE drawer (approve / decline / price). The card was the
  // third booking-info page and the source of "Requested by: User #null".
  // M004-F0074: Pending deletion requests state
  const [pendingDeletions, setPendingDeletions] = useState([]);
  const [deletionsLoading, setDeletionsLoading] = useState(true);
  const [deletionActionLoading, setDeletionActionLoading] = useState(null);
  const [rejectDeletionModal, setRejectDeletionModal] = useState(null);
  const [rejectDeletionReason, setRejectDeletionReason] = useState('');
  const [formData, setFormData] = useState(buildRoomEditorFormData());
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  // M004-F0613 / T-M004-0020: Add New Room, named explicitly alongside Add New User in
  // the owner's report. Scoped per M004-D016 §5.2; the HA long-lived access token field
  // (name="ha_token") is never captured — dialogDraftStore excludes it both by its
  // type="password" default state and, since it toggles to type="text" behind a
  // show/hide control, by its semantic field name too.
  const newRoomDraft = useDialogDraft('admin-room-new', 'new', { active: showModal && modalMode === 'add' });
  const editRoomDraft = useDialogDraft('admin-room-edit', selectedRoom?.id, { active: showModal && modalMode === 'edit' });
  const roomDialogDraftRef = modalMode === 'add' ? newRoomDraft.containerRef : editRoomDraft.containerRef;
  const organisationScopeIsCurrent = resolvedScopeIdentity === authScopeIdentity;
  const canLoadCurrentOrganisationScope = canLoadAdminRoomsScope(
    canResolveAdminRoomsScope
      && organisationScopeReady
      && organisationScopeSucceeded
      && organisationScopeIsCurrent,
    selectedOrgFilter,
    user,
    scopeOptions,
  );
  const childScopeKey = `${authScopeIdentity}:${organisationScopeRevision}:${selectedOrgFilter || 'all'}`;
  const activeScopeRequestKey = canLoadCurrentOrganisationScope ? childScopeKey : '';
  const activeScopeRequestKeyRef = useRef(activeScopeRequestKey);
  activeScopeRequestKeyRef.current = activeScopeRequestKey;

  // Resolve the current user's Rooms administration scope before any child
  // request runs.  /organisations/mine is already membership scoped; this
  // narrows it further to memberships which actually grant org admin.
  useEffect(() => {
    let active = true;
    const requestedScopeIdentity = authScopeIdentity;
    const requestedScopeUser = { role: user?.role };
    const requestedScopeOptions = { viewer: canViewRoomsAsViewer };

    const fetchOrganisationScope = async () => {
      setOrganisationScopeReady(false);
      setOrganisationScopeSucceeded(false);
      setResolvedScopeIdentity('');
      setLoading(true);
      // Do not leave a previous identity or membership refresh visible while
      // the new scope is unresolved.  Keep the selected value in state so a
      // still-valid selection can be restored after the authoritative list.
      setOrganisations([]);
      setRooms([]);
      setPendingDeletions([]);
      setShowModal(false);
      setSelectedRoom(null);
      setRejectDeletionModal(null);
      setDeletionActionLoading(null);
      if (!canResolveAdminRoomsScope) {
        setSelectedOrgFilter('');
        setResolvedScopeIdentity(requestedScopeIdentity);
        setOrganisationScopeRevision((current) => current + 1);
        setOrganisationScopeReady(true);
        setLoading(false);
        return;
      }
      try {
        const orgsResponse = await organisationApi.getMine();
        const orgsData = orgsResponse.data?.data || [];
        const nextOrganisations = selectAdminRoomsOrganisations(orgsData, requestedScopeUser, requestedScopeOptions);
        if (!active) return;
        setOrganisations(nextOrganisations);
        setSelectedOrgFilter((current) => resolveAdminRoomsSelection(current, nextOrganisations, requestedScopeUser, requestedScopeOptions));
        setResolvedScopeIdentity(requestedScopeIdentity);
        setOrganisationScopeSucceeded(true);
      } catch (err) {
        console.error('Failed to resolve Rooms organisation scope:', err);
        if (!active) return;
        setOrganisations([]);
        setSelectedOrgFilter('');
        setResolvedScopeIdentity(requestedScopeIdentity);
        setOrganisationScopeSucceeded(false);
      } finally {
        if (active) {
          setOrganisationScopeRevision((current) => current + 1);
          setOrganisationScopeReady(true);
        }
      }
    };
    fetchOrganisationScope();
    return () => { active = false; };
  }, [authScopeIdentity, canResolveAdminRoomsScope, canViewRoomsAsViewer, user?.role]);

  // Load rooms only after a non-global administrator has an explicit allowed
  // organisation.  Core still authorizes every request; this prevents a blank
  // browser request from presenting cross-organisation data while scope loads.
  useEffect(() => {
    if (!canLoadCurrentOrganisationScope) {
      setRooms([]);
      setRoomsFetched(false);
      setLoading(false);
      return undefined;
    }
    let active = true;
    const fetchRoomsForScope = async () => {
      try {
        setRoomsFetched(false);
        setLoading(true);
        const includeOrphaned = isGlobalSuperAdmin(user) && showOrphaned;
        const includeDeleted = (isGlobalSuperAdmin(user) || user?.role === 'admin') && showDeleted;
        const roomsResponse = await roomApi.getRooms(selectedOrgFilter || undefined, includeOrphaned, includeDeleted);
        if (active) setRooms(roomsResponse.data?.data || []);
      } catch (err) {
        console.error('Failed to fetch rooms:', err);
        if (active) setRooms([]);
      } finally {
        if (active) { setLoading(false); setRoomsFetched(true); }
      }
    };
    fetchRoomsForScope();
    return () => { active = false; };
  }, [canLoadCurrentOrganisationScope, selectedOrgFilter, showOrphaned, showDeleted, user?.role]);

  // M004-F0074: Fetch pending deletion requests
  useEffect(() => {
    if (!canLoadCurrentOrganisationScope) {
      setPendingDeletions([]);
      setDeletionsLoading(false);
      return undefined;
    }
    let active = true;
    const fetchPendingDeletions = async () => {
      try {
        setDeletionsLoading(true);
        const response = await bookingApi.getPendingDeletions(selectedOrgFilter || undefined);
        const deletions = response.data?.data || [];
        if (active) setPendingDeletions(deletions);
      } catch (err) {
        console.error('Failed to fetch pending deletions:', err);
        if (active) setPendingDeletions([]);
      } finally {
        if (active) setDeletionsLoading(false);
      }
    };
    fetchPendingDeletions();
    return () => { active = false; };
  }, [canLoadCurrentOrganisationScope, selectedOrgFilter]);

  // M004-F0077: Subscribe to WebSocket events for real-time updates
  useEffect(() => {
    if (!canLoadCurrentOrganisationScope) {
      return undefined;
    }
    let active = true;
    wsService.connect();

    // Helper function to refetch all pending data
    const refetchPendingData = async () => {
      try {
        const deletionsRes = await bookingApi.getPendingDeletions(selectedOrgFilter || undefined);
        if (!active) return;
        setPendingDeletions(deletionsRes.data?.data || []);
      } catch (err) {
        if (!active) return;
        console.error('Failed to fetch bookings:', err);
      }
    };

    // Refetch pending bookings when new booking created
    const unsubCreated = wsService.subscribe('booking:created', (data) => {
      console.log('AdminRooms: New booking created via WebSocket:', data);
      refetchPendingData();
    });

    // Refetch when booking status changes
    const unsubUpdated = wsService.subscribe('booking:updated', (data) => {
      console.log('AdminRooms: Booking updated via WebSocket:', data);
      refetchPendingData();
    });

    // Refetch when booking is approved (to clear from pending list)
    const unsubApproved = wsService.subscribe('booking:approved', (data) => {
      console.log('AdminRooms: Booking approved via WebSocket:', data);
      refetchPendingData();
    });

    // Refetch when booking is rejected (to clear from pending list)
    const unsubRejected = wsService.subscribe('booking:rejected', (data) => {
      console.log('AdminRooms: Booking rejected via WebSocket:', data);
      refetchPendingData();
    });

    // Refetch when booking is deleted
    const unsubDeleted = wsService.subscribe('booking:deleted', (data) => {
      console.log('AdminRooms: Booking deleted via WebSocket:', data);
      refetchPendingData();
    });

    return () => {
      active = false;
      unsubCreated();
      unsubUpdated();
      unsubApproved();
      unsubRejected();
      unsubDeleted();
    };
  }, [canLoadCurrentOrganisationScope, selectedOrgFilter]);

  // M004-F0074-H: Approve deletion request
  const handleApproveDeletion = async (bookingId) => {
    try {
      setDeletionActionLoading(bookingId);
      await bookingApi.approveDeleteRequest(bookingId);
      setPendingDeletions(prev => prev.filter(b => b.id !== bookingId));
    } catch (err) {
      console.error('Failed to approve deletion:', err);
      alert('Failed to approve deletion: ' + (err.response?.data?.message || err.message));
    } finally {
      setDeletionActionLoading(null);
    }
  };

  // M004-F0074-I: Reject deletion request
  const openRejectDeletionModal = (booking) => {
    setRejectDeletionModal(booking);
    setRejectDeletionReason('');
  };

  const handleRejectDeletion = async () => {
    if (!rejectDeletionModal) return;
    try {
      setDeletionActionLoading(rejectDeletionModal.id);
      await bookingApi.rejectDeleteRequest(rejectDeletionModal.id, rejectDeletionReason);
      setPendingDeletions(prev => prev.filter(b => b.id !== rejectDeletionModal.id));
      setRejectDeletionModal(null);
      setRejectDeletionReason('');
    } catch (err) {
      console.error('Failed to reject deletion:', err);
      alert('Failed to reject deletion: ' + (err.response?.data?.message || err.message));
    } finally {
      setDeletionActionLoading(null);
    }
  };

  const formatBookingDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatBookingTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const fetchRooms = async () => {
    if (!canLoadCurrentOrganisationScope) {
      setRooms([]);
      return [];
    }
    const requestedScopeKey = activeScopeRequestKey;
    try {
      const includeOrphaned = isGlobalSuperAdmin(user) && showOrphaned;
      const includeDeleted = (isGlobalSuperAdmin(user) || user?.role === 'admin') && showDeleted;
      const roomsResponse = await roomApi.getRooms(selectedOrgFilter || undefined, includeOrphaned, includeDeleted);
      const nextRooms = roomsResponse.data?.data || [];
      if (activeScopeRequestKeyRef.current === requestedScopeKey) setRooms(nextRooms);
      return nextRooms;
    } catch (err) {
      console.error('Failed to refresh rooms:', err);
      return [];
    }
  };

  const roomEditorTabs = ROOM_EDITOR_TABS.filter((tab) => {
    if (tab.superAdminOnly && user?.role !== 'super_admin') return false;
    return true;
  });
  // M004-F0764 (WebUI #237): [Rooms] [<room>...] [Term Planner] [Term Settings] [Access].
  // The room list is the source: a created room gains a tab, a deleted one loses it.
  const adminRoomsTabs = buildTopTabs({ rooms, isAdmin: isAdminUser });
  const deepLink = resolveRoomDeepLink({
    roomParam: searchParams.get('room'),
    subTabParam: searchParams.get('roomTab'),
    rooms,
    isAdmin: isAdminUser,
    canViewDevices: canViewRoomDevices,
  });
  const activeRoom = deepLink?.room || null;
  const activeRoomSubTab = deepLink?.subTab || 'overview';
  const roomSubTabs = visibleSubTabs({ isAdmin: isAdminUser, canViewDevices: canViewRoomDevices });
  const roomsLoaded = canLoadCurrentOrganisationScope && roomsFetched && !loading;
  // A viewer has no [Rooms] tab: with no usable room link they land on the first room tab.
  const viewerLandingRoom = !isAdminUser ? roomsWithTabs(rooms)[0] || null : null;
  const effectiveRoom = activeRoom || viewerLandingRoom;
  const effectiveSubTab = activeRoom ? activeRoomSubTab : 'overview';
  const activeTopTabId = effectiveRoom ? `room:${effectiveRoom.id}` : (isAdminUser ? activeAdminRoomsTab : '');

  // An unknown or invisible room falls back silently: drop the stale query so the
  // URL never confirms or denies that the room exists.
  useEffect(() => {
    if (searchParams.get('room') && roomsLoaded && !activeRoom) {
      setSearchParams({}, { replace: true });
    }
  }, [roomsLoaded, activeRoom, searchParams, setSearchParams]);

  const selectTopTab = (tab) => {
    if (tab.roomId) {
      setSearchParams({ room: tab.roomId, roomTab: 'overview' });
      return;
    }
    if (searchParams.get('room')) setSearchParams({});
    setActiveAdminRoomsTab(tab.id);
  };
  const selectRoomSubTab = (subTabId) => {
    if (!effectiveRoom) return;
    setSearchParams({ room: String(effectiveRoom.id), roomTab: subTabId });
  };

  const updateCoverPhotoPreview = (nextUrl = '') => {
    setCoverPhotoPreviewUrl((currentUrl) => {
      if (currentUrl && currentUrl.startsWith('blob:')) {
        URL.revokeObjectURL(currentUrl);
      }

      return nextUrl || '';
    });
  };

  const resetCoverPhotoState = (room = null) => {
    setCoverPhotoFile(null);
    setRemoveCoverPhoto(false);
    setCoverPhotoInputKey((current) => current + 1);
    updateCoverPhotoPreview(getRoomCoverPhotoUrl(room));
  };

  const handleCoverPhotoChange = (e) => {
    const nextFile = e.target.files?.[0];

    if (!nextFile) {
      return;
    }

    if (!['image/jpeg', 'image/png'].includes(nextFile.type)) {
      setFormError('Cover photo must be a JPG or PNG image.');
      setCoverPhotoInputKey((current) => current + 1);
      return;
    }

    if (nextFile.size > 5 * 1024 * 1024) {
      setFormError('Cover photo must be 5 MB or smaller.');
      setCoverPhotoInputKey((current) => current + 1);
      return;
    }

    setFormError('');
    setCoverPhotoFile(nextFile);
    setRemoveCoverPhoto(false);
    updateCoverPhotoPreview(URL.createObjectURL(nextFile));
  };

  const handleCoverPhotoRemoval = () => {
    setCoverPhotoFile(null);
    setRemoveCoverPhoto(Boolean(selectedRoom?.id));
    setCoverPhotoInputKey((current) => current + 1);
    updateCoverPhotoPreview('');
  };

  const openAddModal = () => {
    setModalMode('add');
    setSelectedRoom(null);
    const draft = getDraftForScope(buildDraftScopeKey('admin-room-new', 'new'));
    setFormData({ ...buildRoomEditorFormData({}, organisations.length > 0 ? organisations[0].id : ''), ...draft });
    setFormError('');
    setShowHaToken(false);
    setActiveRoomTab('details');
    setRoomModalLoading(false);
    resetCoverPhotoState();
    setShowModal(true);
  };

  const openEditModal = async (room) => {
    setModalMode('edit');
    setSelectedRoom(room);
    const roomDraftScopeKey = buildDraftScopeKey('admin-room-edit', room.id);
    const draft = getDraftForScope(roomDraftScopeKey);
    setFormData({ ...buildRoomEditorFormData(room, room.organisation_id), ...draft });
    setFormError('');
    setShowHaToken(false);
    setActiveRoomTab('details');
    setRoomModalLoading(true);
    resetCoverPhotoState(room);
    setShowModal(true);

    try {
      const response = await roomApi.getRoom(room.id);
      const detailedRoom = response.data?.data || room;
      setSelectedRoom(detailedRoom);
      // Re-merge the draft on top of the freshly-fetched server data (M004-D016 §5.2):
      // this async fetch resolves after the synchronous seed above and must not
      // silently drop the restored draft.
      setFormData({ ...buildRoomEditorFormData(detailedRoom, detailedRoom.organisation_id), ...draft });
      resetCoverPhotoState(detailedRoom);
    } catch (err) {
      console.error('Failed to load room details:', err);
    } finally {
      setRoomModalLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRoom(null);
    setFormError('');
    setShowHaToken(false);
    setActiveRoomTab('details');
    setRoomModalLoading(false);
    resetCoverPhotoState();
  };

  // M004-F0612 / T-M004-0019: dismiss only on a genuine outside press-and-release;
  // a text-selection drag from inside a dialog to outside it must not close it.
  const roomModalDismissHandlers = useDismissOnOutsideClick(closeModal);
  const rejectDeletionModalDismissHandlers = useDismissOnOutsideClick(() => setRejectDeletionModal(null));

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: ['capacity', 'hourly_rate', 'organisation_id'].includes(name) ? (parseFloat(value) || 0) : value
      };
      // When selecting a room to inherit from, clear manual ha_url/ha_token
      if (name === 'ha_token_inherited_from_room') {
        if (value) {
          updated.ha_url = '';
          updated.ha_token = '';
        }
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    // Captured before closeModal() resets selectedRoom back to null.
    const roomDraftScopeKey = modalMode === 'add'
      ? buildDraftScopeKey('admin-room-new', 'new')
      : buildDraftScopeKey('admin-room-edit', selectedRoom?.id);

    if (!formData.name.trim()) {
      setFormError('Room name is required');
      setFormLoading(false);
      return;
    }

    if (!formData.organisation_id) {
      setFormError('Organisation is required');
      setFormLoading(false);
      return;
    }

    const equipmentArray = splitRoomListInput(formData.equipment);
    const featureArray = splitRoomListInput(formData.features);
    const suitableForArray = splitRoomListInput(formData.suitable_for);
    const existingSettings = selectedRoom?.settings && typeof selectedRoom.settings === 'object' ? selectedRoom.settings : {};
    const existingPublicHire = getRoomHireSettings(selectedRoom);
    const publicHireSettings = {
      ...existingPublicHire,
      suitable_for: suitableForArray,
      hourly_rate_text: formData.public_pricing_text.trim(),
      public_status: formData.public_status.trim(),
      public_status_note: formData.public_status_note.trim(),
      notes: formData.public_notes.trim(),
    };
    const settings = {
      ...existingSettings,
      public_hire: publicHireSettings,
    };

    try {
      let savedRoomId = selectedRoom?.id;

      if (modalMode === 'add') {
        const roomData = {
          organisation_id: formData.organisation_id,
          name: formData.name,
          capacity: formData.capacity,
          hourly_rate: formData.hourly_rate,
          status: formData.status,
          equipment: equipmentArray,
          description: formData.description,
          floor_area: formData.floor_area.trim(),
          features: featureArray,
          settings,
          google_calendar_link: formData.google_calendar_link,
        };
        // Only super admin can set HA fields and network_cidr
        if (user?.role === 'super_admin') {
          roomData.network_cidr = formData.network_cidr;
          if (formData.ha_token_inherited_from_room) {
            roomData.ha_token_inherited_from_room = parseInt(formData.ha_token_inherited_from_room);
          } else {
            roomData.ha_url = formData.ha_url;
            roomData.ha_token = formData.ha_token;
          }
          roomData.ha_climate_entity = formData.ha_climate_entity;
          roomData.ha_occupancy_entity = formData.ha_occupancy_entity;
          roomData.ha_lighting_index_entity = formData.ha_lighting_index_entity;
          roomData.ha_lighting_name_entity = formData.ha_lighting_name_entity;
        }
        const response = await roomApi.createRoom(roomData);
        savedRoomId = response.data?.data?.id || response.data?.id || response.data?.data?.roomId || savedRoomId;
      } else {
        const updateData = {
          name: formData.name,
          capacity: formData.capacity,
          hourly_rate: formData.hourly_rate,
          status: formData.status,
          equipment: equipmentArray,
          description: formData.description,
          floor_area: formData.floor_area.trim(),
          features: featureArray,
          settings,
          google_calendar_link: formData.google_calendar_link,
        };

        // Include organisation_id if Super Admin is reassigning room to different org
        if (user?.role === 'super_admin' && formData.organisation_id !== selectedRoom.organisation_id) {
          updateData.organisation_id = formData.organisation_id;
        }

        // Only super admin can set HA fields and network_cidr
        if (user?.role === 'super_admin') {
          updateData.network_cidr = formData.network_cidr;
          if (formData.ha_token_inherited_from_room) {
            updateData.ha_token_inherited_from_room = parseInt(formData.ha_token_inherited_from_room);
          } else {
            updateData.ha_url = formData.ha_url;
            updateData.ha_token = formData.ha_token;
            updateData.ha_token_inherited_from_room = null;
          }
          updateData.ha_climate_entity = formData.ha_climate_entity;
          updateData.ha_occupancy_entity = formData.ha_occupancy_entity;
          updateData.ha_lighting_index_entity = formData.ha_lighting_index_entity;
          updateData.ha_lighting_name_entity = formData.ha_lighting_name_entity;
        }

        await roomApi.updateRoom(selectedRoom.id, updateData);
      }

      if (savedRoomId && removeCoverPhoto && !coverPhotoFile && getRoomCoverPhotoUrl(selectedRoom)) {
        await roomApi.deleteCoverPhoto(savedRoomId);
      }

      if (savedRoomId && coverPhotoFile) {
        await roomApi.uploadCoverPhoto(savedRoomId, coverPhotoFile);
      }

      await fetchRooms();
      closeModal();
      clearDraftForScope(roomDraftScopeKey);
    } catch (err) {
      console.error('Failed to save room:', err);
      setFormError(err.response?.data?.message || 'Failed to save room');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (room) => {
    if (!window.confirm(`Are you sure you want to delete ${room.name}?`)) {
      return;
    }
    try {
      await roomApi.deleteRoom(room.id);
      await fetchRooms();
    } catch (err) {
      console.error('Failed to delete room:', err);
      alert(err.response?.data?.message || 'Failed to delete room');
    }
  };

  const handleRestore = async (room) => {
    if (!window.confirm(`Restore ${room.name}?`)) {
      return;
    }
    try {
      await roomApi.restoreRoom(room.id);
      await fetchRooms();
    } catch (err) {
      console.error('Failed to restore room:', err);
      alert(err.response?.data?.message || 'Failed to restore room');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'success';
      case 'occupied': return 'warning';
      case 'maintenance': return 'error';
      default: return 'info';
    }
  };

  return (
    <Layout>
      <div className="admin-rooms">
        <header className="page-header">
          <div>
            <h1>Room Management</h1>
            <p className="subtitle">
              {rooms.length} rooms
              {showOrphaned && rooms.some(r => r.is_orphaned) ? ` (${rooms.filter(r => r.is_orphaned).length} orphaned)` : ''}
              {showDeleted && rooms.some(r => r.is_deleted) ? ` (${rooms.filter(r => r.is_deleted).length} deleted)` : ''}
            </p>
          </div>
          <div className="header-actions">
            {organisationScopeIsCurrent && organisations.length > 0 && (
              <select
                className="form-input"
                value={selectedOrgFilter}
                onChange={(e) => setSelectedOrgFilter(resolveAdminRoomsSelection(e.target.value, organisations, user, scopeOptions))}
                aria-label="Organisation"
                data-testid="admin-rooms-organisation-filter"
                style={{ marginRight: '10px', width: 'auto' }}
              >
                {(isGlobalSuperAdmin(user) || canViewRoomsAsViewer) && <option value="">All Organisations</option>}
                {organisations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            )}
            {organisationScopeReady && organisationScopeIsCurrent && !isGlobalSuperAdmin(user) && !canViewRoomsAsViewer && !selectedOrgFilter && (
              <p className="form-error" role="alert" data-testid="admin-rooms-organisation-scope-empty">
                No administered organisation is available for Room Management.
              </p>
            )}
            {user?.role === 'super_admin' && (
              <label style={{ display: 'flex', alignItems: 'center', marginRight: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showOrphaned}
                  onChange={(e) => setShowOrphaned(e.target.checked)}
                  style={{ marginRight: '5px' }}
                />
                Show Orphaned
              </label>
            )}
            {(user?.role === 'super_admin' || user?.role === 'admin') && (
              <label style={{ display: 'flex', alignItems: 'center', marginRight: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                  style={{ marginRight: '5px' }}
                />
                Show Deleted
              </label>
            )}
            {isAdminUser && <button className="btn btn-primary" onClick={openAddModal}>+ Add Room</button>}
          </div>
        </header>

        {!isAdminUser && myPermissionsReady && !canViewRoomsAsViewer && (
          <p className="form-error" role="alert" data-testid="admin-rooms-denied">
            You do not have access to Room Management.
          </p>
        )}
        {/* width:0 + min-width:100%: the bar contributes no intrinsic width, so many room tabs scroll
            inside it instead of widening the flex item .main-content (WebUI pipeline 14829). */}
        <div className="admin-rooms-tabs-clip" style={{ width: 0, minWidth: '100%' }}>
          <div className="network-tabs admin-rooms-tabs" role="tablist" aria-label="Room management sections" data-testid="WEB-TERM-017" style={{ overflowX: 'auto', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
            {adminRoomsTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`network-tab admin-rooms-tab ${activeTopTabId === tab.id ? 'active' : ''}`}
                role="tab"
                aria-selected={activeTopTabId === tab.id}
                aria-controls={`admin-rooms-tabpanel-${tab.id}`}
                id={`admin-rooms-tab-${tab.id}`}
                data-testid={`admin-rooms-tab-${tab.id}`}
                onClick={() => selectTopTab(tab)}
                style={{ flex: '0 0 auto' }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div
          id={`admin-rooms-tabpanel-${activeTopTabId}`}
          role="tabpanel"
          aria-labelledby={`admin-rooms-tab-${activeTopTabId}`}
          className="admin-rooms-tabpanel"
        >
          {effectiveRoom && (
            <div className="room-tab" data-testid={`room-tab-${effectiveRoom.id}`}>
              <div className="network-tabs room-sub-tabs" role="tablist" aria-label={`${effectiveRoom.name} sections`}>
                {roomSubTabs.map((sub) => (
                  <button
                    key={sub.id}
                    type="button"
                    role="tab"
                    className={`network-tab ${effectiveSubTab === sub.id ? 'active' : ''}`}
                    aria-selected={effectiveSubTab === sub.id}
                    data-testid={`room-subtab-${sub.id}`}
                    onClick={() => selectRoomSubTab(sub.id)}
                  >
                    {sub.label}
                  </button>
                ))}
              </div>
              {effectiveSubTab === 'overview' && <RoomOverviewDetails room={effectiveRoom} />}
              {effectiveSubTab === 'climate' && isAdminUser && (
                <RoomClimateTab key={effectiveRoom.id} room={effectiveRoom} />
              )}
              {effectiveSubTab === 'sensors' && isAdminUser && (
                <RoomSensorsPanel key={effectiveRoom.id} roomId={effectiveRoom.id} />
              )}
              {effectiveSubTab === 'devices' && canViewRoomDevices && (
                <RoomDevicesTab key={effectiveRoom.id} roomId={effectiveRoom.id} />
              )}
            </div>
          )}

          {activeTopTabId === 'rooms' && (
            <>
              {/* M004-F0074: Pending Deletion Requests Section */}
              {pendingDeletions.length > 0 && (
                <section className="booking-requests-section deletion-requests">
                  <div className="section-header">
                    <h2>🗑️ Pending Deletion Requests ({pendingDeletions.length})</h2>
                  </div>
                  <div className="booking-requests-grid">
                    {deletionsLoading ? (
                      <div className="loading-spinner">Loading deletion requests...</div>
                    ) : (
                      pendingDeletions.map((booking) => (
                        <div key={booking.id} className="booking-request-card deletion-request">
                          <div className="booking-request-header">
                            <h3>{booking.title || 'Room Booking'}</h3>
                            <span className="booking-status delete-pending">⏱️ Delete Pending</span>
                          </div>
                          <div className="booking-request-details">
                            <p><strong>🏠 Room:</strong> {booking.room_name || `Room #${booking.room_id}`}</p>
                            <p><strong>👤 Booked by:</strong> {requesterNameOf(booking)}</p>
                            <p><strong>📅 Date:</strong> {formatBookingDate(booking.start_time)}</p>
                            <p><strong>🕐 Time:</strong> {formatBookingTime(booking.start_time)} - {formatBookingTime(booking.end_time)}</p>
                            {booking.deletion_reason && <p><strong>📝 Reason:</strong> {booking.deletion_reason}</p>}
                            {booking.deletion_requested_at && (
                              <p><strong>🕐 Requested:</strong> {new Date(booking.deletion_requested_at).toLocaleString('en-GB')}</p>
                            )}
                          </div>
                          <div className="booking-request-actions">
                            <button
                              className="btn btn-success"
                              onClick={() => handleApproveDeletion(booking.id)}
                              disabled={deletionActionLoading === booking.id}
                            >
                              {deletionActionLoading === booking.id ? '...' : '✓ Approve Delete'}
                            </button>
                            <button
                              className="btn btn-warning"
                              onClick={() => openRejectDeletionModal(booking)}
                              disabled={deletionActionLoading === booking.id}
                            >
                              ✗ Keep Booking
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </section>
              )}

              {loading ? (
                <div className="loading-spinner">Loading rooms...</div>
              ) : (
                <div className="rooms-grid">
                  {rooms.map((room) => (
                    <RoomCard
                      key={room.id}
                      room={room}
                      getStatusColor={getStatusColor}
                      openEditModal={openEditModal}
                      handleDelete={handleDelete}
                      handleRestore={handleRestore}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {activeTopTabId === 'term-bookings' && canLoadCurrentOrganisationScope && (
            <TermManagementPanel
              key={`bookings:${childScopeKey}`}
              organisations={organisations}
              rooms={rooms}
              selectedOrgFilter={selectedOrgFilter}
              mode="bookings"
              showOrganisationControl={false}
            />
          )}

          {activeTopTabId === 'term-settings' && canLoadCurrentOrganisationScope && (
            <TermManagementPanel
              key={`settings:${childScopeKey}`}
              organisations={organisations}
              rooms={rooms}
              selectedOrgFilter={selectedOrgFilter}
              mode="settings"
              showOrganisationControl={false}
            />
          )}

          {activeTopTabId === 'access' && canLoadCurrentOrganisationScope && (
            <>
              {(user?.role === 'super_admin' || user?.role === 'admin') && (
                <div className="access-advanced-link-bar">
                  <a
                    href="/app/admin/rooms/access/advanced"
                    className="btn btn-outline"
                    data-testid="access-advanced-link"
                  >
                    Advanced configuration
                  </a>
                </div>
              )}
              <AccessMemberList
                key={`access-members:${childScopeKey}`}
                organisationId={selectedOrgFilter}
              />
            </>
          )}
        </div>

        {/* Add/Edit Room Modal */}
        {showModal && (
          <div className="modal-overlay" {...roomModalDismissHandlers}>
            <div className="modal room-editor-modal" ref={roomDialogDraftRef} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div>
                  <h2>
                    {modalMode === 'add' ? 'Add New Room' : (user?.role === 'super_admin' ? 'Edit / Reassign Room' : 'Edit Room')}
                  </h2>
                  <p className="room-editor-modal-subtitle">
                    Split core setup, public room-hire content, and integrations into separate sections.
                  </p>
                </div>
                <button className="modal-close" onClick={closeModal}>&times;</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="room-editor-tabs" role="tablist" aria-label="Room editor sections">
                  {roomEditorTabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={activeRoomTab === tab.id}
                      className={`room-editor-tab ${activeRoomTab === tab.id ? 'active' : ''}`}
                      onClick={() => setActiveRoomTab(tab.id)}
                      data-testid={`room-editor-tab-${tab.id}`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="modal-body room-editor-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {formError && <div className="form-error">{formError}</div>}

                  {roomModalLoading && (
                    <div className="room-editor-loading-note">Loading the latest room settings…</div>
                  )}

                  {selectedRoom?.is_orphaned && (
                    <div className="form-warning" style={{
                      background: '#fff3cd',
                      border: '1px solid #ffc107',
                      borderRadius: '4px',
                      padding: '10px',
                      marginBottom: '15px',
                      color: '#856404'
                    }}>
                      ⚠️ This room belongs to a deleted organisation. Please select a new organisation to reassign it.
                    </div>
                  )}

                  <fieldset className="room-editor-fieldset" disabled={formLoading || roomModalLoading}>
                    {activeRoomTab === 'details' && (
                      <div className="room-editor-panel">
                        <section className="room-editor-section">
                          <h3>Core Room Details</h3>
                          <p>Keep the operational setup here. The hire-facing content sits in its own tab.</p>

                          <div className="room-editor-grid">
                            {(modalMode === 'add' || (modalMode === 'edit' && user?.role === 'super_admin')) && (
                              <div className="form-group">
                                <label htmlFor="organisation_id">Organisation *</label>
                                <select
                                  id="organisation_id"
                                  name="organisation_id"
                                  value={formData.organisation_id}
                                  onChange={handleFormChange}
                                  required
                                >
                                  <option value="">Select Organisation</option>
                                  {organisations.map((org) => (
                                    <option key={org.id} value={org.id}>{org.name}</option>
                                  ))}
                                </select>
                              </div>
                            )}

                            <div className="form-group">
                              <label htmlFor="name">Room Name *</label>
                              <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleFormChange}
                                required
                                placeholder="e.g., Studio A"
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="capacity">Capacity *</label>
                              <input
                                type="number"
                                id="capacity"
                                name="capacity"
                                value={formData.capacity}
                                onChange={handleFormChange}
                                required
                                min={1}
                                max={100}
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="hourly_rate">Hourly Rate (£)</label>
                              <input
                                type="number"
                                id="hourly_rate"
                                name="hourly_rate"
                                value={formData.hourly_rate}
                                onChange={handleFormChange}
                                min={0}
                                step="0.01"
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="status">Status *</label>
                              <select
                                id="status"
                                name="status"
                                value={formData.status}
                                onChange={handleFormChange}
                                required
                              >
                                <option value="available">Available</option>
                                <option value="occupied">Occupied</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="maintenance">Maintenance</option>
                              </select>
                            </div>

                            <div className="form-group room-editor-span-2">
                              <label htmlFor="equipment">Equipment</label>
                              <textarea
                                id="equipment"
                                name="equipment"
                                rows="3"
                                value={formData.equipment}
                                onChange={handleFormChange}
                                placeholder="Mirror, Sound System, Ballet Bar"
                              />
                              <small className="room-editor-help">Enter one per line or separate items with commas.</small>
                            </div>
                          </div>
                        </section>
                      </div>
                    )}

                    {activeRoomTab === 'hire' && (
                      <div className="room-editor-panel">
                        <section className="room-editor-section">
                          <h3>Room Hire Content</h3>
                          <p>These fields feed the public room showcase and enquiry experience described in the room hire spec.</p>

                          <div className="room-editor-photo">
                            <div className="room-editor-photo-preview">
                              {coverPhotoPreviewUrl ? (
                                <img src={coverPhotoPreviewUrl} alt={`${formData.name || 'Room'} cover`} />
                              ) : (
                                <span>No cover photo selected</span>
                              )}
                            </div>

                            <div className="room-editor-photo-actions">
                              <div className="form-group">
                                <label htmlFor="room_cover_photo">Cover Photo</label>
                                <input
                                  key={coverPhotoInputKey}
                                  type="file"
                                  id="room_cover_photo"
                                  accept="image/png,image/jpeg"
                                  onChange={handleCoverPhotoChange}
                                  disabled={modalMode === 'add'}
                                />
                                <small className="room-editor-help">
                                  {modalMode === 'add'
                                    ? 'Create the room first, then reopen it to upload the cover photo.'
                                    : 'JPEG or PNG, up to 5 MB.'}
                                </small>
                              </div>

                              {modalMode === 'edit' && (coverPhotoPreviewUrl || getRoomCoverPhotoUrl(selectedRoom)) && (
                                <button type="button" className="btn btn-outline" onClick={handleCoverPhotoRemoval}>
                                  Remove Cover Photo
                                </button>
                              )}

                              {removeCoverPhoto && (
                                <small className="room-editor-help">Cover photo will be removed when you save.</small>
                              )}
                            </div>
                          </div>
                        </section>

                        <section className="room-editor-section">
                          <h3>Showcase Copy And Metadata</h3>

                          <div className="room-editor-grid">
                            <div className="form-group">
                              <label htmlFor="floor_area">Floor Area</label>
                              <input
                                type="text"
                                id="floor_area"
                                name="floor_area"
                                value={formData.floor_area}
                                onChange={handleFormChange}
                                placeholder="e.g., 611 sq ft"
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="public_pricing_text">Public Pricing Text</label>
                              <input
                                type="text"
                                id="public_pricing_text"
                                name="public_pricing_text"
                                value={formData.public_pricing_text}
                                onChange={handleFormChange}
                                placeholder="e.g., From £28 / hour"
                              />
                              <small className="room-editor-help">Leave blank to fall back to the internal hourly rate.</small>
                            </div>

                            <div className="form-group">
                              <label htmlFor="public_status">Public Status Badge</label>
                              <input
                                type="text"
                                id="public_status"
                                name="public_status"
                                value={formData.public_status}
                                onChange={handleFormChange}
                                placeholder="e.g., Ready for bookings"
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="public_status_note">Public Status Note</label>
                              <input
                                type="text"
                                id="public_status_note"
                                name="public_status_note"
                                value={formData.public_status_note}
                                onChange={handleFormChange}
                                placeholder="e.g., Availability confirmed during enquiry review"
                              />
                            </div>

                            <div className="form-group room-editor-span-2">
                              <label htmlFor="suitable_for">Suitable For Tags</label>
                              <textarea
                                id="suitable_for"
                                name="suitable_for"
                                rows="3"
                                value={formData.suitable_for}
                                onChange={handleFormChange}
                                placeholder="Dance classes, Rehearsals, Fitness sessions"
                              />
                              <small className="room-editor-help">Enter one per line or separate tags with commas.</small>
                            </div>

                            <div className="form-group room-editor-span-2">
                              <label htmlFor="features">Feature List</label>
                              <textarea
                                id="features"
                                name="features"
                                rows="4"
                                value={formData.features}
                                onChange={handleFormChange}
                                placeholder="Sprung wooden floor&#10;Full-length mirrors&#10;Integrated sound system"
                              />
                              <small className="room-editor-help">These become the public bullet-point feature list.</small>
                            </div>

                            <div className="form-group room-editor-span-2">
                              <label htmlFor="description">Marketing Description</label>
                              <textarea
                                id="description"
                                name="description"
                                rows="4"
                                value={formData.description}
                                onChange={handleFormChange}
                                placeholder="Describe the room, common use cases, and what makes it a good fit."
                              />
                            </div>

                            <div className="form-group room-editor-span-2">
                              <label htmlFor="public_notes">Optional Notes</label>
                              <textarea
                                id="public_notes"
                                name="public_notes"
                                rows="3"
                                value={formData.public_notes}
                                onChange={handleFormChange}
                                placeholder="Accessibility notes, parking, or equipment availability"
                              />
                            </div>
                          </div>
                        </section>
                      </div>
                    )}

                    {activeRoomTab === 'integrations' && user?.role === 'super_admin' && (
                      <div className="room-editor-panel">
                        <section className="room-editor-section">
                          <h3>Calendar And Location Detection</h3>

                          <div className="room-editor-grid">
                            <div className="form-group room-editor-span-2">
                              <label htmlFor="google_calendar_link">Google Calendar Link</label>
                              <input
                                type="text"
                                id="google_calendar_link"
                                name="google_calendar_link"
                                value={formData.google_calendar_link}
                                onChange={handleFormChange}
                                placeholder="Paste Google Calendar URL or ID"
                              />
                              <small className="room-editor-help">
                                Supported formats: public URL, embed URL, or calendar ID.
                              </small>
                            </div>

                            <div className="form-group room-editor-span-2">
                              <label htmlFor="network_cidr">Network Address (CIDR)</label>
                              <input
                                type="text"
                                id="network_cidr"
                                name="network_cidr"
                                value={formData.network_cidr}
                                onChange={handleFormChange}
                                placeholder="e.g., 192.168.55.0/24"
                              />
                              <small className="room-editor-help">
                                Used to detect if a user is in the building. Multiple rooms can share the same network.
                              </small>
                            </div>
                          </div>
                        </section>

                        <section className="room-editor-section">
                          <h3>Home Assistant Integration</h3>

                          <div className="room-editor-grid">
                            <div className="form-group room-editor-span-2">
                              <label htmlFor="ha_token_inherited_from_room">Inherit HA Token From Room</label>
                              <select
                                id="ha_token_inherited_from_room"
                                name="ha_token_inherited_from_room"
                                value={formData.ha_token_inherited_from_room}
                                onChange={handleFormChange}
                              >
                                <option value="">— None (enter manually) —</option>
                                {rooms
                                  .filter((room) => room.id !== selectedRoom?.id && room.ha_url && room.ha_token)
                                  .map((room) => (
                                    <option key={room.id} value={room.id}>{room.name}</option>
                                  ))}
                              </select>
                              <small className="room-editor-help">
                                Only rooms with HA credentials configured are listed.
                              </small>
                            </div>

                            <div className="form-group">
                              <label htmlFor="ha_url">HA Server URL</label>
                              <input
                                type="text"
                                id="ha_url"
                                name="ha_url"
                                value={formData.ha_token_inherited_from_room ? '(inherited)' : formData.ha_url}
                                onChange={handleFormChange}
                                placeholder="e.g., <ha-server>:8123"
                                disabled={!!formData.ha_token_inherited_from_room}
                                style={formData.ha_token_inherited_from_room ? { opacity: 0.5, fontStyle: 'italic' } : {}}
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="ha_token">Long-Lived Access Token</label>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <input
                                  type={showHaToken ? 'text' : 'password'}
                                  id="ha_token"
                                  name="ha_token"
                                  value={formData.ha_token_inherited_from_room ? '(inherited)' : formData.ha_token}
                                  onChange={handleFormChange}
                                  placeholder="Long-lived access token from HA"
                                  disabled={!!formData.ha_token_inherited_from_room}
                                  style={{ flex: 1, fontFamily: showHaToken ? 'monospace' : undefined, ...(formData.ha_token_inherited_from_room ? { opacity: 0.5, fontStyle: 'italic' } : {}) }}
                                />
                                {!formData.ha_token_inherited_from_room && (
                                  <button
                                    type="button"
                                    onClick={() => setShowHaToken((v) => !v)}
                                    title={showHaToken ? 'Hide token' : 'Show token'}
                                    style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', background: '#f8fafc', cursor: 'pointer', fontSize: '16px', whiteSpace: 'nowrap' }}
                                  >
                                    {showHaToken ? '🙈' : '👁️'}
                                  </button>
                                )}
                              </div>
                            </div>

                            <div className="form-group">
                              <label htmlFor="ha_climate_entity">Climate Entity</label>
                              <input
                                type="text"
                                id="ha_climate_entity"
                                name="ha_climate_entity"
                                value={formData.ha_climate_entity}
                                onChange={handleFormChange}
                                placeholder="e.g., climate.studio_1"
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="ha_occupancy_entity">Occupancy Entity</label>
                              <input
                                type="text"
                                id="ha_occupancy_entity"
                                name="ha_occupancy_entity"
                                value={formData.ha_occupancy_entity}
                                onChange={handleFormChange}
                                placeholder="e.g., input_text.s1_cal_occupancy"
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="ha_lighting_index_entity">Lighting Index Entity</label>
                              <input
                                type="text"
                                id="ha_lighting_index_entity"
                                name="ha_lighting_index_entity"
                                value={formData.ha_lighting_index_entity}
                                onChange={handleFormChange}
                                placeholder="e.g., input_number.s1_lighting_index"
                              />
                            </div>

                            <div className="form-group">
                              <label htmlFor="ha_lighting_name_entity">Lighting Name Entity</label>
                              <input
                                type="text"
                                id="ha_lighting_name_entity"
                                name="ha_lighting_name_entity"
                                value={formData.ha_lighting_name_entity}
                                onChange={handleFormChange}
                                placeholder="e.g., input_text.s1_lighting_name"
                              />
                            </div>
                          </div>
                        </section>
                      </div>
                    )}
                  </fieldset>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-outline" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={formLoading || roomModalLoading}>
                    {formLoading ? 'Saving...' : (modalMode === 'add' ? 'Create Room' : 'Save Changes')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* M004-F0074-I: Reject Deletion Modal */}
        {rejectDeletionModal && (
          <div className="modal-overlay" {...rejectDeletionModalDismissHandlers}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Keep Booking (Reject Deletion)</h2>
                <button className="close-btn" onClick={() => setRejectDeletionModal(null)}>×</button>
              </div>
              <div className="modal-body">
                <p><strong>Booking:</strong> {rejectDeletionModal.title || 'Room Booking'}</p>
                <p><strong>Booked by:</strong> {requesterNameOf(rejectDeletionModal)}</p>
                <p><strong>Date:</strong> {formatBookingDate(rejectDeletionModal.start_time)}</p>
                <p><strong>Time:</strong> {formatBookingTime(rejectDeletionModal.start_time)} - {formatBookingTime(rejectDeletionModal.end_time)}</p>
                {rejectDeletionModal.deletion_reason && (
                  <p><strong>User's reason for deletion:</strong> {rejectDeletionModal.deletion_reason}</p>
                )}

                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label htmlFor="rejectDeletionReason">Reason for keeping booking (optional)</label>
                  <textarea
                    id="rejectDeletionReason"
                    className="form-input"
                    rows="3"
                    value={rejectDeletionReason}
                    onChange={(e) => setRejectDeletionReason(e.target.value)}
                    placeholder="Enter reason for rejecting deletion request..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setRejectDeletionModal(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleRejectDeletion}
                  disabled={deletionActionLoading === rejectDeletionModal?.id}
                >
                  {deletionActionLoading === rejectDeletionModal?.id ? 'Keeping...' : 'Keep Booking'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

// ==================== Admin Organisations (Super Admin Only) ====================
const AdminOrganisations = () => {
  const { user } = useContext(AuthContext);
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    address: '',
    contact_email: '',
    contact_phone: '',
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  // M004-F0613 / T-M004-0020: Add/Edit Organisation falls under the spec's blanket
  // rule (§2 "In scope") but was left unwired as a declared follow-up (Mantis
  // #1177). "new" and "edit:<id>" are separate scope keys (§5.2) so a draft for one
  // organisation's edit can never leak into the add form or into a different
  // organisation's edit.
  const newOrgDraft = useDialogDraft('admin-org-new', 'new', { active: showModal && modalMode === 'add' });
  const editOrgDraft = useDialogDraft('admin-org-edit', selectedOrg?.id, { active: showModal && modalMode === 'edit' });
  const orgDialogDraftRef = modalMode === 'add' ? newOrgDraft.containerRef : editOrgDraft.containerRef;
  const [securityPolicyData, setSecurityPolicyData] = useState(DEFAULT_ORG_SECURITY_POLICY);
  const [securityPolicyAvailable, setSecurityPolicyAvailable] = useState(false);
  const [securityPolicyError, setSecurityPolicyError] = useState('');
  const [securityPolicySuccess, setSecurityPolicySuccess] = useState('');
  const [securityPolicyLoading, setSecurityPolicyLoading] = useState(false);
  const [myceliumInstallations, setMyceliumInstallations] = useState([]);
  const [myceliumLoading, setMyceliumLoading] = useState(false);
  const [myceliumError, setMyceliumError] = useState('');

  // User management state
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [addUserData, setAddUserData] = useState({ user_id: '', role: 'student', is_primary: false });
  const [addUserError, setAddUserError] = useState('');
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [orgUserActionLoading, setOrgUserActionLoading] = useState(null);

  // Mantis #1390 / M004-F0618 §D.4: per-person permission override panel
  // target. Adjacent to, not overlapping, the Plan column state below —
  // it opens from its own "Permissions…" action in the Actions column.
  const [permissionOverrideTarget, setPermissionOverrideTarget] = useState(null);
  const openPermissionOverridePanel = (orgUser) => setPermissionOverrideTarget(orgUser);
  const closePermissionOverridePanel = () => setPermissionOverrideTarget(null);

  // M004-F0615 / T-M004-0022: member default price plan panel state.
  // `pricingEffectivePermissions` is the caller's own tri-state-resolved
  // projection for the open organisation (MR 1 #4 / D.5) — the render gate
  // is always read from this, never from `user.role`.
  const [pricingEffectivePermissions, setPricingEffectivePermissions] = useState(null);
  const [pricingPlansViewDenied, setPricingPlansViewDenied] = useState(false);
  const [memberPlanAssignments, setMemberPlanAssignments] = useState({});
  const [expandedPriorPlans, setExpandedPriorPlans] = useState({});
  const [changePlanTarget, setChangePlanTarget] = useState(null);
  const [changePlanPlans, setChangePlanPlans] = useState([]);
  const [changePlanPlansLoading, setChangePlanPlansLoading] = useState(false);
  const [changePlanForm, setChangePlanForm] = useState({
    pricing_plan_id: '',
    effective_from: '',
  });
  const [changePlanError, setChangePlanError] = useState('');
  const [changePlanLoading, setChangePlanLoading] = useState(false);
  const [changePlanStepUpChallenge, setChangePlanStepUpChallenge] = useState(null);
  const [changePlanStepUpRetry, setChangePlanStepUpRetry] = useState(null);

  useEffect(() => {
    fetchOrganisations();
    fetchMyceliumLinkContext();
  }, []);

  // M004-F0654 (WebUI #124): the route admits `admin`, so the page must
  // serve one. A global super_admin (organisation_view_all) lists every
  // organisation via GET /organisations; anyone else lists the
  // organisations they ADMINISTER from GET /organisations/mine (membership
  // user_role 'admin', the Admin Rooms scope idiom). Core stays the
  // authority for every action on those rows.
  const managesAllOrganisations = isGlobalSuperAdmin(user);
  // M004-F0654 B3 (amended 2026-10-05, TODO-F0654-1): Add / Edit / Delete
  // follow the caller's own system capabilities from Core
  // GET /users/me/system-permissions — organisation_create /
  // organisation_update_all / organisation_delete, the exact keys Core's
  // routes assert — not the role. `{}` until Core answers and on any failure,
  // so every control stays hidden (fail closed).
  const [myOrgSystemPermissions, setMyOrgSystemPermissions] = useState({});
  useEffect(() => {
    let cancelled = false;
    loadMySystemPermissions(userApi).then((map) => {
      if (!cancelled) setMyOrgSystemPermissions(map);
    });
    return () => { cancelled = true; };
  }, [user?.id, user?.uuid]);
  const organisationActions = organisationActionsFor(myOrgSystemPermissions);

  const fetchOrganisations = async () => {
    try {
      setLoading(true);
      const response = managesAllOrganisations
        ? await organisationApi.getAll()
        : await organisationApi.getMine();
      const listed = response.data?.data || [];
      const nextOrganisations = managesAllOrganisations
        ? listed
        : selectAdminRoomsOrganisations(listed, user);
      setOrganisations(nextOrganisations);
      return nextOrganisations;
    } catch (err) {
      console.error('Failed to fetch organisations:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchMyceliumLinkContext = async () => {
    try {
      setMyceliumLoading(true);
      setMyceliumError('');
      const response = await myceliumApi.getLinkContext();
      const payload = response.data?.data || response.data || {};
      setMyceliumInstallations(normaliseArrayPayload(payload.installations));
    } catch (err) {
      console.error('Failed to fetch Mycelium link context:', err);
      const status = err.response?.status;
      if ([401, 403].includes(status)) {
        setMyceliumError('Elevated admin access is required to view Mycelium link status.');
      } else {
        setMyceliumError(getAdminApiErrorMessage(err, 'Mycelium link status could not be loaded.'));
      }
      setMyceliumInstallations([]);
    } finally {
      setMyceliumLoading(false);
    }
  };

  const setSecurityPolicyFromOrg = (org, { allowMissing = true } = {}) => {
    const policy = normaliseOrganisationSecurityPolicy(org);
    if (policy) {
      setSecurityPolicyData(policy);
      setSecurityPolicyAvailable(true);
      setSecurityPolicyError('');
      return true;
    }

    setSecurityPolicyData(DEFAULT_ORG_SECURITY_POLICY);
    setSecurityPolicyAvailable(false);
    if (allowMissing) {
      setSecurityPolicyError('Security policy values were not returned by the backend, so policy changes are disabled for this organisation.');
    }
    return false;
  };

  const loadSecurityPolicyForOrg = async (org) => {
    if (setSecurityPolicyFromOrg(org)) {
      return;
    }

    try {
      const response = await organisationApi.get(org.id);
      const detailedOrg = response.data?.data || {};
      if (setSecurityPolicyFromOrg(detailedOrg)) {
        setSelectedOrg(prev => (prev?.id === org.id ? { ...prev, ...detailedOrg } : prev));
      }
    } catch (err) {
      console.error('Failed to fetch organisation security policy:', err);
      setSecurityPolicyError(err.response?.data?.message || 'Security policy values could not be loaded from the backend.');
    }
  };

  const openAddModal = () => {
    setModalMode('add');
    setSelectedOrg(null);
    // M004-F0613: restore any draft left from a previously-incidental close of this
    // same dialog ("new").
    const draft = getDraftForScope(buildDraftScopeKey('admin-org-new', 'new'));
    setFormData({ name: '', slug: '', description: '', address: '', contact_email: '', contact_phone: '', ...draft });
    setFormError('');
    setSecurityPolicyData(DEFAULT_ORG_SECURITY_POLICY);
    setSecurityPolicyAvailable(false);
    setSecurityPolicyError('');
    setSecurityPolicySuccess('');
    setShowModal(true);
  };

  const openEditModal = (org) => {
    setModalMode('edit');
    setSelectedOrg(org);
    // M004-F0613: scoped to this specific organisation's id, so it can never show a
    // draft left for a different organisation (or for the "new" dialog).
    const draft = getDraftForScope(buildDraftScopeKey('admin-org-edit', org.id));
    setFormData({
      name: org.name,
      slug: org.slug,
      description: org.description || '',
      address: org.address || '',
      contact_email: org.contact_email || '',
      contact_phone: org.contact_phone || '',
      ...draft,
    });
    setFormError('');
    setSecurityPolicySuccess('');
    setSecurityPolicyLoading(false);
    setShowModal(true);
    loadSecurityPolicyForOrg(org);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedOrg(null);
    setFormError('');
    setSecurityPolicyError('');
    setSecurityPolicySuccess('');
    setSecurityPolicyLoading(false);
  };

  // M004-F0612 / T-M004-0019: dismiss only on a genuine outside press-and-release;
  // a text-selection drag from inside a dialog to outside it must not close it.
  const orgModalDismissHandlers = useDismissOnOutsideClick(closeModal);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSecurityPolicyChange = (e) => {
    const { name, checked } = e.target;
    setSecurityPolicyData(prev => ({ ...prev, [name]: checked }));
    setSecurityPolicySuccess('');
  };

  const getSecurityPolicyErrorMessage = (err) => {
    const status = err.response?.status;
    const rawError = err.response?.data?.error;
    const errorCode = typeof rawError === 'string' ? rawError : rawError?.code;
    const message = err.response?.data?.message || rawError?.message;

    if (status === 404) {
      return 'Security policy saving is not available on this backend deployment yet.';
    }

    if ([401, 403].includes(status) && /STEP_UP|ELEVATED|TWO_SV|2SV|SAL/i.test(errorCode || message || '')) {
      return 'Security policy changes require a fresh elevated sign-in or 2SV step-up. Complete step-up and retry.';
    }

    return message || 'Failed to save security policy';
  };

  const handleSaveSecurityPolicy = async () => {
    if (!selectedOrg || !securityPolicyAvailable) {
      setSecurityPolicyError('Security policy values must be loaded from the backend before they can be saved.');
      return;
    }

    setSecurityPolicyLoading(true);
    setSecurityPolicyError('');
    setSecurityPolicySuccess('');

    const payload = {
      require2svAllUsers: securityPolicyData.require2svAllUsers,
      require2svAdminsOnly: securityPolicyData.require2svAdminsOnly,
      allowFederatedMfa: securityPolicyData.allowFederatedMfa,
    };

    try {
      const response = await organisationApi.updateSecurityPolicy(selectedOrg.id, payload);
      const savedPolicy = normaliseOrganisationSecurityPolicy(response.data?.data) || payload;
      setSecurityPolicyData(savedPolicy);
      setSecurityPolicyAvailable(true);
      setSecurityPolicySuccess('Security policy saved.');

      const refreshedOrganisations = await fetchOrganisations();
      const refreshedOrg = refreshedOrganisations.find(org => org.id === selectedOrg.id);
      setSelectedOrg(prev => ({ ...(prev || selectedOrg), ...(refreshedOrg || {}), securityPolicy: savedPolicy }));
      if (showUsersModal) {
        await fetchOrgUsers(selectedOrg.id);
      }
    } catch (err) {
      console.error('Failed to save organisation security policy:', err);
      setSecurityPolicyError(getSecurityPolicyErrorMessage(err));
    } finally {
      setSecurityPolicyLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    // Captured before closeModal() resets selectedOrg back to null.
    const editRecordId = selectedOrg?.id;

    if (!formData.name.trim()) {
      setFormError('Organisation name is required');
      setFormLoading(false);
      return;
    }

    try {
      if (modalMode === 'add') {
        await organisationApi.create(formData);
        // M004-F0613: a draft is cleared only on successful submit, never merely
        // because the dialog closed.
        clearDraftForScope(buildDraftScopeKey('admin-org-new', 'new'));
      } else {
        await organisationApi.update(selectedOrg.id, formData);
        clearDraftForScope(buildDraftScopeKey('admin-org-edit', editRecordId));
      }
      await fetchOrganisations();
      closeModal();
    } catch (err) {
      console.error('Failed to save organisation:', err);
      setFormError(err.response?.data?.message || 'Failed to save organisation');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (org, force = false) => {
    const confirmMessage = force
      ? `⚠️ FORCE DELETE: This will remove ALL users and rooms from "${org.name}" and then delete it. This cannot be undone. Are you sure?`
      : `Are you sure you want to delete "${org.name}"? This cannot be undone.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }
    try {
      await organisationApi.delete(org.id, force);
      await fetchOrganisations();
    } catch (err) {
      console.error('Delete error full:', err);
      console.error('err.response:', err.response);
      console.error('err.response?.data:', err.response?.data);
      console.error('err.response?.status:', err.response?.status);

      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete organisation';
      const errorCode = err.response?.data?.error;

      console.log('Parsed errorMessage:', errorMessage);
      console.log('Parsed errorCode:', errorCode);

      // If blocked due to users/rooms, offer force delete
      if (errorCode === 'ORG_HAS_USERS' || errorCode === 'ORG_HAS_ROOMS') {
        if (window.confirm(`${errorMessage}\n\nWould you like to force delete? This will remove all users and rooms from this organisation.`)) {
          await handleDelete(org, true);
        }
      } else {
        alert(`${errorMessage}\n\nDebug: status=${err.response?.status}, code=${errorCode}`);
      }
    }
  };

  // User management functions
  const openUsersModal = async (org) => {
    setSelectedOrg(org);
    setShowUsersModal(true);
    setShowAddUserForm(false);
    setAddUserError('');
    setMemberPlanAssignments({});
    setExpandedPriorPlans({});
    setPricingEffectivePermissions(null);
    setPricingPlansViewDenied(false);
    const users = await fetchOrgUsers(org.id);
    await fetchAllUsers();
    await loadMemberPricingContext(org.id, users);
  };

  const closeUsersModal = () => {
    setShowUsersModal(false);
    setSelectedOrg(null);
    setOrgUsers([]);
    setShowAddUserForm(false);
    setAddUserError('');
    setOrgUserActionLoading(null);
    setMemberPlanAssignments({});
    setExpandedPriorPlans({});
    setPricingEffectivePermissions(null);
    setPricingPlansViewDenied(false);
  };

  // M004-F0612 / T-M004-0019: dismiss only on a genuine outside press-and-release;
  // a text-selection drag from inside a dialog to outside it must not close it.
  const usersModalDismissHandlers = useDismissOnOutsideClick(closeUsersModal);

  const fetchOrgUsers = async (orgId) => {
    try {
      setUsersLoading(true);
      const response = await organisationApi.getUsers(orgId);
      const users = response.data?.data || [];
      setOrgUsers(users);
      return users;
    } catch (err) {
      console.error('Failed to fetch organisation users:', err);
      return [];
    } finally {
      setUsersLoading(false);
    }
  };

  // M004-F0615 / T-M004-0022: load the caller's effective pricing
  // permissions for this organisation via the canonical, generic
  // `getMyEffectiveOrganisationPermissions` helper (M001-D022-F029, Core MR
  // !571's real `GET /organisations/:id/my-permissions` route — never
  // re-derive this from `organisationApi.getMine()` or `user.role`), then,
  // only if the read is actually permitted (`pricing_plans_view`), EVERY
  // member's CURRENT plan assignment in ONE request
  // (`pricingApi.listAssignmentsForOrganisation`, M005-F0047/T-PRICE-024's
  // org-wide list form) — never one request per member. A denied read
  // renders a single row-spanning note (T-M004-0022f) instead of leaving
  // the column silently blank. Each member's full history (the "Prior
  // plans" expander) is loaded lazily, on demand, by `loadPriorPlansFor`
  // below — never eagerly for every member on modal open.
  const loadMemberPricingContext = async (orgId, users) => {
    const permissions = await getMyEffectiveOrganisationPermissions(orgId);
    setPricingEffectivePermissions(permissions);

    if (!permissions || permissions.pricing_plans_view !== true) {
      setPricingPlansViewDenied(true);
      setMemberPlanAssignments({});
      return;
    }

    setPricingPlansViewDenied(false);

    try {
      const response = await pricingApi.listAssignmentsForOrganisation(orgId);
      const members = response.data?.data?.members || [];
      const next = {};
      members.forEach((member) => {
        next[member.user_id] = {
          current: member.current_assignment || null,
          history: null,
          historyLoading: false,
          error: null,
        };
      });
      // A user present in `users` but absent from the members read (should
      // not happen — same org, same deleted_at filter — but never leave a
      // row silently in the pre-load "Loading…" state forever).
      (users || []).forEach((orgUser) => {
        if (!next[orgUser.id]) {
          next[orgUser.id] = { current: null, history: null, historyLoading: false, error: null };
        }
      });
      setMemberPlanAssignments(next);
    } catch (err) {
      console.error('Failed to load member plan assignments:', err);
      const next = {};
      (users || []).forEach((orgUser) => {
        next[orgUser.id] = { current: null, history: null, historyLoading: false, error: true };
      });
      setMemberPlanAssignments(next);
    }
  };

  // On-demand "Prior plans" read for one member (T-M004-0022): the per-user
  // `pricingApi.getMemberPlanAssignments` call, made ONLY when the expander
  // is first opened for that member — never as part of the initial table
  // load. `assignments` is newest-first, current + history; the current
  // row(s) (`is_current`) are excluded here since the Plan column already
  // shows the current assignment from the org-wide read.
  const loadPriorPlansFor = async (orgId, userId) => {
    setMemberPlanAssignments((prev) => ({
      ...prev,
      [userId]: { ...(prev[userId] || {}), historyLoading: true },
    }));
    try {
      const response = await pricingApi.getMemberPlanAssignments(orgId, userId);
      const assignments = response.data?.data?.assignments || [];
      const history = assignments.filter((assignment) => assignment.is_current !== true);
      setMemberPlanAssignments((prev) => ({
        ...prev,
        [userId]: { ...(prev[userId] || {}), history, historyLoading: false, error: null },
      }));
    } catch (err) {
      console.error('Failed to load prior plan assignments:', err);
      setMemberPlanAssignments((prev) => ({
        ...prev,
        [userId]: { ...(prev[userId] || {}), history: [], historyLoading: false, error: true },
      }));
    }
  };

  const togglePriorPlans = (userId) => {
    const willExpand = !expandedPriorPlans[userId];
    setExpandedPriorPlans((prev) => ({ ...prev, [userId]: willExpand }));
    if (willExpand && selectedOrg && memberPlanAssignments[userId]?.history === null) {
      loadPriorPlansFor(selectedOrg.id, userId);
    }
  };

  const openChangePlanModal = async (orgUser) => {
    if (!selectedOrg) return;
    setChangePlanTarget(orgUser);
    setChangePlanError('');
    setChangePlanForm({
      pricing_plan_id: '',
      effective_from: new Date().toISOString().slice(0, 10),
    });
    setChangePlanStepUpChallenge(null);
    setChangePlanStepUpRetry(null);
    setChangePlanPlansLoading(true);
    try {
      const response = await pricingApi.getPlans(selectedOrg.id);
      const plans = response.data?.data?.plans || response.data?.plans || [];
      setChangePlanPlans(plans.filter((plan) => !plan.is_template && plan.status !== 'retired'));
    } catch (err) {
      setChangePlanError(getAdminApiErrorMessage(err, 'Could not load price plans.'));
      setChangePlanPlans([]);
    } finally {
      setChangePlanPlansLoading(false);
    }
  };

  const closeChangePlanModal = () => {
    setChangePlanTarget(null);
    setChangePlanPlans([]);
    setChangePlanError('');
    setChangePlanStepUpChallenge(null);
    setChangePlanStepUpRetry(null);
  };

  // T-M004-0022(d): every submit POSTs a brand-new assignment row
  // (`pricingApi.assignMemberPlan`) — there is no update-in-place call for
  // an existing assignment anywhere in this panel. After it succeeds the
  // member's assignments are re-read from the server, so the previous
  // current plan moves into Prior plans and the new one becomes current;
  // nothing is patched client-side.
  const submitChangePlan = async () => {
    if (!changePlanTarget || !selectedOrg) return;
    if (!changePlanForm.pricing_plan_id) {
      setChangePlanError('Select a plan.');
      return;
    }
    if (!changePlanForm.effective_from) {
      setChangePlanError('Select an effective date.');
      return;
    }

    setChangePlanLoading(true);
    setChangePlanError('');
    const target = changePlanTarget;
    try {
      await pricingApi.assignMemberPlan(selectedOrg.id, {
        pricing_plan_id: Number(changePlanForm.pricing_plan_id),
        user_id: target.id,
        effective_from: changePlanForm.effective_from,
        // Wire shape verified against Core's real `assignPlanSchema`
        // (src/routes/pricing.routes.ts, origin/main + MR !590, 2026-09-07):
        // exactly `{pricing_plan_id, user_id | organisation_user_id,
        // effective_from, effective_to?}` and `.strict()` — the previous
        // `grandfather_existing_bookings` key invented here was rejected 400
        // by that strict schema on EVERY submit. Grandfathering is a
        // plan-EDIT concern (PATCH /plans/:id `grandfather`, R8 default
        // OFF), never an assignment concern: an assignment changes which
        // plan FUTURE bookings resolve onto; bookings that already have a
        // quote keep their agreed price unless deliberately repriced
        // (Core MR !590 adds the explicit confirm_supersede guard).
      });
      setChangePlanStepUpChallenge(null);
      setChangePlanStepUpRetry(null);
      closeChangePlanModal();

      // Re-read this one member from the server rather than patching state —
      // T-M004-0022(d): the previous current row must move into history and
      // the new one become current, never edited in place client-side.
      const refreshed = await pricingApi.getMemberPlanAssignments(selectedOrg.id, target.id);
      const assignments = refreshed.data?.data?.assignments || [];
      const current = assignments.find((assignment) => assignment.is_current === true) || null;
      const history = assignments.filter((assignment) => assignment.is_current !== true);
      setMemberPlanAssignments((prev) => ({
        ...prev,
        [target.id]: { current, history, historyLoading: false, error: null },
      }));
    } catch (err) {
      // T-M004-0022(e): a SAL step-up challenge retries this exact
      // submission (same target, same form values) once satisfied, the
      // same pattern as `saveNetworkSal4Setting`/`runAdminSecurityAction`
      // elsewhere in this file.
      const challenge = getStepUpChallengeFromError(err);
      if (challenge?.id) {
        setChangePlanStepUpChallenge(challenge);
        setChangePlanStepUpRetry(() => submitChangePlan);
        setChangePlanError('A fresh 2SV step-up is required before this plan change can continue.');
        return;
      }
      setChangePlanError(getAdminApiErrorMessage(err, 'Failed to change plan.'));
    } finally {
      setChangePlanLoading(false);
    }
  };

  const handleChangePlanSubmit = (e) => {
    e.preventDefault();
    submitChangePlan();
  };

  const completeChangePlanStepUp = async () => {
    const retry = changePlanStepUpRetry;
    setChangePlanStepUpChallenge(null);
    setChangePlanStepUpRetry(null);
    if (retry) {
      await retry();
    }
  };

  const cancelChangePlanStepUp = () => {
    setChangePlanStepUpChallenge(null);
    setChangePlanStepUpRetry(null);
  };

  const fetchAllUsers = async () => {
    try {
      const response = await userApi.getUsers();
      setAllUsers(response.data?.data?.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const getOrgUserName = (orgUser) => (
    orgUser.display_name || orgUser.displayName || orgUser.primary_email || orgUser.email || `User #${orgUser.id}`
  );

  const getOrgUserStatus = (orgUser) => orgUser.status || orgUser.account_status || 'unknown';

  const handleOrgUserLifecycleAction = async (orgUser, action) => {
    const uuid = orgUser.uuid || orgUser.user_uuid;
    if (!uuid) {
      alert('Cannot update this user because no user UUID was returned by the API.');
      return;
    }

    const labels = {
      approve: 'approve',
      reject: 'reject',
      suspend: 'suspend',
      reactivate: 'reactivate',
      reconsider: 'move back to pending',
    };
    const label = labels[action] || action;
    if (!window.confirm(`${label[0].toUpperCase()}${label.slice(1)} ${getOrgUserName(orgUser)}?`)) {
      return;
    }

    const apiByAction = {
      approve: userApi.approveUser,
      reject: userApi.rejectUser,
      suspend: userApi.suspendUserAdmin,
      reactivate: userApi.reactivateUser,
      reconsider: userApi.reconsiderUser,
    };

    try {
      setOrgUserActionLoading(`${uuid}:${action}`);
      await apiByAction[action](uuid);
      await fetchOrgUsers(selectedOrg.id);
    } catch (err) {
      console.error(`Failed to ${action} organisation user:`, err);
      alert(err.response?.data?.message || err.response?.data?.error || `Failed to ${action} user`);
    } finally {
      setOrgUserActionLoading(null);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddUserError('');

    if (!addUserData.user_id) {
      setAddUserError('Please select a user');
      return;
    }

    setAddUserLoading(true);
    try {
      await organisationApi.addUser(
        selectedOrg.id,
        addUserData.user_id,
        addUserData.role,
        addUserData.is_primary
      );
      await fetchOrgUsers(selectedOrg.id);
      setShowAddUserForm(false);
      setAddUserData({ user_id: '', role: 'student', is_primary: false });
    } catch (err) {
      console.error('Failed to add user:', err);
      setAddUserError(err.response?.data?.message || 'Failed to add user to organisation');
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleRemoveUser = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from ${selectedOrg.name}?`)) {
      return;
    }
    try {
      await organisationApi.removeUser(selectedOrg.id, userId);
      await fetchOrgUsers(selectedOrg.id);
    } catch (err) {
      console.error('Failed to remove user:', err);
      alert(err.response?.data?.message || 'Failed to remove user');
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await organisationApi.updateUserRole(selectedOrg.id, userId, newRole);
      await fetchOrgUsers(selectedOrg.id);
    } catch (err) {
      console.error('Failed to update user role:', err);
      alert(err.response?.data?.message || 'Failed to update role');
    }
  };

  // Get users not yet in this organisation for the add dropdown
  const availableUsers = allUsers.filter(u => !orgUsers.some(ou => ou.id === u.id));

  const getMyceliumInstallationForOrg = (org) => {
    const orgId = org?.id || org?.organisation_id || org?.organisationId;
    return myceliumInstallations.find((installation) => (
      String(installation.ownerOrgId || installation.owner_org_id || '') === String(orgId)
    ));
  };

  const renderMyceliumLinkStatus = (org) => {
    if (myceliumLoading) {
      return (
        <div className="mycelium-link-cell" data-testid="mycelium-link-loading">
          Loading link status...
        </div>
      );
    }

    if (myceliumError) {
      return (
        <div className="mycelium-link-cell muted" data-testid="mycelium-link-error">
          {myceliumError}
        </div>
      );
    }

    const installation = getMyceliumInstallationForOrg(org);
    if (!installation) {
      return (
        <div className="mycelium-link-cell muted" data-testid="mycelium-link-empty">
          No server linked
        </div>
      );
    }

    const serviceScopes = normaliseArrayPayload(installation.serviceScopes || installation.service_scopes);
    const serviceKey = installation.serviceKey || installation.service_key || null;
    const keyPresent = Boolean(serviceKey?.present || serviceKey?.keyId || serviceKey?.key_id);
    const keyId = serviceKey?.keyId || serviceKey?.key_id || '';
    const status = installation.status || 'unknown';

    return (
      <div className="mycelium-link-cell" data-testid="mycelium-link-status-panel">
        <div className="mycelium-link-heading">
          <span
            className={`status-badge ${status === 'active' ? 'success' : 'warning'}`}
            data-testid="mycelium-install-state"
          >
            {status}
          </span>
          <strong data-testid="mycelium-install-label">
            {installation.installLabel || installation.install_label || 'Mycelium server'}
          </strong>
        </div>
        <div className="mycelium-link-detail" data-testid="mycelium-install-hostname">
          Host: {installation.hostname || 'Not reported'}
        </div>
        <div className="mycelium-link-detail" data-testid="mycelium-install-base-url">
          Base URL: {installation.localBaseUrl || installation.local_base_url || 'Not reported'}
        </div>
        <div className="mycelium-link-detail" data-testid="mycelium-install-last-seen">
          Last seen: {formatOptionalDateTime(installation.lastSeenAt || installation.last_seen_at)}
        </div>
        <div className="mycelium-link-detail" data-testid="mycelium-install-scopes">
          Scopes: {serviceScopes.length ? serviceScopes.join(', ') : 'None reported'}
        </div>
        <div className="mycelium-link-detail" data-testid="mycelium-key-status">
          Service key: {keyPresent ? `present (${maskServiceKeyId(keyId)})` : 'not present'}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="admin-users">
        <header className="page-header">
          <div>
            <h1>Organisation Management</h1>
            <p className="subtitle">
              {managesAllOrganisations
                ? `${organisations.length} organisations`
                : `${organisations.length} organisation${organisations.length === 1 ? '' : 's'} you administer`}
            </p>
          </div>
          {/* Creating an organisation needs the system capability
              organisation_create (Core POST /organisations). */}
          {organisationActions.canCreate && (
            <button className="btn btn-primary" onClick={openAddModal}>+ Add Organisation</button>
          )}
        </header>

        {loading ? (
          <div className="loading-spinner">Loading organisations...</div>
        ) : !managesAllOrganisations && organisations.length === 0 ? (
          <p className="text-muted" data-testid="admin-organisations-none-administered">
            You do not administer any organisation.
          </p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Mycelium Link</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {organisations.map((org) => (
                  <tr key={org.id}>
                    <td>
                      <strong>{org.name}</strong>
                      {org.description && <div className="text-muted small">{org.description}</div>}
                    </td>
                    <td><code>{org.slug}</code></td>
                    <td>
                      {org.contact_email && <div>{org.contact_email}</div>}
                      {org.contact_phone && <div>{org.contact_phone}</div>}
                    </td>
                    <td>
                      <span className={`status-badge ${org.status === 'active' ? 'success' : 'warning'}`}>
                        {org.status}
                      </span>
                    </td>
                    <td>
                      {renderMyceliumLinkStatus(org)}
                    </td>
                    <td className="actions">
                      <button className="btn btn-sm btn-outline" onClick={() => openUsersModal(org)}>Users</button>
                      {/* Edit needs organisation_update_all (Core PUT
                          /organisations/:id); Delete needs organisation_delete
                          (Core DELETE /organisations/:id). Each on its own key. */}
                      {organisationActions.canEdit && (
                        <button className="btn btn-sm btn-outline" title="Edit" onClick={() => openEditModal(org)}>Edit</button>
                      )}
                      {organisationActions.canDelete && (
                        <button className="btn btn-sm btn-outline" onClick={() => handleDelete(org)}>Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add/Edit Organisation Modal */}
        {showModal && (
          <div className="modal-overlay" {...orgModalDismissHandlers}>
            <div className="modal" ref={orgDialogDraftRef} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{modalMode === 'add' ? 'Add New Organisation' : 'Edit Organisation'}</h2>
                <button className="modal-close" onClick={closeModal}>&times;</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {formError && <div className="form-error">{formError}</div>}

                  <div className="form-group">
                    <label htmlFor="name">Organisation Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                      placeholder="e.g., Social Space Dance"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="slug">URL Slug</label>
                    <input
                      type="text"
                      id="slug"
                      name="slug"
                      value={formData.slug}
                      onChange={handleFormChange}
                      placeholder="auto-generated if empty"
                      disabled={modalMode === 'edit'}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      placeholder="Brief description of the organisation"
                      rows={3}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="address">Address</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleFormChange}
                      placeholder="Organisation address"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="contact_email">Contact Email</label>
                    <input
                      type="email"
                      id="contact_email"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleFormChange}
                      placeholder="admin@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="contact_phone">Contact Phone</label>
                    <input
                      type="tel"
                      id="contact_phone"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleFormChange}
                      placeholder="+44 7700 900123"
                    />
                  </div>

                  {modalMode === 'edit' && (
                    <section className="org-security-policy" data-testid="org-security-policy-controls">
                      <div className="org-security-policy-header">
                        <h3>Security Policy</h3>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          data-testid="org-security-policy-save"
                          onClick={handleSaveSecurityPolicy}
                          disabled={!securityPolicyAvailable || securityPolicyLoading}
                        >
                          {securityPolicyLoading ? 'Saving...' : 'Save Policy'}
                        </button>
                      </div>

                      {securityPolicyError && (
                        <div className="form-error" data-testid="org-security-policy-error">
                          {securityPolicyError}
                        </div>
                      )}
                      {securityPolicySuccess && (
                        <div className="form-success" data-testid="org-security-policy-success">
                          {securityPolicySuccess}
                        </div>
                      )}

                      <label className="policy-toggle" htmlFor="require2svAllUsers">
                        <input
                          type="checkbox"
                          id="require2svAllUsers"
                          name="require2svAllUsers"
                          checked={securityPolicyData.require2svAllUsers}
                          onChange={handleSecurityPolicyChange}
                          disabled={!securityPolicyAvailable || securityPolicyLoading}
                          data-testid="org-policy-require-all-users"
                        />
                        <span>Require 2SV for all users</span>
                      </label>

                      <label className="policy-toggle" htmlFor="require2svAdminsOnly">
                        <input
                          type="checkbox"
                          id="require2svAdminsOnly"
                          name="require2svAdminsOnly"
                          checked={securityPolicyData.require2svAdminsOnly}
                          onChange={handleSecurityPolicyChange}
                          disabled={!securityPolicyAvailable || securityPolicyLoading}
                          data-testid="org-policy-require-admins"
                        />
                        <span>Require 2SV for admins</span>
                      </label>

                      <label className="policy-toggle" htmlFor="allowFederatedMfa">
                        <input
                          type="checkbox"
                          id="allowFederatedMfa"
                          name="allowFederatedMfa"
                          checked={securityPolicyData.allowFederatedMfa}
                          onChange={handleSecurityPolicyChange}
                          disabled={!securityPolicyAvailable || securityPolicyLoading}
                          data-testid="org-policy-allow-federated-mfa"
                        />
                        <span>Allow federated MFA</span>
                      </label>
                    </section>
                  )}
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-outline" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={formLoading}>
                    {formLoading ? 'Saving...' : (modalMode === 'add' ? 'Create Organisation' : 'Save Changes')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Organisation Users Modal */}
        {showUsersModal && selectedOrg && (
          <div className="modal-overlay" {...usersModalDismissHandlers}>
            <div
              className="modal modal-lg organisation-users-modal"
              data-testid="organisation-users-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Users in {selectedOrg.name}</h2>
                <button className="modal-close" onClick={closeUsersModal}>&times;</button>
              </div>
              <div className="modal-body">
                {/* Add User Form */}
                {!showAddUserForm ? (
                  <div style={{ marginBottom: '1rem' }}>
                    <button className="btn btn-primary btn-sm" onClick={() => setShowAddUserForm(true)}>
                      + Add User to Organisation
                    </button>
                  </div>
                ) : (
                  <div className="add-user-form" style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '1rem' }}>Add User to {selectedOrg.name}</h4>
                    {addUserError && <div className="form-error" style={{ marginBottom: '1rem' }}>{addUserError}</div>}
                    <form onSubmit={handleAddUser}>
                      <div className="form-row" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                        <div className="form-group" style={{ flex: '2', minWidth: '200px' }}>
                          <label htmlFor="add_user_id">Select User</label>
                          <select
                            id="add_user_id"
                            value={addUserData.user_id}
                            onChange={(e) => setAddUserData(prev => ({ ...prev, user_id: e.target.value }))}
                            required
                            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                          >
                            <option value="">-- Select a user --</option>
                            {availableUsers.map(u => (
                              <option key={u.id} value={u.id}>
                                {u.display_name || u.displayName} ({u.primary_email || u.email})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group" style={{ flex: '1', minWidth: '120px' }}>
                          <label htmlFor="add_user_role">Role</label>
                          <select
                            id="add_user_role"
                            value={addUserData.role}
                            onChange={(e) => setAddUserData(prev => ({ ...prev, role: e.target.value }))}
                            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                          >
                            <option value="student">Student</option>
                            <option value="teacher">Teacher</option>
                            <option value="admin">Admin</option>
                            <option value="event_simple">Event Simple</option>
                          </select>
                        </div>
                        <div className="form-group" style={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={addUserData.is_primary}
                              onChange={(e) => setAddUserData(prev => ({ ...prev, is_primary: e.target.checked }))}
                            />
                            Primary Org
                          </label>
                        </div>
                      </div>
                      <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                        <button type="submit" className="btn btn-primary btn-sm" disabled={addUserLoading}>
                          {addUserLoading ? 'Adding...' : 'Add User'}
                        </button>
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => {
                          setShowAddUserForm(false);
                          setAddUserError('');
                          setAddUserData({ user_id: '', role: 'student', is_primary: false });
                        }}>
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Users List */}
                {usersLoading ? (
                  <div className="loading-spinner">Loading users...</div>
                ) : orgUsers.length === 0 ? (
                  <p className="text-muted">No users in this organisation yet.</p>
                ) : (
                  <div className="organisation-users-table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>System Role</th>
                          <th>Account Status</th>
                          <th>2SV</th>
                          <th>Org Role</th>
                          <th>Plan</th>
                          <th>Primary</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orgUsers.map((orgUser, orgUserIndex) => (
                          <tr key={orgUser.id}>
                            <td><strong>{orgUser.display_name || orgUser.displayName}</strong></td>
                            <td>{orgUser.primary_email || orgUser.email}</td>
                            <td>
                              <span className={`status-badge ${orgUser.system_role === 'super_admin' ? 'error' : orgUser.system_role === 'admin' ? 'warning' : 'info'}`}>
                                {orgUser.system_role}
                              </span>
                            </td>
                            <td>
                              <span className={`status-badge ${getOrgUserStatus(orgUser)}`}>
                                {getOrgUserStatus(orgUser)}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`security-chip ${isTwoSvEnforced(orgUser) ? 'security-chip-strong' : 'security-chip-muted'}`}
                                data-testid="org-user-2sv-enforced"
                              >
                                {isTwoSvEnforced(orgUser) ? '2SV enforced' : '2SV optional'}
                              </span>
                            </td>
                            <td>
                              <select
                                value={orgUser.org_role}
                                onChange={(e) => handleUpdateUserRole(orgUser.id, e.target.value)}
                                style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                              >
                                <option value="student">Student</option>
                                <option value="teacher">Teacher</option>
                                <option value="admin">Admin</option>
                                <option value="event_simple">Event Simple</option>
                              </select>
                            </td>
                            {/* M004-F0615 / T-M004-0022: Plan column. A denied
                                read renders one row-spanning note instead of a
                                "—" per row (case f); the rest render read-only
                                current + Prior plans, with "Change plan…" shown
                                only when the effective-permission projection
                                grants pricing_member_plan_assign (case a vs b). */}
                            {pricingPlansViewDenied ? (
                              orgUserIndex === 0 && (
                                <td
                                  rowSpan={orgUsers.length}
                                  className="pricing-plan-denied-cell"
                                  data-testid="org-user-plan-denied"
                                >
                                  You don&apos;t have permission to view members&apos; price plans.
                                </td>
                              )
                            ) : (
                              <td data-testid="org-user-plan-cell">
                                {(() => {
                                  const assignmentState = memberPlanAssignments[orgUser.id];
                                  if (!assignmentState) {
                                    return <span className="text-muted" data-testid="org-user-plan-loading">Loading…</span>;
                                  }
                                  if (assignmentState.error) {
                                    return <span className="text-muted" data-testid="org-user-plan-error">Could not load plan</span>;
                                  }
                                  const { current } = assignmentState;
                                  const history = assignmentState.history; // null = not yet loaded
                                  const isExpanded = Boolean(expandedPriorPlans[orgUser.id]);
                                  return (
                                    <>
                                      {!current ? (
                                        <span className="text-muted" data-testid="org-user-plan-none">No plan set</span>
                                      ) : (
                                        (() => {
                                          // The org-wide read (M005-F0047's no-user_id form) does not
                                          // carry a rate — the rate lives on the plan, read separately.
                                          // Never invent one here; "rate not set" is honest, £0 is not.
                                          const rate = formatPricingRate(
                                            current.first_hour_rate_minor,
                                            current.currency,
                                          );
                                          return (
                                            <div data-testid="org-user-plan-current">
                                              <strong>{current.plan_name || 'Unnamed plan'}</strong>
                                              {' — '}
                                              {rate ? `${rate}/hr` : 'rate not set'}
                                              {' since '}
                                              {current.effective_from}
                                            </div>
                                          );
                                        })()
                                      )}
                                      <div className="pricing-prior-plans">
                                        <button
                                          type="button"
                                          className="btn btn-link btn-sm"
                                          data-testid="org-user-plan-prior-toggle"
                                          onClick={() => togglePriorPlans(orgUser.id)}
                                        >
                                          {isExpanded
                                            ? 'Hide prior plans'
                                            : history === null
                                              ? 'Prior plans'
                                              : `Prior plans (${history.length})`}
                                        </button>
                                        {isExpanded && (
                                          assignmentState.historyLoading ? (
                                            <span className="text-muted" data-testid="org-user-plan-prior-loading">Loading…</span>
                                          ) : !history || history.length === 0 ? (
                                            <span className="text-muted" data-testid="org-user-plan-prior-empty">No prior plans</span>
                                          ) : (
                                            <ul data-testid="org-user-plan-prior-list">
                                              {history.map((assignment) => {
                                                const rate = formatPricingRate(
                                                  assignment.first_hour_rate_minor,
                                                  assignment.currency,
                                                );
                                                return (
                                                  <li key={assignment.id} data-testid="org-user-plan-prior-entry">
                                                    {assignment.plan_name || 'Unnamed plan'}
                                                    {' — '}
                                                    {rate ? `${rate}/hr` : 'rate not set'}
                                                    {' ('}
                                                    {assignment.effective_from}
                                                    {' to '}
                                                    {assignment.effective_to || 'ongoing'}
                                                    {')'}
                                                  </li>
                                                );
                                              })}
                                            </ul>
                                          )
                                        )}
                                      </div>
                                      {/* M004-F0646 (amends M004-F0615 / F0637 job 3b, UI-3):
                                          with REACT_APP_MEMBER_PRICE_PLANS_ENABLED the Users
                                          drawer is the ONE canonical place a member's plans
                                          are edited; this row keeps its read-only Plan column
                                          and links there. Flag OFF = today's Change plan…. */}
                                      {pricingEffectivePermissions?.pricing_member_plan_assign === true
                                        && isMemberPricePlansBackendAvailable() && (
                                        <Link
                                          className="btn btn-sm btn-outline"
                                          data-testid="org-user-plan-edit-in-drawer"
                                          to={`/app/admin/users?user=${encodeURIComponent(orgUser.id)}`}
                                        >
                                          Edit in user drawer
                                        </Link>
                                      )}
                                      {pricingEffectivePermissions?.pricing_member_plan_assign === true
                                        && !isMemberPricePlansBackendAvailable() && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline"
                                          data-testid="org-user-plan-change-button"
                                          onClick={() => openChangePlanModal(orgUser)}
                                        >
                                          Change plan…
                                        </button>
                                      )}
                                    </>
                                  );
                                })()}
                              </td>
                            )}
                             {/* M004-F0623 / T-M004-0036 (issue #87) — WIRING
                                 STUB, intentionally inert. The videolib.host
                                 member row (MemberCapabilityRow.js, this
                                 directory) renders here as a new member-table
                                 column, fed by Core #143's per-member "row
                                 with its source" admin API — built on branch
                                 `feat/143-videolib-host-grant`, whose
                                 committed shape does NOT exist yet. Until it
                                 does: no column, no request, no endpoint
                                 guess, no fabricated response — a visible
                                 gap, never a default value (the 2026-09-04
                                 ruling's failure mode). Wiring lands here +
                                 in src/services/api.js when that branch's
                                 commit is posted on studioaeye-Core issue
                                 #143; the browser-level acceptance run
                                 against real Core follows with it. */}
                            <td>
                              {orgUser.is_primary ? (
                                <span className="status-badge success">Yes</span>
                              ) : (
                                <span className="text-muted">No</span>
                              )}
                            </td>
                            <td>
                              <div className="action-buttons">
                                {getOrgUserStatus(orgUser) === 'pending' && (
                                  <>
                                    <button
                                      className="btn btn-sm btn-primary"
                                      data-testid="org-user-approve"
                                      onClick={() => handleOrgUserLifecycleAction(orgUser, 'approve')}
                                      disabled={orgUserActionLoading === `${orgUser.uuid || orgUser.user_uuid}:approve`}
                                    >
                                      Approve
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline"
                                      data-testid="org-user-reject"
                                      onClick={() => handleOrgUserLifecycleAction(orgUser, 'reject')}
                                      disabled={orgUserActionLoading === `${orgUser.uuid || orgUser.user_uuid}:reject`}
                                    >
                                      Reject
                                    </button>
                                  </>
                                )}
                                {getOrgUserStatus(orgUser) === 'rejected' && (
                                  <button
                                    className="btn btn-sm btn-outline"
                                    data-testid="org-user-reconsider"
                                    onClick={() => handleOrgUserLifecycleAction(orgUser, 'reconsider')}
                                    disabled={orgUserActionLoading === `${orgUser.uuid || orgUser.user_uuid}:reconsider`}
                                  >
                                    Reconsider
                                  </button>
                                )}
                                {getOrgUserStatus(orgUser) === 'suspended' && (
                                  <button
                                    className="btn btn-sm btn-outline"
                                    data-testid="org-user-reactivate"
                                    onClick={() => handleOrgUserLifecycleAction(orgUser, 'reactivate')}
                                    disabled={orgUserActionLoading === `${orgUser.uuid || orgUser.user_uuid}:reactivate`}
                                  >
                                    Reactivate
                                  </button>
                                )}
                                {getOrgUserStatus(orgUser) === 'active' && (
                                  <button
                                    className="btn btn-sm btn-outline"
                                    data-testid="org-user-suspend"
                                    onClick={() => handleOrgUserLifecycleAction(orgUser, 'suspend')}
                                    disabled={orgUserActionLoading === `${orgUser.uuid || orgUser.user_uuid}:suspend`}
                                  >
                                    Suspend
                                  </button>
                                )}
                                {/* Mantis #1390 / M004-F0618 §D.4 — per-person
                                    permission override action, adjacent to
                                    (not overlapping) the Plan column above.
                                    Always rendered here, matching every other
                                    sibling action in this column (Approve/
                                    Reject/Suspend/Remove): none of them are
                                    client-side permission-gated, because the
                                    route this opens is itself gated on the
                                    SYSTEM permission `permissions_system_manage`
                                    (verified in admin.routes.ts), which has no
                                    ORGANISATION-scoped projection to read —
                                    `getMyEffectiveOrganisationPermissions`
                                    cannot answer "can I manage permissions" any
                                    more than it can answer "can I approve this
                                    user". The real boundary is the SAL-4 +
                                    403 attempt/react pattern the panel itself
                                    uses, identical to how this same page's
                                    Role Management save already behaves. */}
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline"
                                  data-testid="org-user-permissions-action"
                                  onClick={() => openPermissionOverridePanel(orgUser)}
                                >
                                  Permissions…
                                </button>
                                <button
                                  className="btn btn-sm btn-outline"
                                  onClick={() => handleRemoveUser(orgUser.id, getOrgUserName(orgUser))}
                                  style={{ color: '#dc3545' }}
                                >
                                  Remove
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={closeUsersModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* M004-F0615 / T-M004-0022 §A.1 — "Change plan…" form. Adjacent to,
            not overlapping, the organisation-users-modal block above (that
            table's own new Plan column opens this). Every submit creates a
            new pricing_plan_assignments row (T-M004-0022d); the SAL step-up
            retry (T-M004-0022e) mirrors saveNetworkSal4Setting's pattern. */}
        {changePlanTarget && (
          <div className="modal-overlay" onClick={closeChangePlanModal}>
            <div
              className="modal modal-md change-plan-modal"
              data-testid="change-plan-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Change plan for {getOrgUserName(changePlanTarget)}</h2>
                <button className="modal-close" onClick={closeChangePlanModal}>&times;</button>
              </div>
              <form onSubmit={handleChangePlanSubmit}>
                <div className="modal-body">
                  {changePlanError && (
                    <div className="form-error" data-testid="change-plan-error">{changePlanError}</div>
                  )}
                  <div className="form-group">
                    <label htmlFor="change_plan_plan_id">Plan</label>
                    <select
                      id="change_plan_plan_id"
                      data-testid="change-plan-plan-select"
                      value={changePlanForm.pricing_plan_id}
                      onChange={(e) => setChangePlanForm((prev) => ({ ...prev, pricing_plan_id: e.target.value }))}
                      disabled={changePlanPlansLoading}
                      required
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                    >
                      <option value="">-- Select a plan --</option>
                      {changePlanPlans.map((plan) => (
                        <option key={plan.id} value={plan.id}>
                          {plan.name}{plan.room_name ? ` (${plan.room_name})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label htmlFor="change_plan_effective_from">Effective from</label>
                    <input
                      id="change_plan_effective_from"
                      type="date"
                      data-testid="change-plan-effective-from"
                      value={changePlanForm.effective_from}
                      onChange={(e) => setChangePlanForm((prev) => ({ ...prev, effective_from: e.target.value }))}
                      required
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '1rem' }}>
                    {/* No "grandfather" control here, deliberately: the real
                        POST /pricing/assignments schema is strict and has no
                        such field — grandfathering belongs to plan EDITS
                        (Room Pricing tab, R8 default OFF). UI presents the
                        mental model, never the storage model: this modal
                        states what an assignment actually does. */}
                    <p className="text-muted" style={{ marginTop: '0.25rem' }}>
                      From the effective date, this member&apos;s bookings resolve onto the
                      selected plan. Bookings that already have a price keep their agreed
                      price unless you explicitly recalculate them; billed entries are
                      immutable and can only be corrected with a credit note.
                    </p>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline" onClick={closeChangePlanModal}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    data-testid="change-plan-submit"
                    disabled={changePlanLoading}
                  >
                    {changePlanLoading ? 'Saving...' : 'Change plan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <StepUpDialog
          challenge={changePlanStepUpChallenge}
          title="Plan change needs verification"
          description="Complete a fresh two-step verification before retrying this price plan change."
          onComplete={completeChangePlanStepUp}
          onCancel={cancelChangePlanStepUp}
        />

        {/* Mantis #1390 / M004-F0618 §D.4 — per-person permission override
            panel. Adjacent to, not overlapping, the "Change plan…" modal
            above (that one edits pricing_plan_assignments; this one edits
            permissions_user_organisation via the generic per-user route). */}
        {permissionOverrideTarget && (
          <MemberPermissionOverridePanel
            organisationId={selectedOrg?.id}
            orgUser={permissionOverrideTarget}
            onClose={closePermissionOverridePanel}
          />
        )}
      </div>
    </Layout>
  );
};

// ==================== Admin Devices ====================
const AdminDevices = () => {
  const [devices, setDevices] = useState([]);

  useEffect(() => {
    setDevices([
      { id: 1, name: 'Camera 1 - Entrance', type: 'camera', status: 'online', location: 'Main Entrance' },
      { id: 2, name: 'Camera 2 - Studio A', type: 'camera', status: 'online', location: 'Studio A' },
      { id: 3, name: 'Camera 3 - Studio B', type: 'camera', status: 'offline', location: 'Studio B' },
      { id: 4, name: 'HVAC Controller', type: 'climate', status: 'online', location: 'Main Building' },
      { id: 5, name: 'Door Access - Front', type: 'access', status: 'online', location: 'Main Entrance' },
    ]);
  }, []);

  return (
    <Layout>
      <div className="admin-devices">
        <header className="page-header">
          <div>
            <h1>Devices</h1>
            <p className="subtitle">{devices.filter(d => d.status === 'online').length} online / {devices.length} total</p>
          </div>
          <button className="btn btn-primary">+ Add Device</button>
        </header>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Type</th>
                <th>Location</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => (
                <tr key={device.id}>
                  <td>{device.name}</td>
                  <td><span className={`type-badge ${device.type}`}>{device.type}</span></td>
                  <td>{device.location}</td>
                  <td><span className={`status-badge ${device.status}`}>{device.status}</span></td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" title="View">👁️</button>
                      <button className="btn-icon" title="Configure">⚙️</button>
                      <button className="btn-icon" title="Restart">🔄</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

// ==================== Admin Media ====================
const AdminMedia = () => {
  const [cameras, setCameras] = useState([]);

  useEffect(() => {
    setCameras([
      { id: 1, name: 'Entrance', thumbnail: '📹', status: 'live' },
      { id: 2, name: 'Studio A', thumbnail: '📹', status: 'live' },
      { id: 3, name: 'Studio B', thumbnail: '📹', status: 'offline' },
      { id: 4, name: 'Hallway', thumbnail: '📹', status: 'live' },
    ]);
  }, []);

  return (
    <Layout>
      <div className="admin-media">
        <header className="page-header">
          <div>
            <h1>Media & Surveillance</h1>
            <p className="subtitle">Live camera feeds and recordings</p>
          </div>
        </header>

        <div className="camera-grid">
          {cameras.map((camera) => (
            <div key={camera.id} className={`camera-card ${camera.status}`}>
              <div className="camera-feed">
                <span className="camera-placeholder">{camera.thumbnail}</span>
                {camera.status === 'live' && <span className="live-indicator">● LIVE</span>}
                {camera.status === 'offline' && <span className="offline-indicator">OFFLINE</span>}
              </div>
              <div className="camera-info">
                <h3>{camera.name}</h3>
                <div className="camera-controls">
                  <button className="btn-icon">🔍</button>
                  <button className="btn-icon">📼</button>
                  <button className="btn-icon">⚙️</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

// ==================== Admin Payments ====================
const AdminPayments = () => {
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    setInvoices([
      { id: 'INV-001', user: 'Alice Brown', amount: 45.00, status: 'paid', date: '2025-12-20' },
      { id: 'INV-002', user: 'Bob Wilson', amount: 90.00, status: 'overdue', date: '2025-12-15' },
      { id: 'INV-003', user: 'Carol Davis', amount: 45.00, status: 'pending', date: '2025-12-22' },
      { id: 'INV-004', user: 'David Lee', amount: 120.00, status: 'paid', date: '2025-12-21' },
    ]);
  }, []);

  const totalRevenue = invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.amount, 0);
  const pendingAmount = invoices.filter(i => i.status !== 'paid').reduce((sum, i) => sum + i.amount, 0);

  return (
    <Layout>
      <div className="admin-payments">
        <header className="page-header">
          <div>
            <h1>Payments & Invoices</h1>
            <p className="subtitle">Financial overview</p>
          </div>
          <button className="btn btn-primary">+ Create Invoice</button>
        </header>

        <div className="stats-row">
          <div className="stat-card">
            <span className="stat-value">£{totalRevenue.toFixed(2)}</span>
            <span className="stat-label">Total Revenue</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">£{pendingAmount.toFixed(2)}</span>
            <span className="stat-label">Pending</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{invoices.length}</span>
            <span className="stat-label">Total Invoices</span>
          </div>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>User</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td><strong>{invoice.id}</strong></td>
                  <td>{invoice.user}</td>
                  <td>£{invoice.amount.toFixed(2)}</td>
                  <td>{invoice.date}</td>
                  <td><span className={`status-badge ${invoice.status}`}>{invoice.status}</span></td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" title="View">👁️</button>
                      <button className="btn-icon" title="Send Reminder">📧</button>
                      <button className="btn-icon" title="Download">⬇️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

// ==================== Admin Settings ====================
const AdminSettings = () => {
  const { user: currentUser } = useContext(AuthContext);
  const [settings, setSettings] = useState({
    siteName: 'StudioAEye',
    timezone: 'Europe/London',
    currency: 'GBP',
    emailNotifications: true,
    maintenanceMode: false,
  });
  const [networkSal4Enabled, setNetworkSal4Enabled] = useState(false);
  const [networkSal4Loading, setNetworkSal4Loading] = useState(false);
  const [networkSal4Saving, setNetworkSal4Saving] = useState(false);
  const [networkSal4Message, setNetworkSal4Message] = useState('');
  const [networkSal4Error, setNetworkSal4Error] = useState('');
  const [networkSal4StepUpChallenge, setNetworkSal4StepUpChallenge] = useState(null);
  const [networkSal4StepUpRetry, setNetworkSal4StepUpRetry] = useState(null);

  const isSuperAdmin = currentUser?.role === 'super_admin';

  useEffect(() => {
    let isMounted = true;

    const loadNetworkSal4Permission = async () => {
      if (!isSuperAdmin) {
        return;
      }

      setNetworkSal4Loading(true);
      setNetworkSal4Error('');

      try {
        const response = await permissionApi.getSystemPermission('admin');
        if (isMounted) {
          setNetworkSal4Enabled(getNetworkSal4PermissionValue(response.data));
        }
      } catch (err) {
        if (isMounted) {
          setNetworkSal4Error(getAdminApiErrorMessage(err, 'Unable to load network SAL4 override setting.'));
        }
      } finally {
        if (isMounted) {
          setNetworkSal4Loading(false);
        }
      }
    };

    loadNetworkSal4Permission();

    return () => {
      isMounted = false;
    };
  }, [isSuperAdmin]);

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const saveNetworkSal4Setting = async (nextValue) => {
    setNetworkSal4Saving(true);
    setNetworkSal4Message('');
    setNetworkSal4Error('');

    try {
      await permissionApi.updateSystemPermission('admin', {
        reason: `BUG-351 web admin settings ${nextValue ? 'enabled' : 'disabled'} enforce_SAL4_Network_Access`,
        permissions: {
          enforce_SAL4_Network_Access: nextValue,
        },
      });
      setNetworkSal4Enabled(nextValue);
      setNetworkSal4Message(`Network SAL4 override ${nextValue ? 'enabled' : 'disabled'}.`);
      setNetworkSal4StepUpChallenge(null);
      setNetworkSal4StepUpRetry(null);
    } catch (err) {
      const challenge = getStepUpChallengeFromError(err);
      if (challenge?.id) {
        setNetworkSal4StepUpChallenge(challenge);
        setNetworkSal4StepUpRetry(() => () => saveNetworkSal4Setting(nextValue));
        setNetworkSal4Error('A fresh 2SV step-up is required before changing this setting.');
        return;
      }

      setNetworkSal4Error(getAdminApiErrorMessage(err, 'Unable to update network SAL4 override setting.'));
    } finally {
      setNetworkSal4Saving(false);
    }
  };

  const handleNetworkSal4Toggle = async (event) => {
    const nextValue = event.target.checked;
    const action = nextValue ? 'enable' : 'disable';

    if (!window.confirm(`Are you sure you want to ${action} the network SAL4 override?`)) {
      return;
    }

    await saveNetworkSal4Setting(nextValue);
  };

  const completeNetworkSal4StepUp = async () => {
    const retry = networkSal4StepUpRetry;
    setNetworkSal4StepUpChallenge(null);
    setNetworkSal4StepUpRetry(null);
    if (retry) {
      await retry();
    }
  };

  const closeNetworkSal4StepUp = () => {
    setNetworkSal4StepUpChallenge(null);
    setNetworkSal4StepUpRetry(null);
  };

  return (
    <Layout>
      <div className="admin-settings">
        <header className="page-header">
          <div>
            <h1>Settings</h1>
            <p className="subtitle">System configuration</p>
          </div>
        </header>

        <div className="settings-sections">
          <section className="settings-section">
            <h2>General</h2>
            <div className="setting-item">
              <label>Site Name</label>
              <input
                type="text"
                value={settings.siteName}
                onChange={(e) => handleChange('siteName', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="setting-item">
              <label>Timezone</label>
              <select
                value={settings.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)}
                className="form-input"
              >
                <option value="Europe/London">Europe/London</option>
                <option value="America/New_York">America/New_York</option>
                <option value="Asia/Tokyo">Asia/Tokyo</option>
              </select>
            </div>
            <div className="setting-item">
              <label>Currency</label>
              <select
                value={settings.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
                className="form-input"
              >
                <option value="GBP">GBP (£)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
              </select>
            </div>
          </section>

          <section className="settings-section">
            <h2>Notifications</h2>
            <div className="setting-item toggle">
              <label>Email Notifications</label>
              <input
                type="checkbox"
                checked={settings.emailNotifications}
                onChange={(e) => handleChange('emailNotifications', e.target.checked)}
              />
            </div>
          </section>

          <section className="settings-section">
            <h2>Maintenance</h2>
            <div className="setting-item toggle">
              <label>Maintenance Mode</label>
              <input
                type="checkbox"
                checked={settings.maintenanceMode}
                onChange={(e) => handleChange('maintenanceMode', e.target.checked)}
              />
            </div>
          </section>

          {isSuperAdmin && (
            <section
              className="settings-section network-sal4-setting"
              data-testid="network-sal4-override-panel"
            >
              <h2>Security</h2>
              <div className="setting-item toggle">
                <div>
                  <label htmlFor="network-sal4-override-toggle">Network SAL4 Override</label>
                  <p className="setting-help">
                    Require SAL4 for administrator network access unless the backend override is enabled.
                  </p>
                  <p
                    className="setting-state"
                    data-testid="network-sal4-override-state"
                  >
                    {networkSal4Loading
                      ? 'Loading'
                      : networkSal4Enabled
                        ? 'Enabled'
                        : 'Disabled'}
                  </p>
                </div>
                <input
                  id="network-sal4-override-toggle"
                  type="checkbox"
                  checked={networkSal4Enabled}
                  disabled={networkSal4Loading || networkSal4Saving}
                  onChange={handleNetworkSal4Toggle}
                  data-testid="network-sal4-override-toggle"
                />
              </div>
              {networkSal4Message && (
                <div
                  className="settings-message success"
                  data-testid="network-sal4-override-success"
                >
                  {networkSal4Message}
                </div>
              )}
              {networkSal4Error && (
                <div
                  className="settings-message error"
                  data-testid="network-sal4-override-error"
                >
                  {networkSal4Error}
                </div>
              )}
            </section>
          )}

          <div className="settings-actions">
            <button className="btn btn-primary">Save Changes</button>
            <button className="btn btn-outline">Reset to Default</button>
          </div>
        </div>

        <StepUpDialog
          challenge={networkSal4StepUpChallenge}
          title="Network setting needs verification"
          description="Complete a fresh two-step verification before retrying this network security setting."
          onComplete={completeNetworkSal4StepUp}
          onCancel={closeNetworkSal4StepUp}
        />
      </div>
    </Layout>
  );
};

// ==================== Admin Consoles (Panels) — Super Admin Only ====================
const AdminConsoles = () => {
  const { user } = useContext(AuthContext);
  const [consoles, setConsoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Console create/edit modal
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedConsole, setSelectedConsole] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  // M004-F0613 / T-M004-0020: Create/Edit Console Panel falls under the spec's
  // blanket rule (§2 "In scope") but was left unwired as a declared follow-up
  // (Mantis #1177). "new" and "edit:<id>" are separate scope keys (§5.2) so a draft
  // for one panel's edit can never leak into the create form or into a different
  // panel's edit.
  const newConsoleDraft = useDialogDraft('admin-console-new', 'new', { active: showModal && modalMode === 'add' });
  const editConsoleDraft = useDialogDraft('admin-console-edit', selectedConsole?.id, { active: showModal && modalMode === 'edit' });
  const consoleDialogDraftRef = modalMode === 'add' ? newConsoleDraft.containerRef : editConsoleDraft.containerRef;
  // Rooms linked to the console being edited (inside modal)
  const [modalRooms, setModalRooms] = useState([]);
  const [modalRoomsLoading, setModalRoomsLoading] = useState(false);
  const [modalAddRoomId, setModalAddRoomId] = useState('');
  const [modalAddRoomLoading, setModalAddRoomLoading] = useState(false);
  const [modalAddRoomError, setModalAddRoomError] = useState('');
  // Shutters linked to the console's rooms (inside modal)
  const [modalShutters, setModalShutters] = useState([]);
  const [modalAddShutterRoomId, setModalAddShutterRoomId] = useState('');
  const [modalAddShutterEntity, setModalAddShutterEntity] = useState('');
  const [modalAddShutterName, setModalAddShutterName] = useState('');
  const [modalAddShutterLoading, setModalAddShutterLoading] = useState(false);
  const [modalAddShutterError, setModalAddShutterError] = useState('');
  // HA Entities (switches & lights) linked to the console's rooms (inside modal)
  const [modalEntities, setModalEntities] = useState([]);
  const [modalAddEntityRoomId, setModalAddEntityRoomId] = useState('');
  const [modalAddEntityId, setModalAddEntityId] = useState('');
  const [modalAddEntityName, setModalAddEntityName] = useState('');
  const [modalAddEntityLoading, setModalAddEntityLoading] = useState(false);
  const [modalAddEntityError, setModalAddEntityError] = useState('');

  // Detail panel (rooms + users for a console)
  const [detailConsole, setDetailConsole] = useState(null);
  const [consoleRooms, setConsoleRooms] = useState([]);
  const [consoleUsers, setConsoleUsers] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Room picker
  const [allRooms, setAllRooms] = useState([]);
  const [addRoomId, setAddRoomId] = useState('');
  const [addRoomLoading, setAddRoomLoading] = useState(false);
  const [addRoomError, setAddRoomError] = useState('');

  // User picker
  const [allUsers, setAllUsers] = useState([]);
  const [addUserId, setAddUserId] = useState('');
  const [addUserNotes, setAddUserNotes] = useState('');
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState('');

  // Load consoles + global rooms/users for pickers
  useEffect(() => {
    fetchConsoles();
    fetchAllRooms();
    fetchAllUsers();
  }, []);

  const fetchConsoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await consoleApi.getConsoles();
      setConsoles(res.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch consoles:', err);
      setError('Failed to load panels');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllRooms = async () => {
    try {
      const res = await roomApi.getRooms();
      setAllRooms(res.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch rooms:', err);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await userApi.getUsers();
      setAllUsers(res.data?.data?.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const openDetail = async (console_) => {
    setDetailConsole(console_);
    setAddRoomId('');
    setAddUserId('');
    setAddUserNotes('');
    setAddRoomError('');
    setAddUserError('');
    setDetailLoading(true);
    try {
      const [roomsRes, usersRes] = await Promise.all([
        consoleApi.getConsoleRooms(console_.id),
        consoleApi.getConsoleUsers(console_.id),
      ]);
      setConsoleRooms(roomsRes.data?.data || []);
      setConsoleUsers(usersRes.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch console detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailConsole(null);
    setConsoleRooms([]);
    setConsoleUsers([]);
  };

  // Create / Edit console
  const openAddModal = () => {
    setModalMode('add');
    setSelectedConsole(null);
    // M004-F0613: restore any draft left from a previously-incidental close of this
    // same dialog ("new").
    const draft = getDraftForScope(buildDraftScopeKey('admin-console-new', 'new'));
    setFormData({ name: '', description: '', ...draft });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = async (console_) => {
    setModalMode('edit');
    setSelectedConsole(console_);
    // M004-F0613: scoped to this specific panel's id, so it can never show a draft
    // left for a different panel (or for the "new" dialog).
    const draft = getDraftForScope(buildDraftScopeKey('admin-console-edit', console_.id));
    setFormData({ name: console_.name, description: console_.description || '', ...draft });
    setFormError('');
    setModalAddRoomId('');
    setModalAddRoomError('');
    setModalAddShutterRoomId(''); setModalAddShutterEntity(''); setModalAddShutterName(''); setModalAddShutterError('');
    setModalAddEntityRoomId(''); setModalAddEntityId(''); setModalAddEntityName(''); setModalAddEntityError('');
    setShowModal(true);
    // Load linked rooms for this console
    setModalRoomsLoading(true);
    try {
      const res = await consoleApi.getConsoleRooms(console_.id);
      const rooms = res.data?.data || [];
      setModalRooms(rooms);
      // Load shutters and HA entities for linked rooms
      await loadModalDevices(rooms);
    } catch (err) {
      console.error('Failed to load panel rooms:', err);
      setModalRooms([]);
    } finally {
      setModalRoomsLoading(false);
    }
  };

  // Load shutters and HA entities for a set of rooms
  const loadModalDevices = async (rooms) => {
    const roomIds = new Set(rooms.map(r => r.room_id));
    const [shuttersResult, entitiesResult] = await Promise.allSettled([
      shutterApi.getShutters(),
      haEntityApi.getEntities(),
    ]);
    if (shuttersResult.status === 'fulfilled') {
      const allShutters = shuttersResult.value.data?.data || [];
      setModalShutters(allShutters.filter(s => roomIds.has(s.room_id)));
    } else {
      console.error('Failed to load shutters:', shuttersResult.reason);
      setModalShutters([]);
    }
    if (entitiesResult.status === 'fulfilled') {
      const allEntities = entitiesResult.value.data?.data || [];
      setModalEntities(allEntities.filter(e => roomIds.has(e.room_id)));
    } else {
      console.error('Failed to load HA entities:', entitiesResult.reason);
      setModalEntities([]);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedConsole(null);
    setFormError('');
    setModalRooms([]);
    setModalAddRoomId('');
    setModalAddRoomError('');
    setModalShutters([]);
    setModalAddShutterRoomId(''); setModalAddShutterEntity(''); setModalAddShutterName(''); setModalAddShutterError('');
    setModalEntities([]);
    setModalAddEntityRoomId(''); setModalAddEntityId(''); setModalAddEntityName(''); setModalAddEntityError('');
  };

  // M004-F0612 / T-M004-0019: dismiss only on a genuine outside press-and-release;
  // a text-selection drag from inside a dialog to outside it must not close it.
  const consoleModalDismissHandlers = useDismissOnOutsideClick(closeModal);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.name.trim()) {
      setFormError('Panel name is required');
      return;
    }
    setFormLoading(true);
    // Captured before closeModal() resets selectedConsole back to null.
    const editRecordId = selectedConsole?.id;
    try {
      if (modalMode === 'add') {
        await consoleApi.createConsole(formData);
        // M004-F0613: a draft is cleared only on successful submit, never merely
        // because the dialog closed.
        clearDraftForScope(buildDraftScopeKey('admin-console-new', 'new'));
      } else {
        await consoleApi.updateConsole(selectedConsole.id, formData);
        clearDraftForScope(buildDraftScopeKey('admin-console-edit', editRecordId));
        // Refresh detail if it's open for this console
        if (detailConsole?.id === selectedConsole.id) {
          setDetailConsole(prev => ({ ...prev, ...formData }));
        }
      }
      await fetchConsoles();
      closeModal();
    } catch (err) {
      console.error('Failed to save panel:', err);
      setFormError(err.response?.data?.message || 'Failed to save panel');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (console_) => {
    if (!window.confirm(`Delete panel "${console_.name}"? This will remove all room and user links.`)) return;
    try {
      await consoleApi.deleteConsole(console_.id);
      if (detailConsole?.id === console_.id) closeDetail();
      await fetchConsoles();
    } catch (err) {
      console.error('Failed to delete panel:', err);
      alert(err.response?.data?.message || 'Failed to delete panel');
    }
  };

  // Room management (inside edit modal)
  const handleModalAddRoom = async (e) => {
    e.preventDefault();
    setModalAddRoomError('');
    if (!modalAddRoomId) {
      setModalAddRoomError('Please select a room');
      return;
    }
    setModalAddRoomLoading(true);
    try {
      await consoleApi.addRoom(selectedConsole.id, parseInt(modalAddRoomId, 10));
      const res = await consoleApi.getConsoleRooms(selectedConsole.id);
      const updatedRooms = res.data?.data || [];
      setModalRooms(updatedRooms);
      // Also refresh detail panel if open for same console
      if (detailConsole?.id === selectedConsole.id) {
        setConsoleRooms(updatedRooms);
      }
      // Refresh shutters and HA entities for new room set
      await loadModalDevices(updatedRooms);
      setModalAddRoomId('');
    } catch (err) {
      console.error('Failed to add room:', err);
      setModalAddRoomError(err.response?.data?.message || 'Failed to add room');
    } finally {
      setModalAddRoomLoading(false);
    }
  };

  const handleModalRemoveRoom = async (roomId, roomName) => {
    if (!window.confirm(`Remove "${roomName}" from this panel?`)) return;
    try {
      await consoleApi.removeRoom(selectedConsole.id, roomId);
      const updatedRooms = modalRooms.filter(r => r.room_id !== roomId);
      setModalRooms(updatedRooms);
      // Also refresh detail panel if open for same console
      if (detailConsole?.id === selectedConsole.id) {
        setConsoleRooms(updatedRooms);
      }
      // Remove shutters and entities that belonged to the removed room
      setModalShutters(prev => prev.filter(s => s.room_id !== roomId));
      setModalEntities(prev => prev.filter(e => e.room_id !== roomId));
    } catch (err) {
      console.error('Failed to remove room:', err);
      alert(err.response?.data?.message || 'Failed to remove room');
    }
  };

  // Shutter management (inside edit modal)
  const handleModalAddShutter = async (e) => {
    e.preventDefault();
    setModalAddShutterError('');
    if (!modalAddShutterRoomId) { setModalAddShutterError('Please select a room'); return; }
    if (!modalAddShutterEntity.trim()) { setModalAddShutterError('Entity ID is required (e.g. cover.studio_blinds)'); return; }
    setModalAddShutterLoading(true);
    try {
      await shutterApi.createShutter({
        room_id: parseInt(modalAddShutterRoomId, 10),
        ha_entity_id: modalAddShutterEntity.trim(),
        name: modalAddShutterName.trim() || undefined,
      });
      await loadModalDevices(modalRooms);
      setModalAddShutterRoomId(''); setModalAddShutterEntity(''); setModalAddShutterName('');
    } catch (err) {
      console.error('Failed to add shutter:', err);
      setModalAddShutterError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to add shutter');
    } finally {
      setModalAddShutterLoading(false);
    }
  };

  const handleModalRemoveShutter = async (shutterId, shutterName) => {
    if (!window.confirm(`Remove shutter "${shutterName}" from this panel?`)) return;
    try {
      await shutterApi.deleteShutter(shutterId);
      setModalShutters(prev => prev.filter(s => s.id !== shutterId));
    } catch (err) {
      console.error('Failed to remove shutter:', err);
      alert(err.response?.data?.message || 'Failed to remove shutter');
    }
  };

  // HA Entity (switch/light) management (inside edit modal)
  const handleModalAddEntity = async (e) => {
    e.preventDefault();
    setModalAddEntityError('');
    if (!modalAddEntityRoomId) { setModalAddEntityError('Please select a room'); return; }
    if (!modalAddEntityId.trim()) { setModalAddEntityError('Entity ID is required (e.g. switch.studio_fan or light.studio_ceiling)'); return; }
    setModalAddEntityLoading(true);
    try {
      await haEntityApi.createEntity({
        room_id: parseInt(modalAddEntityRoomId, 10),
        ha_entity_id: modalAddEntityId.trim(),
        name: modalAddEntityName.trim() || undefined,
      });
      await loadModalDevices(modalRooms);
      setModalAddEntityRoomId(''); setModalAddEntityId(''); setModalAddEntityName('');
    } catch (err) {
      console.error('Failed to add entity:', err);
      setModalAddEntityError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to add entity');
    } finally {
      setModalAddEntityLoading(false);
    }
  };

  const handleModalRemoveEntity = async (entityId, entityName) => {
    if (!window.confirm(`Remove "${entityName}" from this panel?`)) return;
    try {
      await haEntityApi.deleteEntity(entityId);
      setModalEntities(prev => prev.filter(e => e.id !== entityId));
    } catch (err) {
      console.error('Failed to remove entity:', err);
      alert(err.response?.data?.message || 'Failed to remove entity');
    }
  };

  // Room management (detail panel)
  const handleAddRoom = async (e) => {
    e.preventDefault();
    setAddRoomError('');
    if (!addRoomId) {
      setAddRoomError('Please select a room');
      return;
    }
    setAddRoomLoading(true);
    try {
      await consoleApi.addRoom(detailConsole.id, parseInt(addRoomId, 10));
      const res = await consoleApi.getConsoleRooms(detailConsole.id);
      setConsoleRooms(res.data?.data || []);
      setAddRoomId('');
    } catch (err) {
      console.error('Failed to add room:', err);
      setAddRoomError(err.response?.data?.message || 'Failed to add room');
    } finally {
      setAddRoomLoading(false);
    }
  };

  const handleRemoveRoom = async (roomId, roomName) => {
    if (!window.confirm(`Remove "${roomName}" from this panel?`)) return;
    try {
      await consoleApi.removeRoom(detailConsole.id, roomId);
      setConsoleRooms(prev => prev.filter(r => r.room_id !== roomId));
    } catch (err) {
      console.error('Failed to remove room:', err);
      alert(err.response?.data?.message || 'Failed to remove room');
    }
  };

  // User management
  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddUserError('');
    if (!addUserId) {
      setAddUserError('Please select a user');
      return;
    }
    setAddUserLoading(true);
    try {
      await consoleApi.addUser(detailConsole.id, parseInt(addUserId, 10), addUserNotes || undefined);
      const res = await consoleApi.getConsoleUsers(detailConsole.id);
      setConsoleUsers(res.data?.data || []);
      setAddUserId('');
      setAddUserNotes('');
    } catch (err) {
      console.error('Failed to add user:', err);
      setAddUserError(err.response?.data?.message || 'Failed to add user');
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleRemoveUser = async (userId, userName) => {
    if (!window.confirm(`Remove "${userName}" from this panel?`)) return;
    try {
      await consoleApi.removeUser(detailConsole.id, userId);
      setConsoleUsers(prev => prev.filter(u => u.user_id !== userId));
    } catch (err) {
      console.error('Failed to remove user:', err);
      alert(err.response?.data?.message || 'Failed to remove user');
    }
  };

  // Rooms already linked to this console (for filtering picker)
  // consoleRooms entries have both `id` (console_rooms join row) and `room_id` (the room's actual ID)
  const linkedRoomIds = new Set(consoleRooms.map(r => r.room_id));
  const availableRooms = allRooms.filter(r => !linkedRoomIds.has(r.id));
  const linkedUserIds = new Set(consoleUsers.map(u => u.user_id));
  const availableUsers = allUsers.filter(u => !linkedUserIds.has(u.id));

  if (user?.role !== 'super_admin') {
    return (
      <Layout>
        <div className="admin-users">
          <header className="page-header"><h1>Access Denied</h1></header>
          <p>Only Super Admins can manage panels.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="admin-users">
        <header className="page-header">
          <div>
            <h1>Panel Management</h1>
            <p className="subtitle">
              Panels group rooms together and assign users who can control them (Simple Room Controls).
            </p>
          </div>
          <button className="btn btn-primary" onClick={openAddModal}>+ New Panel</button>
        </header>

        {error && <div className="error-message">{error}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: detailConsole ? '1fr 1fr' : '1fr', gap: '24px' }}>

          {/* ── Panels table ── */}
          <div>
            {loading ? (
              <div className="loading-spinner">Loading panels…</div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {consoles.length === 0 ? (
                      <tr><td colSpan="3" className="no-data">No panels yet — create one to get started.</td></tr>
                    ) : (
                      consoles.map(c => (
                        <tr
                          key={c.id}
                          style={detailConsole?.id === c.id ? { backgroundColor: '#eff6ff' } : {}}
                        >
                          <td>
                            <button
                              style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 600, padding: 0 }}
                              onClick={() => detailConsole?.id === c.id ? closeDetail() : openDetail(c)}
                            >
                              📋 {c.name}
                            </button>
                          </td>
                          <td style={{ color: '#555', fontSize: '13px' }}>{c.description || '—'}</td>
                          <td>
                            <div className="action-buttons">
                              <button className="btn-icon" title="Edit" onClick={() => openEditModal(c)}>✏️</button>
                              <button className="btn-icon" title="Delete" onClick={() => handleDelete(c)}>🗑️</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Detail panel ── */}
          {detailConsole && (
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px', background: '#f8fafc' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0, fontSize: '18px' }}>📋 {detailConsole.name}</h2>
                <button
                  style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}
                  onClick={closeDetail}
                  title="Close"
                >×</button>
              </div>

              {detailLoading ? (
                <div className="loading-spinner">Loading…</div>
              ) : (
                <>
                  {/* ── Rooms section ── */}
                  <section style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '15px', marginBottom: '10px', color: '#1e293b' }}>🚪 Linked Rooms</h3>

                    {consoleRooms.length === 0 ? (
                      <p style={{ color: '#94a3b8', fontSize: '13px' }}>No rooms linked yet.</p>
                    ) : (
                      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
                        {consoleRooms.map(room => (
                          <li key={room.room_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #e2e8f0' }}>
                            <span style={{ fontSize: '14px' }}>{room.room_name}</span>
                            <button
                              className="btn-icon"
                              title="Remove room"
                              onClick={() => handleRemoveRoom(room.room_id, room.room_name)}
                            >🗑️</button>
                          </li>
                        ))}
                      </ul>
                    )}

                    <form onSubmit={handleAddRoom} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <select
                        value={addRoomId}
                        onChange={e => setAddRoomId(e.target.value)}
                        className="filter-select"
                        style={{ flex: 1, minWidth: '140px' }}
                      >
                        <option value="">— Select room —</option>
                        {availableRooms.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                      <button type="submit" className="btn btn-primary" disabled={addRoomLoading} style={{ whiteSpace: 'nowrap' }}>
                        {addRoomLoading ? '…' : '+ Add Room'}
                      </button>
                      {addRoomError && <span style={{ color: '#dc2626', fontSize: '12px', width: '100%' }}>{addRoomError}</span>}
                    </form>
                  </section>

                  {/* ── Users section ── */}
                  <section>
                    <h3 style={{ fontSize: '15px', marginBottom: '10px', color: '#1e293b' }}>👤 Assigned Users</h3>

                    {consoleUsers.length === 0 ? (
                      <p style={{ color: '#94a3b8', fontSize: '13px' }}>No users assigned yet.</p>
                    ) : (
                      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
                        {consoleUsers.map(u => (
                          <li key={u.user_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #e2e8f0' }}>
                            <div>
                              <span style={{ fontSize: '14px', fontWeight: 500 }}>{u.user_display_name || u.user_email}</span>
                              {u.user_display_name && (
                                <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '6px' }}>#{u.user_id} · {u.user_email}</span>
                              )}
                              <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '8px' }} className={`role-badge ${u.user_role}`}>{u.user_role}</span>
                              {u.notes && <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '6px' }}>({u.notes})</span>}
                            </div>
                            <button
                              className="btn-icon"
                              title="Remove user"
                              onClick={() => handleRemoveUser(u.user_id, u.user_display_name || u.user_email)}
                            >🗑️</button>
                          </li>
                        ))}
                      </ul>
                    )}

                    <form onSubmit={handleAddUser} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <select
                        value={addUserId}
                        onChange={e => setAddUserId(e.target.value)}
                        className="filter-select"
                        style={{ flex: 1, minWidth: '160px' }}
                      >
                        <option value="">— Select user —</option>
                        {availableUsers.map(u => (
                          <option key={u.id} value={u.id}>{u.displayName || u.email} ({u.role})</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Notes (optional)"
                        value={addUserNotes}
                        onChange={e => setAddUserNotes(e.target.value)}
                        className="search-input"
                        style={{ flex: 1, minWidth: '120px' }}
                      />
                      <button type="submit" className="btn btn-primary" disabled={addUserLoading} style={{ whiteSpace: 'nowrap' }}>
                        {addUserLoading ? '…' : '+ Add User'}
                      </button>
                      {addUserError && <span style={{ color: '#dc2626', fontSize: '12px', width: '100%' }}>{addUserError}</span>}
                    </form>
                  </section>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Create / Edit Console Modal ── */}
        {showModal && (
          <div className="modal-overlay" {...consoleModalDismissHandlers}>
            <div className="modal" ref={consoleDialogDraftRef} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{modalMode === 'add' ? 'Create New Panel' : 'Edit Panel'}</h2>
                <button className="modal-close" onClick={closeModal}>&times;</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {formError && <div className="form-error">{formError}</div>}
                  <div className="form-group">
                    <label htmlFor="console-name">Panel Name *</label>
                    <input
                      id="console-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                      placeholder="e.g., Reception Console"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="console-description">Description</label>
                    <input
                      id="console-description"
                      type="text"
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      placeholder="Optional description"
                    />
                  </div>

                  {modalMode === 'edit' && selectedConsole && (
                    <>
                      <hr style={{ margin: '20px 0', borderColor: '#ddd' }} />
                      <h3 style={{ marginBottom: '12px', fontSize: '15px', color: '#1e293b' }}>🚪 Linked Rooms</h3>

                      {modalRoomsLoading ? (
                        <p style={{ color: '#94a3b8', fontSize: '13px' }}>Loading rooms…</p>
                      ) : (
                        <>
                          {modalRooms.length === 0 ? (
                            <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '12px' }}>No rooms linked yet.</p>
                          ) : (
                            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
                              {modalRooms.map(room => (
                                <li key={room.room_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #e2e8f0' }}>
                                  <span style={{ fontSize: '14px' }}>{room.room_name}</span>
                                  <button
                                    type="button"
                                    className="btn-icon"
                                    title="Remove room"
                                    onClick={() => handleModalRemoveRoom(room.room_id, room.room_name)}
                                  >🗑️</button>
                                </li>
                              ))}
                            </ul>
                          )}

                          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            <select
                              value={modalAddRoomId}
                              onChange={e => setModalAddRoomId(e.target.value)}
                              className="filter-select"
                              style={{ flex: 1, minWidth: '140px' }}
                            >
                              <option value="">— Select room —</option>
                              {allRooms
                                .filter(r => !modalRooms.some(mr => mr.room_id === r.id))
                                .map(r => (
                                  <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                            </select>
                            <button
                              type="button"
                              className="btn btn-primary"
                              disabled={modalAddRoomLoading}
                              onClick={handleModalAddRoom}
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              {modalAddRoomLoading ? '…' : '+ Add Room'}
                            </button>
                            {modalAddRoomError && <span style={{ color: '#dc2626', fontSize: '12px', width: '100%' }}>{modalAddRoomError}</span>}
                          </div>
                        </>
                      )}

                      {/* ── Shutters section ── */}
                      {!modalRoomsLoading && modalRooms.length > 0 && (
                        <>
                          <hr style={{ margin: '20px 0', borderColor: '#ddd' }} />
                          <h3 style={{ marginBottom: '12px', fontSize: '15px', color: '#1e293b' }}>🪟 Shutters / Covers</h3>

                          {modalShutters.length === 0 ? (
                            <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '12px' }}>No shutters linked to these rooms.</p>
                          ) : (
                            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
                              {modalShutters.map(shutter => (
                                <li key={shutter.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #e2e8f0' }}>
                                  <div>
                                    <span style={{ fontSize: '14px' }}>{shutter.name || shutter.ha_entity_id}</span>
                                    <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '8px' }}>
                                      {modalRooms.find(r => r.room_id === shutter.room_id)?.room_name || `Room #${shutter.room_id}`}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn-icon"
                                    title="Remove shutter"
                                    onClick={() => handleModalRemoveShutter(shutter.id, shutter.name || shutter.ha_entity_id)}
                                  >🗑️</button>
                                </li>
                              ))}
                            </ul>
                          )}

                          <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            <select
                              value={modalAddShutterRoomId}
                              onChange={e => setModalAddShutterRoomId(e.target.value)}
                              className="filter-select"
                              style={{ flex: '0 0 auto', minWidth: '120px' }}
                            >
                              <option value="">— Room —</option>
                              {modalRooms.map(r => (
                                <option key={r.room_id} value={r.room_id}>{r.room_name}</option>
                              ))}
                            </select>
                            <input
                              type="text"
                              placeholder="cover.entity_id"
                              value={modalAddShutterEntity}
                              onChange={e => setModalAddShutterEntity(e.target.value)}
                              className="search-input"
                              style={{ flex: 1, minWidth: '140px' }}
                            />
                            <input
                              type="text"
                              placeholder="Name (optional)"
                              value={modalAddShutterName}
                              onChange={e => setModalAddShutterName(e.target.value)}
                              className="search-input"
                              style={{ flex: 1, minWidth: '100px' }}
                            />
                            <button
                              type="button"
                              className="btn btn-primary"
                              disabled={modalAddShutterLoading}
                              onClick={handleModalAddShutter}
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              {modalAddShutterLoading ? '…' : '+ Shutter'}
                            </button>
                            {modalAddShutterError && <span style={{ color: '#dc2626', fontSize: '12px', width: '100%' }}>{modalAddShutterError}</span>}
                          </div>
                        </>
                      )}

                      {/* ── Switches & Lights section ── */}
                      {!modalRoomsLoading && modalRooms.length > 0 && (
                        <>
                          <hr style={{ margin: '20px 0', borderColor: '#ddd' }} />
                          <h3 style={{ marginBottom: '12px', fontSize: '15px', color: '#1e293b' }}>💡 Switches & Lights</h3>

                          {modalEntities.length === 0 ? (
                            <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '12px' }}>No switches or lights linked to these rooms.</p>
                          ) : (
                            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px' }}>
                              {modalEntities.map(entity => (
                                <li key={entity.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #e2e8f0' }}>
                                  <div>
                                    <span style={{ fontSize: '14px' }}>{entity.name || entity.ha_entity_id}</span>
                                    <span style={{ fontSize: '11px', color: entity.ha_class === 'light' ? '#f59e0b' : '#6366f1', marginLeft: '6px', fontWeight: 600 }}>
                                      {entity.ha_class === 'light' ? '💡 light' : '🔌 switch'}
                                    </span>
                                    <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '8px' }}>
                                      {modalRooms.find(r => r.room_id === entity.room_id)?.room_name || `Room #${entity.room_id}`}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn-icon"
                                    title="Remove entity"
                                    onClick={() => handleModalRemoveEntity(entity.id, entity.name || entity.ha_entity_id)}
                                  >🗑️</button>
                                </li>
                              ))}
                            </ul>
                          )}

                          <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            <select
                              value={modalAddEntityRoomId}
                              onChange={e => setModalAddEntityRoomId(e.target.value)}
                              className="filter-select"
                              style={{ flex: '0 0 auto', minWidth: '120px' }}
                            >
                              <option value="">— Room —</option>
                              {modalRooms.map(r => (
                                <option key={r.room_id} value={r.room_id}>{r.room_name}</option>
                              ))}
                            </select>
                            <input
                              type="text"
                              placeholder="switch.* or light.*"
                              value={modalAddEntityId}
                              onChange={e => setModalAddEntityId(e.target.value)}
                              className="search-input"
                              style={{ flex: 1, minWidth: '140px' }}
                            />
                            <input
                              type="text"
                              placeholder="Name (optional)"
                              value={modalAddEntityName}
                              onChange={e => setModalAddEntityName(e.target.value)}
                              className="search-input"
                              style={{ flex: 1, minWidth: '100px' }}
                            />
                            <button
                              type="button"
                              className="btn btn-primary"
                              disabled={modalAddEntityLoading}
                              onClick={handleModalAddEntity}
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              {modalAddEntityLoading ? '…' : '+ Entity'}
                            </button>
                            {modalAddEntityError && <span style={{ color: '#dc2626', fontSize: '12px', width: '100%' }}>{modalAddEntityError}</span>}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline" onClick={closeModal}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={formLoading}>
                    {formLoading ? 'Saving…' : (modalMode === 'add' ? 'Create Panel' : 'Save Changes')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export { AdminDashboard, UserManagementPage, AdminUsers, AdminRooms, AdminOrganisations, AdminDevices, AdminMedia, AdminPayments, AdminSettings, AdminConsoles };
